import { i18n } from '../i18n.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { showAchievementBanner } from './banner.js';
import { Cards } from './cards.js';
import { CardTilt } from './card-tilt.js';
import { creationRefusalLine } from './service-pause.js';
import { Confirm } from './confirm-dialog.js';
import { Firebase } from '../firebase-loader.js';
import { FoundryLifecycle } from './foundry-lifecycle.js';
import { FX } from './fx.js';
import { getCloudAchievementBannerConsumer } from '../app-services.js';
import { HoverHandoff } from './hover-handoff.js';
import { MoonDreamDeckFit } from './moon-dream-deck-fit.js';
import { createHoverIntent } from './hover-intent.js';
import { Pagination } from './pagination.js';
import { Platform } from './platform.js';
import { SafeArea } from './safe-area.js';
import { SoundEffects } from './sound-effects.js';
import { SwipePager } from './swipe-pager.js';
import { Stardust } from './stardust.js';
import { announceStardust, lastKnownStardust } from './stardust-chip.js';
import { TouchPreview } from './touch-preview.js';
import { uiPx } from './ui-scale.js';
import { DiscordPings } from './discord-pings.js';
let MoonDream = {};


MoonDream = (function () {
    const PROMPT_MAX_LENGTH = 40;
    // Mirrors game/utils/moonDreamValidation.js exactly: letters, marks,
    // numbers, spaces and ordinary punctuation, with every character that could
    // break out of the server's prompt scaffolding excluded. The server remains
    // authoritative; this only spares the player a round-trip.
    const PROMPT_REGEX = /^[\p{L}\p{M}\p{N} '‘’"“”\-–—.,!?:;()&/+*#@~^%=_…]+$/u;
    const PROMPT_INVISIBLE_OR_FORMAT = /[\p{Cc}\p{Cf}\p{Co}\p{Cs}\p{Zl}\p{Zp}]/u;
    const ACTIVE_POLL_INTERVAL_MS = 2000;
    // Bounded group reveals (docs/asset-loading.md grace rule).
    const SPELL_DECK_REVEAL_CAP_MS = 2500;
    const MEDIA_COHORT_REVEAL_CAP_MS = 2500;
    // Entrance windows. These only decide how long the CSS flags stay on the
    // DOM; foundry.css owns the actual curves. Each is the longest animation
    // under that flag plus a frame of slack.
    const DETAIL_ENTRANCE_TOTAL_MS = 700;
    const DETAIL_NAV_TOTAL_MS = 300;
    const SPELL_DEAL_TOTAL_MS = 900;
    // WHERE THE DECK'S WAVE IS ANCHORED, and the reason the summoner arrives
    // first. The portrait's materialize starts 250ms into the entrance and
    // reaches full opacity at its overshoot; this anchors the deal past that,
    // so the deck deals ON the portrait's impact beat rather than in front of
    // it. At 140 it did the opposite — the first card started at t=140 and the
    // subject of a paid, minutes-long generation landed third.
    //
    // The anchor is ABSOLUTE: `entranceStart + SPELL_DEAL_LEAD_MS`. When the
    // raster cohort releases before it the remainder is a positive
    // `animation-delay`; when it releases after, the elapsed part is applied as
    // a NEGATIVE one that back-dates the wave to the same instant. So the
    // reading order holds no matter how the rasters settle, and a deck slower
    // than the whole wave simply appears at rest. It is an offset in a
    // timeline, never a gate: nothing is hidden by it, because `md-deal-in`
    // animates no opacity at all (docs/asset-loading.md law 2).
    //
    // MUST stay greater than the portrait's own animation-delay in
    // foundry.css. `moonDreamDetailEntrance.test.js` derives both numbers from
    // the source and fails if a future re-timing inverts them.
    const SPELL_DEAL_LEAD_MS = 460;
    // The impact beat: the spark burst fires at the materialize's overshoot
    // peak (250ms delay + 54% of 380ms), so the sparks leave the frame on the
    // frame it snaps outward.
    const DETAIL_ENTRANCE_IMPACT_MS = 450;
    // An arming that is never consumed must not sit there waiting to decorate
    // an unrelated repaint minutes later. A detail read that takes longer than
    // this has stopped being the navigation the player made.
    const DETAIL_ENTRANCE_ARM_TTL_MS = 12000;
    const IDLE_POLL_INTERVAL_MS = 7000;
    // The first view's bounded wait for its pictures (faces, portrait, deck)
    // to settle: the Foundry surface's own 4 s cap, checked every 50 ms.
    const FIRST_VIEW_SETTLE_CAP_MS = 4000;
    const FIRST_VIEW_SETTLE_POLL_MS = 50;
    // How long the first view a surface opens on waits for faces that draw
    // their own loading well: the shared grace (foundry-tabs.js holds a tab
    // switch for the same 450 ms).
    const FIRST_VIEW_WELLS_GRACE_MS = 450;
    /* A REOPEN SHOWS WHAT THE TAB SHOWED A MOMENT AGO (rememberView). Round 14
       and its review: the Foundry closed on Moon Dream and reopened a few
       seconds later waited 0.7-3.1 s behind the tile for the very library,
       summoner and prices it had just shown, past the shared grace and slower
       than the baseline on the phone seats. What this tab read in the last
       minute is shown at once and re-read behind it, the Collection's rule
       for its page cache (collection.js PAGE_PRESENT_MAX_AGE_MS): the re-read
       reconciles in place, and an unchanged answer changes nothing. */
    const REMEMBERED_VIEW_MAX_AGE_MS = 60_000;
    // The page a library with no laid-out rail reads (fittedRunsPageSize).
    const DESKTOP_RUNS_PAGE_SIZE = 8;
    // The runs-page route's own cap (server.js, /api/moon-dream/runs-page).
    const RUNS_PAGE_SIZE_MAX = 24;
    const PHONE_RUNS_PAGE_SIZE = 6;
    // Two rows of two (moon-dream.css): each row a face, a name and its
    // status beneath, above the pager, including the 667x275 phone page box.
    const PHONE_LANDSCAPE_RUNS_PAGE_SIZE = 4;
    const PHONE_RUNS_PAGE_SIZES = new Set([
        PHONE_RUNS_PAGE_SIZE,
        PHONE_LANDSCAPE_RUNS_PAGE_SIZE,
    ]);
    const DESKTOP_SPELLS_PAGE_SIZE = 8;
    const TABLET_SPELLS_PAGE_SIZE = 6;
    const MOBILE_SPELLS_PAGE_SIZE = 4;
    /* A FINISHED SUMMONER'S DECK IS CUT FROM THE BOX IT HAS (moon-dream.css;
       moon-dream-deck-fit.js): the most uniform cards up to the layout's cap
       (two rows of four unless it says otherwise), none narrower than the
       game's physical card (the product call of 2026-07-24: "1.0x of the
       physical card is the smallest acceptable rendering"; the owner,
       2026-10-03: "let the cards take the real space"), nor than a phone's
       art can be read at. Four is the page only where the deck cannot be
       measured (the narrow fallback layout, or no layout at all). */
    const COMPLETED_SPELLS_PAGE_SIZE = MoonDreamDeckFit.UNMEASURED_PAGE_SIZE;
    const DECK_MIN_CARD_SHARE = 1;
    const DECK_MIN_CARD_PX = 50;
    const CARD_ASPECT = 12.29 / 22.2;
    const DIRECT_MEDIA_RETRY_DELAYS_MS = Object.freeze([1000, 3000, 8000, 20000, 30000]);
    const DIRECT_MEDIA_LOAD_TIMEOUT_MS = 8000;
    const DIRECT_MEDIA_ATTACHMENT_GRACE_MS = 5000;
    // The art grace a loading well stays invisible for (cards.css
    // --card-art-grace): past it the player has seen the well.
    const DIRECT_MEDIA_GRACE_MS = 450;
    const RUN_STALE_WARNING_MS = 60_000;
    const READ_REQUEST_TIMEOUT_MS = 15_000;
    const PENDING_START_STORAGE_PREFIX = 'moonfall:moon-dream:pending-start:';
    const PENDING_RECONCILE_MAX_DELAY_MS = 60_000;
    const PENDING_RECONCILE_WINDOW_MS = 2 * 60_000;
    const PAID_MUTATION_TIMEOUT_MS = 30_000;
    // The prompt check spends nothing; a slow answer must not hold the player.
    const PROMPT_CHECK_TIMEOUT_MS = 8_000;
    const PROMPT_POLICY_CODE = 'moon_dream_prompt_policy';
    const SELECTION_RECONCILIATION_MAX_ATTEMPTS = 3;
    const SUPPORTED_RUN_LANGUAGES = new Set(['en', 'ja', 'ko', 'zh-CN', 'de', 'fr', 'es', 'pt']);

    function sameAuthenticatedAccount(firstUser, secondUser) {
        const firstUid = typeof firstUser?.uid === 'string' ? firstUser.uid : null;
        const secondUid = typeof secondUser?.uid === 'string' ? secondUser.uid : null;
        return Boolean(firstUid && firstUid === secondUid);
    }

    function readDreamSurfaceActive() {
        if (document.visibilityState === 'hidden') return false;
        const lifecycle = FoundryLifecycle;
        if (typeof lifecycle?.isTabActive === 'function') {
            return lifecycle.isTabActive('moon-dream-panel');
        }
        return lifecycle?.isActive?.() !== false;
    }

    const state = {
        initialized: false,
        root: document,
        eventController: null,
        authRetryTimer: null,
        authUnsubscribe: null,
        user: null,
        runs: [],
        selectedRunId: null,
        runsPage: 1,
        runsPageSize: DESKTOP_RUNS_PAGE_SIZE,
        // The library page the window's rail holds (fittedRunsPageSize).
        runsFit: null,
        runsNextCursor: null,
        runsIsDone: true,
        runsPageCursors: new Map([[1, null]]),
        // When the page shown and the selected summoner were last read (ms).
        runsReadAt: null,
        detailReadAt: null,
        // The newest summoner, as page 1 last named it (captureViewState).
        latestRunId: null,
        /* An opening's summoner is the newest one, named before the library
           is read: true until page 1 confirms or replaces it (loadRunsPage). */
        openingLatest: false,
        spellsPage: 1,
        spellsPageSize: DESKTOP_SPELLS_PAGE_SIZE,
        // The last cut of a finished summoner's deck (moon-dream-deck-fit.js).
        completedDeckFit: null,
        spellPageRunId: null,
        pollTimer: null,
        pollDelayMs: null,
        pollPromise: null,
        // The view the poll in flight loads: `read` once it has read the page
        // and the summoner it shows (pollTick).
        pollView: null,
        // The last detail read started ({ id, runId }), kept after it ends.
        lastDetailRequest: null,
        loadingRuns: false,
        loadingDetail: false,
        detailRequestGeneration: 0,
        detailRequest: null,
        starting: false,
        selectingArt: false,
        selectingArtRunId: null,
        selectionMutationGeneration: 0,
        deletingSummoner: false,
        deletionMutationGeneration: 0,
        // Deletions the server has accepted: a library read that left before
        // one of them still lists its summoner, so its answer is read again.
        deletionsAccepted: 0,
        pendingDeletionRunId: null,
        pricing: null,
        artVariationCount: null,
        stardustBalance: null,
        generationNotice: null,
        currentRunDetails: null,
        // The UI language the detail's chrome was last painted in: its labels
        // come from uiText, not from the run, so an identical run re-read in
        // another language is not "unchanged".
        detailLanguage: null,
        // The panel's own words, each with how to say it again (labelDetail),
        // and whether they are ALL of its words: then a language change
        // re-describes the panel in place (relabelRunDetail).
        detailLabels: [],
        detailRelabelable: false,
        balanceMutationVersion: 0,
        pendingStart: null,
        reconciledPendingRequestId: null,
        pendingReconcileAttempts: 0,
        pendingReconcileRetryAt: 0,
        artSelections: new Map(),
        selectionFeedback: new Map(),
        deletionFeedback: null,
        createdBannerRuns: new Set(),
        retryingSpellsRunId: null,
        generationCooldownUntil: 0,
        generationCooldownTimer: null,
        surfaceActive: readDreamSurfaceActive(),
        lifecycleUnsubscribe: null,
        tabLifecycleUnsubscribe: null,
        readControllers: new Set(),
        resumePollGeneration: 0,
        resumePollPromise: null,
        suspendedViewState: null,
        viewInteractionVersions: {
            detailScroll: 0,
            runsScroll: 0,
            focusSelection: 0,
        },
        spellPreviewGeneration: 0,
        spellDeckGeneration: 0,
        // The spell-deck page the player could see before this paint
        // ({ runId, page }), and when each spell first waited for its art
        // (run:card -> ms), so a rebuilt arrival's cap keeps counting.
        shownSpellDeck: null,
        spellArrivalAt: new Map(),
        carriedDetailMedia: null,
        // The summoner reveal is armed by NAVIGATION and consumed by the next
        // paint, because the panel repaints on every poll tick and every page
        // turn — animating on build would loop the entrance forever.
        detailEntranceArmed: false,
        detailEntranceArmedAt: 0,
        detailEntrancePlaying: false,
        detailEntranceStartedAt: 0,
        detailEntranceTimer: null,
        detailEntranceImpactTimer: null,
        detailNavTimer: null,
        // Stage last painted per run, and the runs whose arrival already paid
        // out. Both are session-scoped: the chime is a one-time payoff.
        detailStageByRun: new Map(),
        celebratedRuns: new Set(),
        // A run whose arrival happened out of sight (a phone showing the
        // library); its celebration waits for the player to see it.
        unseenCelebrationRunId: null,
    };

    function runIdentity(run) {
        const id = run?.id ?? run?._id;
        return (typeof id === 'string' && id) ? id : null;
    }

    const elements = {
        form: null,
        prompt: null,
        generate: null,
        status: null,
        runs: null,
        runsPagination: null,
        runsPrev: null,
        runsNext: null,
        runsPageInfo: null,
        detail: null,
        stardust: null,
        cost: null,
    };

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        const text = translated && translated !== key ? translated : fallback;
        /* BOTH brace styles, double FIRST.
           The copy uses `{{name}}` (as ui.json writes it) and, in a few
           places, single `{name}`. A helper that knows only one leaves the
           other on screen whenever i18n has not resolved. Order matters:
           substituting the single form first turns `{{count}}` into `{3}`,
           which is exactly how starter-reward.js was rendering it. */
        return Object.entries(replacements || {}).reduce(
            (result, [name, value]) => String(result)
                .split(`{{${name}}}`).join(String(value))
                .split(`{${name}}`).join(String(value)),
            String(text),
        );
    }

    const formatStardust = (value) => Stardust?.format?.(value) ?? String(value);

    function renderStardustBalance(value) {
        if (!elements.stardust) return;
        if (!Number.isSafeInteger(value) || value < 0) {
            delete elements.stardust.dataset.countValue;
            elements.stardust.textContent = '';
            return;
        }
        elements.stardust.dataset.countValue = String(value);
        elements.stardust.textContent = formatStardust(value);
    }

    function formatCooldownDuration(ms) {
        const durationMs = Number(ms);
        if (!Number.isFinite(durationMs) || durationMs <= 0) return '';
        const minutes = Math.ceil(durationMs / 60_000);
        if (minutes <= 1) return uiText('common.underMinute', 'under a minute');
        if (minutes < 60) return uiText('common.minutesShort', '{{count}} min', { count: minutes });
        const hours = Math.ceil(minutes / 60);
        if (hours < 24) return uiText('common.hoursShort', '{{count}}h', { count: hours });
        const days = Math.ceil(hours / 24);
        return uiText('common.daysShort', '{{count}}d', { count: days });
    }

    function resolvePositiveCooldownMs(value, fallbackMs) {
        const durationMs = Number(value);
        return Number.isFinite(durationMs) && durationMs > 0 ? durationMs : fallbackMs;
    }

    function resolveCooldownUntil(value, retryAfterMs, now = Date.now()) {
        const cooldownUntil = Number(value);
        return Number.isFinite(cooldownUntil) && cooldownUntil > now
            ? cooldownUntil
            : now + retryAfterMs;
    }

    function buildCreationCooldownMessage(data) {
        const base = data?.error || uiText(
            'foundry.moonDream.creationRateLimited',
            'Too many failed AI creations. Please wait before starting another Moon Dream.'
        );
        const wait = formatCooldownDuration(data?.retryAfterMs);
        if (!wait) return base;
        return uiText('foundry.moonDream.creationRateLimitedWithWait', `${base} Try again in ${wait}.`, {
            message: base,
            wait,
        });
    }

    function isGenerationCooldownActive(now = Date.now()) {
        return Number(state.generationCooldownUntil || 0) > now;
    }

    function clearGenerationCooldownIfExpired(now = Date.now()) {
        if (!isGenerationCooldownActive(now)) {
            state.generationCooldownUntil = 0;
            if (state.generationCooldownTimer) {
                clearTimeout(state.generationCooldownTimer);
                state.generationCooldownTimer = null;
            }
            return true;
        }
        return false;
    }

    function applyGenerationCooldown(data) {
        const now = Date.now();
        const retryAfterMs = resolvePositiveCooldownMs(data?.retryAfterMs, 1000);
        state.generationCooldownUntil = resolveCooldownUntil(data?.cooldownUntil, retryAfterMs, now);
        if (state.generationCooldownTimer) {
            clearTimeout(state.generationCooldownTimer);
        }
        if (elements.generate) {
            elements.generate.disabled = true;
        }
        setStatus(buildCreationCooldownMessage({ ...data, retryAfterMs }), 'error');
        state.generationCooldownTimer = setTimeout(() => {
            state.generationCooldownTimer = null;
            state.generationCooldownUntil = 0;
            updateGenerateAvailability();
            setStatus('');
        }, Math.min(Math.max(1, retryAfterMs), 86_400_000));
    }

    function cacheElements() {
        const byId = (id) => state.root === document
            ? document.getElementById(id)
            : state.root?.querySelector?.(`#${id}`);
        elements.prompt = byId('moon-dream-prompt');
        elements.form = elements.prompt?.form
            || state.root?.querySelector?.('.moon-dream-create-controls')
            || null;
        elements.generate = byId('moon-dream-generate');
        elements.status = byId('moon-dream-status');
        elements.runs = byId('moon-dream-runs');
        elements.runsPagination = byId('moon-dream-runs-pagination');
        elements.runsPrev = byId('moon-dream-runs-prev');
        elements.runsNext = byId('moon-dream-runs-next');
        elements.runsPageInfo = byId('moon-dream-runs-page-info');
        elements.detail = byId('moon-dream-detail');
        elements.stardust = byId('moon-dream-stardust');
        elements.cost = byId('moon-dream-cost');
        const content = state.root?.querySelector?.('#moon-dream-panel-content');
        if (content && !content.dataset.mobileView) content.dataset.mobileView = 'history';
        if (elements.runs && !elements.runs.dataset.loadState) {
            elements.runs.dataset.loadState = 'idle';
            elements.runs.setAttribute('aria-busy', 'false');
        }
    }

    function setRunsLoadState(loadState) {
        if (!elements.runs) return;
        elements.runs.dataset.loadState = loadState;
        elements.runs.setAttribute('aria-busy', loadState === 'loading' ? 'true' : 'false');
    }

    function updateCreateStripVisibility() {
        const strip = state.root?.querySelector?.('.moon-dream-create-strip');
        if (!strip) return;
        const content = state.root?.querySelector?.('#moon-dream-panel-content');
        const hidden = usesMobileMasterDetail()
            && content?.dataset.mobileView === 'detail';
        strip.hidden = hidden;
        strip.setAttribute('aria-hidden', String(hidden));
    }

    /**
     * Master/detail is a spatial move, not a swap: the incoming pane travels in
     * from the side it conceptually came from. The flag is one-shot — it must
     * not survive to decorate an unrelated repaint.
     */
    function markMobileNavDirection(content, direction) {
        if (!content) return;
        content.dataset.mobileNav = direction;
        clearTimeout(state.detailNavTimer);
        state.detailNavTimer = setTimeout(() => {
            state.detailNavTimer = null;
            if (content.dataset.mobileNav === direction) delete content.dataset.mobileNav;
        }, DETAIL_NAV_TOTAL_MS);
    }

    /** Arm the summoner reveal for the NEXT paint of the detail panel. */
    function armDetailEntrance() {
        state.detailEntranceArmed = true;
        state.detailEntranceArmedAt = Date.now();
        // Build the shared FX overlay NOW, while we are still waiting on the
        // detail read. It is a fixed full-viewport element, so creating it is a
        // document-wide style recalc — and the impact beat wants it 300ms into
        // an animation, which is the worst possible moment to ask for one.
        try { FX?.ensureLayer?.(); } catch (_) { /* no FX module */ }
    }

    /**
     * Drop an arming that will never be honoured. A navigation whose detail
     * read FAILED has no reveal to play, and leaving the arm set meant the
     * entrance fired later on whatever unrelated repaint happened to come
     * next — a poll tick on a different run, a page turn, a retry.
     */
    function disarmDetailEntrance() {
        state.detailEntranceArmed = false;
        state.detailEntranceArmedAt = 0;
    }

    /**
     * Consume the arming, if any, and flag this paint to play the reveal.
     * Returns whether it plays, so the spell deck knows to deal itself in.
     */
    function consumeDetailEntrance() {
        if (!elements.detail) return false;
        const expired = state.detailEntranceArmed
            && Date.now() - state.detailEntranceArmedAt > DETAIL_ENTRANCE_ARM_TTL_MS;
        if (!state.detailEntranceArmed || expired) {
            if (expired) disarmDetailEntrance();
            delete elements.detail.dataset.reveal;
            return false;
        }
        disarmDetailEntrance();
        state.detailEntranceStartedAt = Date.now();
        elements.detail.dataset.reveal = 'play';
        clearTimeout(state.detailEntranceTimer);
        state.detailEntranceTimer = setTimeout(() => {
            state.detailEntranceTimer = null;
            if (elements.detail?.dataset.reveal === 'play') delete elements.detail.dataset.reveal;
        }, DETAIL_ENTRANCE_TOTAL_MS);
        return true;
    }

    /**
     * The payoff beat. A silent reveal reads as a bug, and a frame that
     * resolves with no impact reads as a div becoming visible — so the landing
     * gets the shared SFX channel (so `audio-settings.js` volume and mute are
     * respected) and the same spark burst the forge fires on a craft. Sound
     * leads the picture: the chime plays now, the materialize starts 90ms
     * later. Both are best-effort decoration — a missing sound file or an
     * absent FX module may never break the panel.
     */
    function celebrateSummonerReveal() {
        const frame = elements.detail?.querySelector?.('.moon-dream-portrait-frame');
        if (!frame) return;
        /* The arrival IS the reveal, so it arms its own presentation rather
           than borrowing the mobile navigation's. Keying the burst off that
           navigation flag is what confined the whole payoff to phones. */
        if (elements.detail) {
            elements.detail.dataset.reveal = 'play';
            clearTimeout(state.detailEntranceTimer);
            state.detailEntranceTimer = setTimeout(() => {
                state.detailEntranceTimer = null;
                if (elements.detail?.dataset.reveal === 'play') delete elements.detail.dataset.reveal;
            }, DETAIL_ENTRANCE_TOTAL_MS);
        }
        try { SoundEffects?.play?.('card_reveal'); } catch (_) { /* muted or unloaded */ }
        clearTimeout(state.detailEntranceImpactTimer);
        state.detailEntranceImpactTimer = setTimeout(() => {
            state.detailEntranceImpactTimer = null;
            if (!frame.isConnected || elements.detail?.dataset.reveal !== 'play') return;
            // ONE layout read, and the burst is handed the rectangle rather than
            // the element so it does not take a second one inside the window.
            const rect = frame.getBoundingClientRect?.();
            if (!rect) return;
            // Back before the beat hides the pane on a phone; a burst sized
            // from a frame with no box lands in the library's corner.
            if (usesMobileMasterDetail() && !hasBox(rect)) return;
            // Sized to the frame it decorates, so it reads as that frame's own
            // impact rather than a fixed shower of confetti over a thumbnail.
            // FX.burst opts itself out under prefers-reduced-motion.
            FX?.burst?.(rect, {
                count: 8,
                radiusX: Math.max(uiPx(16), rect.width * 0.42),
                radiusY: Math.max(uiPx(11), rect.height * 0.42),
            });
        }, DETAIL_ENTRANCE_IMPACT_MS);
    }

    /* A phone showing the library hides the detail pane (`display: none`), so
       a summoner that finishes there has nothing to land on: the chime came
       from nowhere and the burst was sized from a zero box in the corner. The
       whole celebration waits for the first moment the player sees that
       summoner — setMobileDreamView('detail') showing its panel, or its panel
       painted in view later — and is not played if that never happens. The
       desktop shows the pane beside the library and celebrates at once. */
    function celebrateSummonerArrival(runId) {
        if (usesMobileMasterDetail() && !portraitFrameHasBox()) {
            state.unseenCelebrationRunId = runId;
            return;
        }
        celebrateSummonerReveal();
    }

    /** Play a held arrival once the panel on screen is that run's, in view. */
    function playUnseenCelebration(runId) {
        if (!runId || state.unseenCelebrationRunId !== runId || !portraitFrameHasBox()) return;
        state.unseenCelebrationRunId = null;
        celebrateSummonerReveal();
    }

    function portraitFrameHasBox() {
        return hasBox(elements.detail?.querySelector?.('.moon-dream-portrait-frame')?.getBoundingClientRect?.());
    }

    function hasBox(rect) {
        return Boolean(rect) && rect.width > 0 && rect.height > 0;
    }

    function setMobileDreamView(view = 'history') {
        const content = state.root?.querySelector?.('#moon-dream-panel-content');
        const next = view === 'detail' ? 'detail' : 'history';
        const changed = Boolean(content) && content.dataset.mobileView !== next;
        if (content) content.dataset.mobileView = next;
        if (changed && usesMobileMasterDetail()) {
            markMobileNavDirection(content, next === 'detail' ? 'forward' : 'back');
            /* No entrance on navigation any more (owner, 2026-09-21: the
               portrait bloom and the card deal read as the screen "morphing"
               into its final state). The completed screen paints settled.
               A run that finishes in front of the player still gets its
               celebration (celebrateSummonerReveal), which arms itself. */
        }
        if (next !== 'detail' && usesMobileMasterDetail()) {
            setCompletedPresentation(false);
            return;
        }
        /* The library dropped the finished presentation above (its `:has()`
           page-box rules must not reach the library pane). A detail still
           painted for the selected run is shown again as it is — re-selecting
           that summoner keeps its panel, and an identical re-read no longer
           repaints it — so its presentation comes back in this same task, or
           the pane shows the three-column DOM without its rules. A deck
           painted while the pane was hidden had no cells to measure, so it is
           fitted here too, before the first frame of the shown pane. */
        if (next === 'detail' && canPreserveRenderedRunDetail(state.selectedRunId)) {
            setCompletedPresentation(isCompletedPresentation(state.currentRunDetails));
            if (changed) refitCompletedDeck();
            playUnseenCelebration(state.selectedRunId);
            return;
        }
        updateCreateStripVisibility();
    }

    function usesMobileMasterDetail() {
        return Platform?.isMobileViewport?.() === true;
    }

    /* Viewport-driven like the rest of the surface, so a DevTools device
       emulator and a shrunk desktop window exercise the same path a phone does.
       Portrait keeps six: the whole app shows its rotate prompt below 1024px
       portrait, so that branch only serves a viewport with height to spare. */
    function isLandscapeViewport() {
        try {
            return window.innerWidth > window.innerHeight;
        } catch (_) {
            return false;
        }
    }

    /* ONE STATUS LINE, as the strip lays it out. The composer's status shows
       a refused delete or a failed generation under the prompt and shortens
       the panel by its box and the strip's gap; while it is empty that room
       is still the list's, so the fit sets it aside. */
    function statusLineReserve() {
        const status = elements.status;
        if (!status || status.textContent.trim() || typeof window.getComputedStyle !== 'function') return 0;
        const line = window.getComputedStyle(status);
        const px = (value) => Number.parseFloat(value) || 0;
        const lineHeight = px(line.lineHeight) || px(line.fontSize) * 1.2;
        const strip = status.parentElement ? window.getComputedStyle(status.parentElement) : null;
        return lineHeight
            + px(line.paddingTop) + px(line.paddingBottom)
            + px(line.borderTopWidth) + px(line.borderBottomWidth)
            + px(strip?.rowGap);
    }

    /* THE SUMMONER LIBRARY IS A PAGE, NEVER A SCROLLER (owner, 4 October
       2026: "it already has pagination, should not have scrolling"). Desktop
       and tablet read a fixed eight, and eight rows are 464px: the rail holds
       325 at 1400x640 and 453 at 1366x768, so from an account's eighth
       summoner the list scrolled. A page is the rows the rail holds — its
       room, less a status line not yet showing, over one row and its gap
       (moon-dream.css states both; every row is one height there).

       The answer belongs to the WINDOW, as the detail's arrangement does: a
       status line coming and going must not re-cut the library and send the
       player back to its first page. A rail with no box (another tab is
       showing) keeps the last answer, or reads the default page. */
    function fittedRunsPageSize() {
        const runs = elements.runs;
        if (!runs) return null;
        const key = `${window.innerWidth}x${window.innerHeight}`;
        if (state.runsFit?.key === key) return state.runsFit.pageSize;
        const room = runs.clientHeight;
        if (!room) return state.runsFit?.pageSize ?? null;
        const { row, gap } = resolveLengths(runs, {
            row: 'var(--dream-run-row, 0px)',
            gap: 'var(--dream-run-gap, 0px)',
        });
        if (!(row > 0)) return null;
        const fits = Math.floor((room - statusLineReserve() + gap) / (row + gap));
        const pageSize = Math.max(1, Math.min(RUNS_PAGE_SIZE_MAX, fits));
        state.runsFit = { key, pageSize };
        return pageSize;
    }

    function runsPageSize() {
        if (!usesMobileMasterDetail()) return fittedRunsPageSize() ?? DESKTOP_RUNS_PAGE_SIZE;
        return isLandscapeViewport()
            ? PHONE_LANDSCAPE_RUNS_PAGE_SIZE
            : PHONE_RUNS_PAGE_SIZE;
    }

    function spellsPageSize() {
        if (elements.detail?.dataset.stage === 'complete') {
            return state.completedDeckFit?.pageSize || COMPLETED_SPELLS_PAGE_SIZE;
        }
        if (usesMobileMasterDetail()) return MOBILE_SPELLS_PAGE_SIZE;
        return Platform?.isTabletLandscapeViewport?.() === true
            ? TABLET_SPELLS_PAGE_SIZE
            : DESKTOP_SPELLS_PAGE_SIZE;
    }

    /**
     * MOON DREAM SIGIL, IN THE SESSION THAT EARNED IT.
     *
     * `convex/moonDreamLifecycleData.js` awards the cardback when the spell
     * stage settles — the run reaching `complete` — not at art selection where
     * the "Summoner Created!" banner fires. Before this, nothing on the surface
     * asked for the banner, so the sigil waited for the next app launch.
     *
     * Only a live transition counts: a run this session already observed as
     * unfinished, now finished. A history of finished runs on arrival is not a
     * transition and costs nothing, which is what keeps this off the poll bill.
     */
    const runsSeenUnfinished = new Set();
    const runsDrained = new Set();
    /* The statuses under which a run is still being generated. Queue copy
       and the completion line both key on these: a run in any other state
       (choosing art, failed, deleting) is not "in the archive". */
    const GENERATING_RUN_STATUSES = new Set([
        'pending',
        'queued',
        'generating',
        'art',
        'summoner_queued',
        'summoner_designing',
        'summoner_art_generating',
        'spells_queued',
        'spells_designing',
        'spells_art_generating',
    ]);
    // Runs this session watched being generated; only their completion is
    // news worth a status line. A deleting run that replays a stale
    // `complete` snapshot is in `runsSeenUnfinished` too, and is not.
    const runsSeenGenerating = new Set();

    /* The Collection presents pages it read in the last minute from its page
       cache. Found 25 September 2026: a deletion here, or a run's spells
       entering the Collection, left its next Cards view on the old holdings
       until a revalidation morphed them. A change to the account's cards made
       here is announced; the Collection drops every page read before it
       (collection.js setupHoldingsFollow). */
    function announceHoldingsChanged() {
        window.dispatchEvent(new CustomEvent('moonfall:holdings-changed', { detail: { source: 'moon-dream' } }));
    }

    function noteRunCompletions(runs) {
        let completedNow = false;
        let generatedRun = null;
        for (const run of Array.isArray(runs) ? runs : [runs]) {
            const id = run?.id;
            const status = run?.status;
            if (!id || !status) continue;
            const notice = state.generationNotice;
            if (notice?.runId === id && notice.status !== status) setStatus('');
            if (status !== 'complete') {
                runsSeenUnfinished.add(id);
                if (GENERATING_RUN_STATUSES.has(status)) runsSeenGenerating.add(id);
                continue;
            }
            // Completion FLAPS: a detail repaint can replay an older snapshot,
            // so one poll tick reads complete → spells → complete. Latch per
            // run, or that stale frame spends a second acknowledgement call.
            if (!runsSeenUnfinished.has(id) || runsDrained.has(id)) continue;
            runsDrained.add(id);
            completedNow = true;
            if (runsSeenGenerating.has(id)) generatedRun = run;
        }
        if (!completedNow) return;
        // The run's spells enter the Collection together as it finishes.
        announceHoldingsChanged();
        void getCloudAchievementBannerConsumer()?.();
        // The status line was left at the last action ("Summoner confirmed.
        // Spell creation has begun.") and nothing rewrote it when the spells
        // landed, so a finished run sat under a line saying it had just
        // started. An error still standing there is not painted over: it is
        // the player's to read first.
        if (!generatedRun || elements.status?.classList?.contains('error')) return;
        setStatus(uiText(
            'foundry.moonDream.runComplete',
            '{{name}} is complete. The new spells are in your Collection.',
            { name: generatedRun.summonerName || generatedRun.prompt || 'Moon Dream' },
        ), 'success');
    }

    function isCompletedPresentation(run) {
        return run?.status === 'complete'
            && Boolean(run?.summoner)
            && Boolean(run?.selectedCandidateId || run?.selectedImageUrl || run?.summoner?.imageUrl);
    }

    /** A phone showing its library hides the detail pane behind it. */
    function isDetailBehindLibrary() {
        const content = /** @type {HTMLElement | null} */ (
            state.root?.querySelector?.('#moon-dream-panel-content') || null
        );
        return usesMobileMasterDetail() && content?.dataset.mobileView === 'history';
    }

    function setCompletedPresentation(active) {
        /* A phone showing the library has no finished summoner on screen: the
           detail pane is hidden, and the finished presentation's `:has()`
           page-box rules would reshape the library around it. A repaint behind
           the library (a language change, a run finishing live) paints the
           summoner's DOM but leaves the presentation to the library;
           setMobileDreamView('detail') applies it when the pane is shown. */
        const isActive = active === true && !isDetailBehindLibrary();
        const presentation = isActive ? 'complete' : 'workflow';
        const panel = /** @type {HTMLElement | null} */ (state.root?.matches?.('#moon-dream-panel')
            ? state.root
            : state.root?.querySelector?.('#moon-dream-panel'));
        // Same-value attribute writes are still mutations; every repaint and
        // every re-entry into the painted detail passes through here.
        if (panel && panel.dataset.presentation !== presentation) panel.dataset.presentation = presentation;
        elements.detail?.classList?.toggle('moon-dream-detail--complete', isActive);
        // The finished summoner's arrangement is measured with its deck.
        if (!isActive && elements.detail?.dataset.arrangement) delete elements.detail.dataset.arrangement;
        updateCreateStripVisibility();
    }

    function normalizePricing(data) {
        const pricing = data?.pricing;
        const summoner = Number(pricing?.summoner);
        const spells = Number(pricing?.spells);
        const total = Number(pricing?.total);
        const variationCount = Number(data?.artVariationCount);
        if (
            !Number.isSafeInteger(summoner) || summoner <= 0
            || !Number.isSafeInteger(spells) || spells <= 0
            || !Number.isSafeInteger(total) || total !== summoner + spells
            || !Number.isSafeInteger(variationCount) || variationCount !== 6
        ) {
            return null;
        }
        return { pricing: { summoner, spells, total }, variationCount };
    }

    function updatePricingUi() {
        const pricing = state.pricing;
        if (elements.cost) {
            const compactPricing = pricing && Object.fromEntries(
                Object.entries(pricing).map(([key, value]) => [key, formatStardust(value)])
            );
            const totalWithMoney = pricing && Stardust?.withMoneyLive?.(
                Number(pricing.total),
                () => updatePricingUi(),
            );
            elements.cost.textContent = pricing
                ? uiText(
                    'foundry.moonDream.stagePricingMoney',
                    'Total cost {{total}}',
                    { ...compactPricing, total: totalWithMoney || `${compactPricing.total} ✦` },
                )
                : uiText('foundry.moonDream.pricingLoading', 'Loading stage prices...');
            /* The title carries the EXACT metadata the compacted visible text
               drops, so it must not reuse `totalWithMoney` — that string runs
               through the same compacting formatter and would republish
               "12.3K" as the precise figure. Pair the raw total with the money
               equivalent alone. */
            const exactTotalMoney = pricing
                && Stardust?.moneyEquivalent?.(Number(pricing.total));
            elements.cost.title = pricing
                ? uiText(
                    'foundry.moonDream.stagePricingMoney',
                    'Total cost {{total}}',
                    {
                        ...pricing,
                        total: exactTotalMoney
                            ? `${pricing.total} ✦ (${exactTotalMoney})`
                            : `${pricing.total} ✦`,
                    },
                )
                : elements.cost.textContent;
        }
        if (elements.generate) {
            // The button names the action only. "(100 ✦)" on it read as the
            // whole price when the spells cost 400 more; the confirmation the
            // click opens spells out both stages (handleGenerate).
            elements.generate.textContent = uiText('foundry.moonDream.generateSummoner', 'Create Summoner');
            elements.generate.removeAttribute('aria-label');
        }
    }

    function updateGenerateAvailability() {
        if (!elements.generate) return;
        const summonerPrice = Number(state.pricing?.summoner);
        elements.generate.disabled = !state.user
            || !state.pricing
            || state.artVariationCount !== 6
            || !Number.isSafeInteger(state.stardustBalance)
            || !Number.isSafeInteger(summonerPrice)
            || state.stardustBalance < summonerPrice
            || Boolean(readPendingStart())
            || state.starting
            || isGenerationCooldownActive();
    }

    function setAuthoritativePricing(data) {
        const normalized = normalizePricing(data);
        if (!normalized) return false;
        state.pricing = normalized.pricing;
        state.artVariationCount = normalized.variationCount;
        updatePricingUi();
        updateGenerateAvailability();
        return true;
    }

    function accountStorageKey() {
        const uid = typeof state.user?.uid === 'string' ? state.user.uid.trim() : '';
        return uid ? `${PENDING_START_STORAGE_PREFIX}${encodeURIComponent(uid.slice(0, 256))}` : null;
    }

    function readPendingStart() {
        const storageKey = accountStorageKey();
        if (!storageKey) return state.pendingStart;
        try {
            const parsed = JSON.parse(window.localStorage?.getItem(storageKey) || 'null');
            if (
                !parsed
                || typeof parsed.requestId !== 'string'
                || typeof parsed.prompt !== 'string'
                || typeof parsed.language !== 'string'
            ) {
                return null;
            }
            return parsed;
        } catch (_) {
            return null;
        }
    }

    function writePendingStart(pending) {
        state.pendingStart = pending;
        state.reconciledPendingRequestId = null;
        state.pendingReconcileAttempts = 0;
        state.pendingReconcileRetryAt = 0;
        const storageKey = accountStorageKey();
        if (!storageKey) return;
        try {
            window.localStorage?.setItem(storageKey, JSON.stringify(pending));
        } catch (_) { }
    }

    function clearPendingStart(requestId) {
        const pending = readPendingStart();
        if (requestId && pending?.requestId !== requestId) return;
        state.pendingStart = null;
        state.reconciledPendingRequestId = null;
        state.pendingReconcileAttempts = 0;
        state.pendingReconcileRetryAt = 0;
        const storageKey = accountStorageKey();
        if (!storageKey) return;
        try {
            window.localStorage?.removeItem(storageKey);
        } catch (_) { }
    }

    function createRequestUuid() {
        if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
        if (typeof window.crypto?.getRandomValues !== 'function') {
            throw new Error('Secure random UUID generation is unavailable');
        }
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));
        return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
    }

    function resolvePendingStart(prompt, language) {
        const existing = readPendingStart();
        if (existing?.prompt === prompt && existing?.language === language) return existing;
        const pending = {
            requestId: createRequestUuid(),
            prompt,
            language,
            createdAt: Date.now(),
        };
        writePendingStart(pending);
        return pending;
    }

    function keepPendingStartForReconciliation(requestId, reconciling) {
        const pending = readPendingStart();
        if (!pending || pending.requestId !== requestId) return;
        state.reconciledPendingRequestId = null;
        if (!reconciling) {
            state.pendingReconcileAttempts = 0;
            state.pendingReconcileRetryAt = 0;
            return;
        }
        state.pendingReconcileAttempts = Math.min(state.pendingReconcileAttempts + 1, 16);
        const exponent = Math.min(state.pendingReconcileAttempts - 1, 4);
        const delayMs = Math.min(
            PENDING_RECONCILE_MAX_DELAY_MS,
            IDLE_POLL_INTERVAL_MS * (2 ** exponent)
        );
        state.pendingReconcileRetryAt = Date.now() + delayMs;
    }

    function normalizeArtCandidateId(value) {
        const candidateId = typeof value === 'string' ? value.trim() : '';
        return candidateId && candidateId.length <= 128 ? candidateId : null;
    }

    /** Drop a portrait the player picked that the run can no longer take. */
    function pruneArtSelection(run) {
        const candidateId = state.artSelections.get(run.id);
        if (!candidateId) return;
        const candidate = Array.isArray(run.candidates)
            ? run.candidates.find((option) => option?.candidateId === candidateId)
            : null;
        if (
            run.selectedCandidateId
            || !['awaiting_art_selection', 'ready'].includes(run.status)
            || candidate?.status !== 'complete'
            || !sanitizeImageUrlForDirectClient(candidate?.imageUrl)
        ) {
            state.artSelections.delete(run.id);
        }
    }

    /**
     * THE FOUNDRY OPENS MOON DREAM ON ITS NEWEST SUMMONER (owner, 4 October
     * 2026: "it starts with a random summoner, it should start with the latest
     * summoner selected"). The device view used to carry the page, summoner,
     * portrait pick, deck page and scroll offsets the last visit ended on, so
     * an opening landed there — days later, a summoner from page three. It
     * now keeps the prompt the player had not sent, and the newest summoner
     * page 1 named (`latestRunId`). On a desktop that one is read beside the
     * library, as a summoner the view names always was, and painted only once
     * page 1 confirms it is still the newest; a newer one (made on another
     * device) is selected instead. A phone opens on its library — or on the
     * newest summoner itself when the player left Moon Dream there
     * (`latestOpen`): a run they were watching generate, a portrait choice
     * they were making. A portrait picked on the newest summoner and not yet
     * confirmed comes back with it (`latestArtCandidateId`).
     */
    function restoreViewState(viewState) {
        if (!viewState || typeof viewState !== 'object' || Array.isArray(viewState)) return;
        const prompt = typeof viewState.prompt === 'string'
            ? viewState.prompt.trim().slice(0, PROMPT_MAX_LENGTH)
            : '';
        const latestRunId = typeof viewState.latestRunId === 'string'
            ? viewState.latestRunId.trim().slice(0, 128)
            : '';
        state.latestRunId = latestRunId || null;
        const opensLatest = Boolean(latestRunId)
            && (!usesMobileMasterDetail() || viewState.latestOpen === true);
        state.selectedRunId = opensLatest ? latestRunId : null;
        state.openingLatest = true;
        state.artSelections.clear();
        const pick = normalizeArtCandidateId(viewState.latestArtCandidateId);
        // Checked against the run when it is painted (pruneArtSelection).
        if (state.selectedRunId && pick) state.artSelections.set(state.selectedRunId, pick);
        state.spellPageRunId = null;
        state.spellsPage = 1;
        setMobileDreamView(state.selectedRunId ? 'detail' : 'history');
        if (elements.prompt) elements.prompt.value = prompt;

        if (state.initialized && state.user && state.surfaceActive) {
            showRememberedView();
            state.resumePollGeneration += 1;
            /* The Foundry reopening on this tab restores its device view in
               the task that mounts it, while the opening read has not read
               its view yet: that read carries this one, and the resume only
               waits for it. It used to wait and then read everything again —
               1.5 s the first view waited for (round 14). */
            const carrier = state.pollView && !state.pollView.read
                ? { poll: state.pollPromise, view: state.pollView }
                : null;
            beginResumePolling(state.resumePollGeneration, carrier);
        }
    }

    function captureViewState() {
        const content = state.root?.querySelector?.('#moon-dream-panel-content');
        const onLatest = Boolean(state.selectedRunId) && state.selectedRunId === state.latestRunId;
        return {
            prompt: String(elements.prompt?.value || '').trim().slice(0, PROMPT_MAX_LENGTH),
            latestRunId: state.latestRunId,
            latestOpen: onLatest && content?.getAttribute('data-mobile-view') === 'detail',
            latestArtCandidateId: onLatest
                ? normalizeArtCandidateId(state.artSelections.get(state.selectedRunId))
                : null,
        };
    }

    function formatEtaMinutes(minutes) {
        if (!Number.isFinite(minutes) || minutes <= 0) return null;
        if (minutes < 60) return uiText('common.minutesShort', `${minutes} min`, { count: minutes });
        const hours = Math.floor(minutes / 60);
        const remainder = minutes % 60;
        if (remainder <= 0) return uiText('common.hoursShort', `${hours}h`, { count: hours });
        return uiText('common.hoursMinutesShort', `${hours}h ${remainder}m`, { hours, minutes: remainder });
    }

    function formatEtaWindow(minMs, maxMs) {
        if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) return null;
        const minMinutes = Math.max(1, Math.round(Math.max(45_000, minMs) / 60_000));
        const maxMinutes = Math.max(minMinutes, Math.round(Math.max(45_000, maxMs) / 60_000));
        if (maxMinutes <= 1) {
            return uiText('common.aboutDuration', 'about {{duration}}', {
                duration: uiText('common.oneMinute', '1 min'),
            });
        }
        if (maxMinutes < 60) {
            if (minMinutes >= maxMinutes) {
                return uiText('common.aboutDuration', 'about {{duration}}', {
                    duration: formatEtaMinutes(maxMinutes),
                });
            }
            return uiText('common.aboutDuration', 'about {{duration}}', {
                duration: uiText('common.minutesRangeShort', '{{min}}-{{max}} min', {
                    min: minMinutes,
                    max: maxMinutes,
                }),
            });
        }
        if (minMinutes >= maxMinutes) {
            return uiText('common.aboutDuration', 'about {{duration}}', {
                duration: formatEtaMinutes(maxMinutes),
            });
        }
        return uiText('common.aboutRange', 'about {{min}}–{{max}}', {
            min: formatEtaMinutes(minMinutes),
            max: formatEtaMinutes(maxMinutes),
        });
    }

    function buildQueueEtaLine(run) {
        // Queue copy belongs to a run that is still being generated. A
        // finished one carries none, whatever its queue metadata says: the
        // backend served a completed summoner as "queued, about 1 min" while
        // its post-completion translation follow-ups drained, and the list
        // printed "Completed" over "The lunar archive is stirring".
        if (!GENERATING_RUN_STATUSES.has(run?.status)) return null;
        // The server drops its estimate once the work has outlived it, rather
        // than recomputing the same optimistic number forever. Say so: a run
        // stuck for an hour under "Expected in about 1 min" reads as a lie.
        if (run?.queueOverdue === true) {
            return uiText(
                'foundry.moonDream.queueOverdue',
                'The lunar archive is taking longer than usual. Your place is held.'
            );
        }
        const eta = formatEtaWindow(run?.estimatedWaitMinMs, run?.estimatedWaitMaxMs);
        // Waiting in line behind someone, the run says exactly where: the queue
        // knows it. First in line is the moment before it starts.
        const position = Number(run?.queuePosition);
        if (run?.queueStatus === 'queued' && Number.isSafeInteger(position) && position > 1) {
            return eta
                ? uiText('foundry.moonDream.queuePlaceWithEta', 'The lunar archive is stirring. You are #{{position}} in line. Expected in {{eta}}.', { position, eta })
                : uiText('foundry.moonDream.queuePlace', 'The lunar archive is stirring. You are #{{position}} in line.', { position });
        }
        if (run?.queueStatus === 'queued') {
            return eta
                ? uiText('foundry.moonDream.queueQueuedWithEta', 'The lunar archive is stirring. Expected in {{eta}}.', { eta })
                : uiText('foundry.moonDream.queueQueued', 'The lunar archive is stirring.');
        }
        if (run?.queueStatus === 'running') {
            return eta
                ? uiText('foundry.moonDream.queueRunningWithEta', 'The lunar archive is weaving your dream. Expected in {{eta}}.', { eta })
                : uiText('foundry.moonDream.queueRunning', 'The lunar archive is weaving your dream.');
        }
        return null;
    }

    function updateRunsPagination() {
        const hasPreviousPage = state.runsPage > 1;
        const hasNextPage = !state.runsIsDone;
        if (elements.runsPagination) {
            elements.runsPagination.hidden = !hasPreviousPage && !hasNextPage;
        }
        if (elements.runsPrev) elements.runsPrev.disabled = state.runsPage <= 1;
        if (elements.runsNext) elements.runsNext.disabled = state.runsIsDone;
        if (elements.runsPageInfo) {
            elements.runsPageInfo.textContent = uiText('common.page', `Page ${state.runsPage}`, {
                page: state.runsPage,
            });
        }
    }

    function paginateList(items, pageSize, page) {
        if (Pagination?.paginate) {
            return Pagination.paginate(items, pageSize, page);
        }
        const list = Array.isArray(items) ? items : [];
        const size = Number(pageSize) > 0 ? Number(pageSize) : 1;
        const totalItems = list.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / size));
        const currentPage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
        const startIndex = (currentPage - 1) * size;
        const endIndex = startIndex + size;
        return {
            totalItems,
            totalPages,
            page: currentPage,
            pageSize: size,
            startIndex,
            endIndex: Math.min(endIndex, totalItems),
            pageItems: list.slice(startIndex, endIndex),
        };
    }

    function ensureSpellsPageForRun(runId) {
        if (!runId) return;
        if (state.spellPageRunId !== runId) {
            state.spellPageRunId = runId;
            state.spellsPage = 1;
        }
    }

    /** Where the wait starts: one line after the status offering a Discord ping (ui/discord-pings.js). */
    function offerDiscordPing() {
        const offer = DiscordPings?.offerFor?.('moonDream');
        if (offer) elements.status?.append(offer);
    }

    function setStatus(message, tone = 'info', generationNotice = null) {
        state.generationNotice = generationNotice;
        if (!elements.status) return;
        elements.status.textContent = message || '';
        elements.status.classList.remove('error', 'success');
        if (tone === 'error') elements.status.classList.add('error');
        if (tone === 'success') elements.status.classList.add('success');
    }

    function applySelectionFeedback(commitBar, statusElement, feedback) {
        if (!commitBar || !statusElement) return;
        const message = typeof feedback?.message === 'string' ? feedback.message.trim() : '';
        const tone = feedback?.tone || 'info';
        commitBar.setAttribute('aria-busy', String(feedback?.busy === true));
        statusElement.hidden = !message;
        statusElement.textContent = message;
        statusElement.classList.toggle('error', tone === 'error');
        statusElement.classList.toggle('success', tone === 'success');
    }

    function setSelectionFeedback(runId, feedback) {
        if (!runId) return;
        const message = typeof feedback?.message === 'string' ? feedback.message.trim() : '';
        const normalized = message
            ? {
                message,
                tone: feedback?.tone || 'info',
                busy: feedback?.busy === true,
            }
            : null;
        if (normalized) state.selectionFeedback.set(runId, normalized);
        else state.selectionFeedback.delete(runId);
        if (state.currentRunDetails?.id !== runId || !elements.detail) return;
        const commitBar = elements.detail.querySelector('.moon-dream-commit-bar');
        const statusElement = commitBar?.querySelector('.moon-dream-commit-status');
        if (commitBar && statusElement) {
            applySelectionFeedback(commitBar, statusElement, normalized);
        }
    }

    function isSelectionBusy(runId) {
        return state.selectionFeedback.get(runId)?.busy === true;
    }

    function pruneRunSelectionState(runId) {
        if (!runId) return;
        state.artSelections.delete(runId);
        state.selectionFeedback.delete(runId);
        if (state.deletionFeedback?.runId === runId) state.deletionFeedback = null;
    }

    function forgetCurrentRunDetails(runId) {
        if (state.currentRunDetails?.id === runId) {
            state.currentRunDetails = null;
        }
    }

    function setDetailEmpty(message, { loading = false } = {}) {
        if (!elements.detail) return;
        const shown = elements.detail.firstElementChild;
        if (
            !state.selectedRunId
            && elements.detail.childElementCount === 1
            && shown?.classList.contains('moon-dream-empty')
            && shown.classList.contains('moon-dream-detail-loading') === loading
            && shown.textContent === message
        ) {
            /* The panel already says exactly this. A re-read that lands on the
               same empty state (the resume a tab switch performs, a poll) keeps
               the node the player is reading instead of rebuilding it. */
            setCompletedPresentation(false);
            delete elements.detail.dataset.reveal;
            if (!loading) disarmDetailEntrance();
            elements.detail.setAttribute('aria-busy', loading ? 'true' : 'false');
            return;
        }
        setCompletedPresentation(false);
        // An empty panel is not the reveal, so drop any spent flag. The ARMING
        // survives only the `loading` panel that every real navigation passes
        // through on its way to the run; a terminal empty state — a failed
        // read, "select a summoner", signed out — is the end of that
        // navigation, and an arm left set there fires the entrance later on an
        // unrelated repaint.
        delete elements.detail.dataset.reveal;
        if (!loading) disarmDetailEntrance();
        cleanupUnsettledDetailMedia();
        elements.detail.innerHTML = '';
        state.detailLabels = [];
        state.detailRelabelable = false;
        elements.detail.setAttribute('aria-busy', loading ? 'true' : 'false');
        if (state.selectedRunId) {
            elements.detail.appendChild(buildDetailCommand());
        }
        const empty = document.createElement('div');
        empty.className = 'moon-dream-empty';
        empty.classList.toggle('moon-dream-detail-loading', loading);
        empty.textContent = message;
        elements.detail.appendChild(empty);
    }

    function canPreserveRenderedRunDetail(runId) {
        return Boolean(
            runId
            && state.currentRunDetails?.id === runId
            && elements.detail?.childElementCount
            && !elements.detail.querySelector(':scope > .moon-dream-empty')
        );
    }

    function showDetailRefreshFailure(runId) {
        if (!canPreserveRenderedRunDetail(runId)) return;
        const noticeHost = elements.detail.querySelector('.moon-dream-status-card')
            || elements.detail.querySelector(
                '.moon-dream-complete-lore > .moon-dream-detail-summary'
            );
        if (!noticeHost) return;
        let notice = noticeHost.querySelector('.moon-dream-detail-refresh-error');
        if (!notice) {
            notice = document.createElement('div');
            notice.className = 'moon-dream-detail-actions moon-dream-status-notice moon-dream-detail-refresh-error';
            notice.setAttribute('role', 'status');
            notice.setAttribute('aria-live', 'polite');
            notice.setAttribute('aria-atomic', 'true');
            noticeHost.appendChild(notice);
        }
        notice.textContent = uiText(
            'foundry.moonDream.loadDetailsFailed',
            'Failed to load summoner details.'
        );
    }

    function getStatusLabel(status, queueStatus) {
        if ((queueStatus === 'failed' || queueStatus === 'expired') && ![
            'ready', 'awaiting_art_selection', 'complete', 'summoner_failed', 'spells_failed',
        ].includes(status)) {
            return { label: uiText('status.failed', 'Failed'), className: 'failed' };
        }
        switch (status) {
            case 'pending':
            case 'queued':
            case 'generating':
            case 'art':
                return { label: uiText('status.loading', 'Loading'), className: 'loading' };
            case 'summoner_queued':
                return { label: uiText('foundry.moonDream.statusSummonerQueued', 'Summoner queued'), className: 'loading' };
            case 'summoner_designing':
                return { label: uiText('foundry.moonDream.statusSummonerDesigning', 'Designing summoner'), className: 'loading' };
            case 'summoner_art_generating':
                return { label: uiText('foundry.moonDream.statusSummonerArt', 'Creating portraits'), className: 'loading' };
            case 'awaiting_art_selection':
                return { label: uiText('foundry.moonDream.statusAwaitingSelection', 'Choose permanent art'), className: 'ready' };
            case 'spells_queued':
                return { label: uiText('foundry.moonDream.statusSpellsQueued', 'Spells queued'), className: 'loading' };
            case 'spells_designing':
                return { label: uiText('foundry.moonDream.statusSpellsDesigning', 'Designing spells'), className: 'loading' };
            case 'spells_art_generating':
                return { label: uiText('foundry.moonDream.statusSpellArt', 'Creating spell art'), className: 'loading' };
            case 'ready':
                return { label: uiText('status.ready', 'Ready'), className: 'ready' };
            case 'complete':
                return { label: uiText('status.completed', 'Completed'), className: 'complete' };
            case 'failed':
            case 'summoner_failed':
            case 'spells_failed':
                return { label: uiText('status.failed', 'Failed'), className: 'failed' };
            case 'deleting':
                return { label: uiText('foundry.moonDream.statusDeleting', 'Deleting'), className: 'loading' };
            default:
                return { label: uiText('status.loading', 'Loading'), className: 'loading' };
        }
    }

    function isRunStalled(run) {
        if (!run) return false;
        if (![
            'pending',
            'queued',
            'generating',
            'art',
            'summoner_queued',
            'summoner_designing',
            'summoner_art_generating',
            'spells_queued',
            'spells_designing',
            'spells_art_generating',
        ].includes(run.status)) return false;
        if (run.queueStatus === 'queued' || run.queueStatus === 'running') return false;
        const updatedAt = Number.isFinite(run.updatedAt) ? run.updatedAt : run.createdAt;
        if (!Number.isFinite(updatedAt)) return false;
        return (Date.now() - updatedAt) >= RUN_STALE_WARNING_MS;
    }

    function buildMoonDreamCardData(card, typeOverride) {
        if (!card) return null;
        const directImageUrl = sanitizeImageUrlForDirectClient(card.imageUrl);
        const type = typeOverride || card.type || 'Spell';
        const isStellar = card.isStellar === true
            || card.stellar === true
            || String(card.variant || '').toLowerCase() === 'stellar';
        return {
            id: card.cardId || card.id,
            instanceId: card.cardId || card.id,
            name: card.name,
            type,
            manaCost: Number.isFinite(card.manaCost) ? card.manaCost : 0,
            picture: directImageUrl || '',
            imageUrl: directImageUrl,
            description: card.description || '',
            targetingType: card.targetingType || 'None',
            character: card.character || null,
            isStellar,
            stellar: isStellar,
            variant: isStellar ? 'stellar' : 'normal',
            stellarVideoUrl: card.stellarVideoUrl || '',
            stellarSummonVideoUrl: card.stellarSummonVideoUrl || '',
            stellarCastVideoUrl: card.stellarCastVideoUrl || '',
        };
    }

    function sanitizeImageUrlForDirectClient(raw) {
        if (!raw || typeof raw !== 'string') return null;
        const value = raw.trim();
        let parsed;
        try {
            parsed = new URL(value, window.location?.href || 'http://localhost/');
        } catch (_) {
            parsed = null;
        }
        let decodedPathname = parsed?.pathname || '';
        try {
            for (let count = 0; count < 4; count += 1) {
                const decoded = decodeURIComponent(decodedPathname);
                if (decoded === decodedPathname) break;
                decodedPathname = decoded;
            }
        } catch (_) {
            decodedPathname = '';
        }
        if (
            !value
            || decodedPathname === '/assets/art_placeholder.webp'
            || value.startsWith('/api/card-image/')
        ) return null;
        if (
            value.startsWith('http') ||
            value.startsWith('data:') ||
            value.startsWith('blob:') ||
            value.startsWith('/') ||
            value.startsWith('/assets/') ||
            value.startsWith('assets/')
        ) {
            return value;
        }
        return null;
    }

    function isDecodedMediaImage(img) {
        return Boolean(img?.complete && img.naturalWidth > 0);
    }

    /**
     * `onSettled` runs once, after this owner has settled the picture and let
     * go of the element — the point where a caller may rewrite its source.
     */
    function attachDirectMediaImage(img, value, identity = null, { onSettled = null } = {}) {
        if (!img) return;
        const directUrl = sanitizeImageUrlForDirectClient(value);
        const identityId = typeof identity?.id === 'string' ? identity.id.trim() : '';
        // Poll-driven re-renders re-attach constantly; a settled image keeps
        // its pixels instead of flickering back through the loading well
        // (docs/asset-loading.md law 2).
        if (
            directUrl
            && img.dataset.moonDreamMediaSource === directUrl
            && (
                img.getAttribute('src') === directUrl
                || typeof img.__moonDreamMediaCleanup === 'function'
            )
        ) return;
        if (
            !directUrl
            && identityId
            && img.dataset.moonDreamMediaIdentity === identityId
            && typeof img.__moonDreamMediaCleanup === 'function'
        ) return;
        if (typeof img.__moonDreamMediaCleanup === 'function') {
            img.__moonDreamMediaCleanup({ keepSource: false });
        }

        img.classList.remove(
            'placeholder-art',
            'moon-dream-media-retrying',
            'moon-dream-media-unavailable',
        );
        if (!directUrl) {
            delete img.dataset.moonDreamMediaSource;
            img.removeAttribute('src');
            if (identityId) {
                attachIdentityMediaImage(img, identityId, identity?.picture);
                return;
            }
            delete img.dataset.moonDreamMediaIdentity;
            img.classList.remove('moon-dream-media-loading', 'moon-dream-media-retrying');
            img.classList.add('moon-dream-media-unavailable');
            return;
        }

        delete img.dataset.moonDreamMediaIdentity;
        /* ONE REQUEST PER PICTURE (cards.js useSharedArtRequestMode). The
           library disc bakes a summoner's face from a CORS load of its URL and
           the deck's cards load theirs with CORS, and the art hosts answer a
           CORS request with `Vary: Origin`: a picture of the same URL loaded
           here without CORS missed that cached copy and downloaded it again
           (round 14 review, WebKit, iPhone 13 landscape, EU phone 4G: every
           reopen of the Foundry on Moon Dream re-downloaded the library face,
           0.85-1.23 s, and the first view waited for it; Chrome 1400x640: the
           opened summoner's portrait once the face had joined). Set before
           any source; every Moon Dream picture is on the art hosts. */
        if (img.crossOrigin == null) img.crossOrigin = 'anonymous';
        img.dataset.moonDreamMediaSource = directUrl;
        img.dataset.moonDreamMediaSince = String(Date.now());
        delete img.dataset.moonDreamShown;
        img.classList.add('moon-dream-media-loading');
        let attemptTimer = null;
        let retryTimer = null;
        let retryAttempt = 0;
        let wakeListenersBound = false;
        let disposed = false;
        let everConnected = img.isConnected;
        const createdAt = Date.now();

        const clearAttemptTimer = () => {
            if (attemptTimer !== null) clearTimeout(attemptTimer);
            attemptTimer = null;
        };
        const removeCandidateListeners = () => {
            img.removeEventListener('load', settle);
            img.removeEventListener('error', handleError);
        };
        const unbindWakeListeners = () => {
            if (!wakeListenersBound) return;
            wakeListenersBound = false;
            window.removeEventListener('online', wakeRetry);
            window.removeEventListener('pageshow', wakeRetry);
            document.removeEventListener('visibilitychange', wakeRetry);
        };
        const clearRetryTimer = () => {
            if (retryTimer !== null) clearTimeout(retryTimer);
            retryTimer = null;
            unbindWakeListeners();
        };
        const cleanup = ({ keepSource = true } = {}) => {
            if (disposed) return;
            disposed = true;
            clearAttemptTimer();
            clearRetryTimer();
            removeCandidateListeners();
            if (!keepSource) {
                delete img.dataset.moonDreamMediaSource;
                img.removeAttribute('src');
            }
            if (img.__moonDreamMediaCleanup === cleanup) {
                delete img.__moonDreamMediaCleanup;
            }
        };
        const canContinue = () => {
            if (disposed || img.dataset.moonDreamMediaSource !== directUrl) return false;
            if (img.isConnected) {
                everConnected = true;
                return true;
            }
            if (everConnected || Date.now() - createdAt >= DIRECT_MEDIA_ATTACHMENT_GRACE_MS) {
                cleanup({ keepSource: false });
                return false;
            }
            return true;
        };
        function settle() {
            if (disposed || img.getAttribute('src') !== directUrl) return;
            img.classList.remove(
                'moon-dream-media-loading',
                'moon-dream-media-retrying',
                'moon-dream-media-unavailable',
            );
            cleanup();
            onSettled?.();
        }
        const scheduleRetry = () => {
            if (!canContinue()) return;
            clearAttemptTimer();
            clearRetryTimer();
            removeCandidateListeners();
            img.removeAttribute('src');
            img.classList.add('moon-dream-media-loading', 'moon-dream-media-retrying');
            img.classList.remove('moon-dream-media-unavailable', 'placeholder-art');
            const delay = DIRECT_MEDIA_RETRY_DELAYS_MS[
                Math.min(retryAttempt, DIRECT_MEDIA_RETRY_DELAYS_MS.length - 1)
            ];
            retryAttempt += 1;
            retryTimer = setTimeout(trySource, delay);
            if (!wakeListenersBound) {
                wakeListenersBound = true;
                window.addEventListener('online', wakeRetry, { passive: true });
                window.addEventListener('pageshow', wakeRetry, { passive: true });
                document.addEventListener('visibilitychange', wakeRetry, { passive: true });
            }
        };
        function handleError() {
            if (disposed || img.getAttribute('src') !== directUrl) return;
            scheduleRetry();
        }
        function trySource() {
            if (!canContinue()) return;
            clearAttemptTimer();
            clearRetryTimer();
            removeCandidateListeners();
            img.classList.add('moon-dream-media-loading');
            img.classList.remove('moon-dream-media-unavailable');
            img.addEventListener('load', settle);
            img.addEventListener('error', handleError);
            img.setAttribute('src', directUrl);
            attemptTimer = setTimeout(handleError, DIRECT_MEDIA_LOAD_TIMEOUT_MS);
            // A cache hit can leave the image already decoded here, in which
            // case no load event is coming and the gate would never lift.
            if (isDecodedMediaImage(img)) settle();
        }
        function wakeRetry() {
            if (retryTimer === null || document.visibilityState === 'hidden') return;
            trySource();
        }

        img.__moonDreamMediaCleanup = cleanup;
        trySource();
    }

    function attachIdentityMediaImage(img, identityId, picture) {
        img.dataset.moonDreamMediaIdentity = identityId;
        img.classList.add('moon-dream-media-loading', 'moon-dream-media-retrying');
        img.classList.remove('moon-dream-media-unavailable', 'placeholder-art');
        let retryTimer = null;
        let retryAttempt = 0;
        let wakeListenersBound = false;
        let disposed = false;
        let everConnected = img.isConnected;
        const createdAt = Date.now();

        const unbindWakeListeners = () => {
            if (!wakeListenersBound) return;
            wakeListenersBound = false;
            window.removeEventListener('online', wakeRetry);
            window.removeEventListener('pageshow', wakeRetry);
            document.removeEventListener('visibilitychange', wakeRetry);
        };
        const clearRetry = () => {
            if (retryTimer !== null) clearTimeout(retryTimer);
            retryTimer = null;
            unbindWakeListeners();
        };
        const cleanup = ({ keepSource = true } = {}) => {
            if (disposed) return;
            // A decoded node is carried whole across poll-driven rebuilds. Its
            // shared Cards owner moves with it and remains the exact identity
            // authority until the reused node is eventually removed.
            if (keepSource && isDecodedMediaImage(img)) return;
            disposed = true;
            clearRetry();
            Cards?.disposeCardArtImage?.(img);
            if (!keepSource) {
                delete img.dataset.moonDreamMediaIdentity;
                img.removeAttribute('src');
            }
            if (img.__moonDreamMediaCleanup === cleanup) {
                delete img.__moonDreamMediaCleanup;
            }
        };
        const canContinue = () => {
            if (disposed || img.dataset.moonDreamMediaIdentity !== identityId) return false;
            if (img.isConnected) {
                everConnected = true;
                return true;
            }
            if (everConnected || Date.now() - createdAt >= DIRECT_MEDIA_ATTACHMENT_GRACE_MS) {
                cleanup({ keepSource: false });
                return false;
            }
            return true;
        };
        const scheduleRetry = () => {
            if (!canContinue()) return;
            clearRetry();
            Cards?.disposeCardArtImage?.(img);
            img.classList.add('moon-dream-media-loading', 'moon-dream-media-retrying');
            img.classList.remove('moon-dream-media-unavailable', 'placeholder-art');
            const delay = DIRECT_MEDIA_RETRY_DELAYS_MS[
                Math.min(retryAttempt, DIRECT_MEDIA_RETRY_DELAYS_MS.length - 1)
            ];
            retryAttempt += 1;
            retryTimer = setTimeout(tryIdentity, delay);
            if (!wakeListenersBound) {
                wakeListenersBound = true;
                window.addEventListener('online', wakeRetry, { passive: true });
                window.addEventListener('pageshow', wakeRetry, { passive: true });
                document.addEventListener('visibilitychange', wakeRetry, { passive: true });
            }
        };
        function tryIdentity() {
            if (!canContinue()) return;
            clearRetry();
            const resolveCardArt = Cards?.resolveCardArt;
            if (typeof resolveCardArt !== 'function') {
                scheduleRetry();
                return;
            }
            let readiness;
            try {
                readiness = resolveCardArt(
                    { id: identityId, imageUrl: undefined, picture },
                    img,
                    { directArtOnly: true, rasterReserveScale: 1, fit: 'contain' },
                );
            } catch (_) {
                scheduleRetry();
                return;
            }
            void Promise.resolve(readiness).then((ready) => {
                if (!canContinue()) return;
                if (ready !== true) {
                    scheduleRetry();
                    return;
                }
                retryAttempt = 0;
                img.classList.remove(
                    'moon-dream-media-loading',
                    'moon-dream-media-retrying',
                    'moon-dream-media-unavailable',
                );
            }, scheduleRetry);
        }
        function wakeRetry() {
            if (retryTimer === null || document.visibilityState === 'hidden') return;
            tryIdentity();
        }

        img.__moonDreamMediaCleanup = cleanup;
        tryIdentity();
    }

    // Only this module's own media: spell cards are carried whole by
    // state.previousSpellCards and their rasters belong to Cards.
    const CARRIED_MEDIA_SELECTOR = [
        '.moon-dream-art-preview img',
        '.moon-dream-art-visual img',
        '.moon-dream-portrait-frame img',
        '.moon-dream-card-art img',
    ].join(', ');

    /**
     * Active generation rebuilds this whole panel every poll tick. A fresh
     * <img> has no pixels until it decodes, so recreating settled portraits
     * made each tick blank them for a frame — a 2s flicker for as long as the
     * run took. Harvest the decoded images before the wipe, keyed by the src
     * they already carry, so the rebuild can adopt the very same nodes.
     */
    function harvestSettledDetailMedia() {
        const settled = new Map();
        if (!elements.detail) return settled;
        elements.detail.querySelectorAll(CARRIED_MEDIA_SELECTOR).forEach((img) => {
            if (!isDecodedMediaImage(img)) {
                /* A picture still on its way is carried too, with its request
                   and its retry ladder: a new <img> started its loading well
                   invisible for the art grace again, so the shimmering well
                   the player could see vanished on every poll and the retry
                   ladder restarted at attempt 0. One that has been loading
                   past the grace has been SEEN, and keeps showing its well. */
                const source = img.dataset.moonDreamMediaSource;
                if (!source || typeof img.__moonDreamMediaCleanup !== 'function') return;
                const since = Number(img.dataset.moonDreamMediaSince) || Date.now();
                if (Date.now() - since >= DIRECT_MEDIA_GRACE_MS) img.dataset.moonDreamShown = 'true';
                img.classList.remove('moon-dream-media-cohort-hold');
                const pool = settled.get(source);
                if (pool) pool.push(img);
                else settled.set(source, [img]);
                return;
            }
            const src = img.getAttribute('src');
            const identityKey = img.dataset.moonDreamMediaIdentity
                ? `identity:${img.dataset.moonDreamMediaIdentity}`
                : '';
            const carryKey = identityKey || src;
            if (!carryKey) return;
            if (typeof img.__moonDreamMediaCleanup === 'function') img.__moonDreamMediaCleanup();
            // Decoded means painted: drop both gates, including the case where
            // `complete` flipped before the load event was dispatched.
            img.classList.remove('moon-dream-media-loading', 'moon-dream-media-cohort-hold');
            const pool = settled.get(carryKey);
            if (pool) pool.push(img);
            else settled.set(carryKey, [img]);
        });
        return settled;
    }

    function cleanupUnsettledDetailMedia() {
        if (!elements.detail) return;
        const carried = new Set();
        state.carriedDetailMedia?.forEach?.((pool) => pool.forEach((img) => carried.add(img)));
        elements.detail.querySelectorAll(CARRIED_MEDIA_SELECTOR).forEach((img) => {
            if (carried.has(img)) return;
            if (typeof img.__moonDreamMediaCleanup === 'function') {
                img.__moonDreamMediaCleanup({ keepSource: false });
            }
        });
    }

    function cleanupUnusedCarriedDetailMedia() {
        state.carriedDetailMedia?.forEach?.((pool) => {
            pool.forEach((img) => img.__moonDreamMediaCleanup?.({ keepSource: false }));
        });
    }

    /** An <img> for `value`, reusing the settled node for that url if we kept one. */
    function createDetailMediaImage(value, alt, identity = null) {
        const targetUrl = sanitizeImageUrlForDirectClient(value);
        const identityId = typeof identity?.id === 'string' ? identity.id.trim() : '';
        const carryKey = targetUrl || (identityId ? `identity:${identityId}` : '');
        const pool = carryKey ? state.carriedDetailMedia?.get(carryKey) : null;
        const img = pool?.length ? pool.shift() : document.createElement('img');
        img.alt = alt;
        attachDirectMediaImage(img, value, identity);
        return img;
    }

    // Group form of the grace rule for direct media: still-loading images in a
    // freshly built group are withheld until every member loads (or enters its
    // neutral retry well), or the bounded cap releases them — so a set that
    // arrives at once (opening a finished run's six portraits) appears at once
    // rather than in a ragged cascade. Already-decoded images are never held,
    // which is what lets a single newly finished portrait join a live
    // generation immediately instead of re-hiding the ones already on screen.
    function holdMediaCohortUntilLoaded(images, capMs = MEDIA_COHORT_REVEAL_CAP_MS) {
        // A well the player has already seen is never hidden again.
        const held = [...(images || [])].filter((img) => (
            img && !isDecodedMediaImage(img) && img.dataset.moonDreamShown !== 'true'
        ));
        if (held.length < 2) return;
        held.forEach((img) => img.classList.add('moon-dream-media-cohort-hold'));
        const release = () => held.forEach((img) => (
            img.classList.remove('moon-dream-media-cohort-hold')
        ));
        const capTimer = setTimeout(release, capMs);
        let remaining = held.length;
        held.forEach((img) => {
            const settle = () => {
                img.removeEventListener('load', settle);
                img.removeEventListener('error', settle);
                remaining -= 1;
                if (remaining === 0) {
                    clearTimeout(capTimer);
                    release();
                }
            };
            img.addEventListener('load', settle);
            img.addEventListener('error', settle);
        });
    }

    function validatePrompt(input) {
        const normalized = String(input || '').normalize('NFC').replace(/ +/g, ' ').trim();
        if (!normalized) {
            return { ok: false, message: uiText('foundry.moonDream.promptRequired', 'Enter a prompt to begin.') };
        }
        if (normalized.length > PROMPT_MAX_LENGTH) {
            return {
                ok: false,
                message: uiText('foundry.moonDream.promptTooLong', `Prompt must be at most ${PROMPT_MAX_LENGTH} characters.`, {
                    max: PROMPT_MAX_LENGTH,
                }),
            };
        }
        if (PROMPT_INVISIBLE_OR_FORMAT.test(normalized) || !PROMPT_REGEX.test(normalized)) {
            return {
                ok: false,
                message: uiText(
                    'foundry.moonDream.promptLettersOnly',
                    'Use letters, numbers and simple punctuation only.',
                ),
            };
        }
        return { ok: true, normalized };
    }

    async function fetchWithAuth(url, options = {}) {
        const requestUser = state.user;
        if (!requestUser?.getIdToken) {
            throw new Error('Authentication required');
        }
        const { timeoutMs, ...requestOptions } = options;
        if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
            const token = await requestUser.getIdToken();
            const headers = Object.assign({}, requestOptions.headers || {}, {
                Authorization: `Bearer ${token}`,
            });
            return fetch(url, { ...requestOptions, headers });
        }
        const controller = new AbortController();
        state.readControllers.add(controller);
        const callerSignal = requestOptions.signal;
        let rejectCancellation;
        const cancellation = new Promise((_, reject) => {
            rejectCancellation = reject;
        });
        const cancel = (name, message) => {
            controller.abort();
            const error = new Error(message);
            error.name = name;
            rejectCancellation(error);
        };
        const handleCallerAbort = () => cancel('AbortError', 'Moon Dream request was canceled');
        if (callerSignal?.aborted) {
            handleCallerAbort();
        } else {
            callerSignal?.addEventListener('abort', handleCallerAbort, { once: true });
        }
        const timeout = setTimeout(() => {
            cancel('TimeoutError', 'Moon Dream request timed out');
        }, timeoutMs);
        const request = (async () => {
            const token = await requestUser.getIdToken();
            const headers = Object.assign({}, requestOptions.headers || {}, {
                Authorization: `Bearer ${token}`,
            });
            return fetch(url, { ...requestOptions, headers, signal: controller.signal });
        })();
        try {
            return await Promise.race([request, cancellation]);
        } finally {
            clearTimeout(timeout);
            callerSignal?.removeEventListener('abort', handleCallerAbort);
            state.readControllers.delete(controller);
        }
    }

    function currentLanguage() {
        return i18n?.getLanguage?.() || 'en';
    }

    function authoritativeRunLanguage(run) {
        const language = typeof run?.selectedLanguage === 'string'
            ? run.selectedLanguage.trim()
            : '';
        return SUPPORTED_RUN_LANGUAGES.has(language) ? language : null;
    }

    async function loadStardust() {
        if (!state.user || !elements.stardust) return;
        const requestUser = state.user;
        const mutationVersion = state.balanceMutationVersion;
        // The detail's admission state (price known, balance covers it) is the
        // only thing this read can change on screen; repaint only when it did.
        const admissionBefore = JSON.stringify([state.stardustBalance, state.pricing]);
        try {
            const res = await fetchWithAuth('/api/moon-dream/user', {
                cache: 'no-store',
                timeoutMs: READ_REQUEST_TIMEOUT_MS,
            });
            if (!res.ok) return;
            const data = await res.json();
            if (state.user !== requestUser) return;
            const value = Number(data?.stardust);
            setAuthoritativePricing(data);
            if (state.balanceMutationVersion === mutationVersion) {
                state.stardustBalance = Number.isSafeInteger(value) && value >= 0 ? value : null;
                renderStardustBalance(state.stardustBalance);
                updateGenerateAvailability();
                // The lobby chip under the Foundry never re-reads on its own.
                announceStardust(state.stardustBalance);
            }
            const admissionChanged = JSON.stringify([state.stardustBalance, state.pricing]) !== admissionBefore;
            if (admissionChanged && state.currentRunDetails?.id === state.selectedRunId
                && !canPreserveSpellInspection(state.currentRunDetails)) {
                renderRunDetail(state.currentRunDetails);
            }
        } catch (_) {
            console.warn('[MoonDream]', { code: 'stardust_load_failed' });
        }
    }

    function applyMutationBalance(data) {
        const value = Number(data?.stardustRemaining);
        if (Number.isSafeInteger(value) && value >= 0) {
            state.balanceMutationVersion += 1;
            state.stardustBalance = value;
            renderStardustBalance(value);
            updateGenerateAvailability();
            // A charged Stage 1/Stage 2 is the figure the lobby chip must show.
            announceStardust(value);
        }
    }

    async function loadRuns() {
        return loadRunsPage(state.runsPage, { forceReload: state.runsPage === 1 });
    }

    function resetRunsPagination() {
        state.runsPage = 1;
        state.runsNextCursor = null;
        state.runsIsDone = true;
        state.runsPageCursors = new Map([[1, null]]);
    }

    /** Read one library page and show it. Returns 'read' once a page is shown. */
    async function loadRunsPage(targetPage = 1, { forceReload = false } = {}) {
        if (!state.user || state.loadingRuns) return undefined;
        const requestUser = state.user;
        const pageSize = runsPageSize();
        const deletionsAtStart = state.deletionsAccepted;
        let fallbackPage = null;
        let readAgain = false;
        state.loadingRuns = true;
        try {
            const normalizedPage = Math.max(1, Number(targetPage) || 1);
            /* A RE-READ IS NOT A LOAD. The resume a tab switch performs, a poll
               tick and a language change all re-read the page the list already
               shows, and each one marked the list busy for its whole round
               trip — a loading state over rows that stay exactly where they
               are, on every return to the tab (26 September 2026). Only a read
               for rows the list does not show yet is a wait. */
            const rereadingShownPage = normalizedPage === state.runsPage
                && state.runsPageSize === pageSize
                && elements.runs?.dataset.loadState === 'ready';
            /* A reset is committed WITH the answer, not before the read: the
               pager reset first (page 1, "no next page") hid itself for the
               whole read whenever anything repainted it meanwhile. */
            const resetting = forceReload || state.runsPageSize !== pageSize;
            if (resetting) state.runsPageSize = pageSize;
            const cursors = resetting ? new Map([[1, null]]) : state.runsPageCursors;
            const cursor = cursors.has(normalizedPage)
                ? cursors.get(normalizedPage)
                : undefined;
            if (normalizedPage > 1 && cursor === undefined) {
                if (resetting) resetRunsPagination();
                return undefined;
            }
            if (!rereadingShownPage) setRunsLoadState('loading');

            const params = new URLSearchParams({
                limit: String(pageSize),
                lang: currentLanguage(),
            });
            if (cursor) params.set('cursor', cursor);
            const res = await fetchWithAuth(`/api/moon-dream/runs-page?${params.toString()}`, {
                cache: 'no-store',
                timeoutMs: READ_REQUEST_TIMEOUT_MS,
            });
            if (state.user !== requestUser || pageSize !== runsPageSize()) return undefined;
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                setRunsLoadState('error');
                setStatus(payload?.error || uiText('foundry.moonDream.loadSummonersFailed', 'Failed to load summoners.'), 'error');
                return undefined;
            }
            const data = await res.json();
            if (state.user !== requestUser || pageSize !== runsPageSize()) return undefined;
            /* A deletion accepted while this read was out: the answer still
               lists that summoner and would paint it back into the library. */
            if (deletionsAtStart !== state.deletionsAccepted) {
                readAgain = true;
                return undefined;
            }
            if (resetting) state.runsPageCursors = cursors;
            state.runsReadAt = Date.now();
            state.runs = Array.isArray(data?.items) ? data.items : [];
            noteRunCompletions(state.runs);
            reschedulePollingForCurrentState();
            state.runsPage = normalizedPage;
            state.runsNextCursor = typeof data?.nextCursor === 'string' ? data.nextCursor : null;
            state.runsIsDone = Boolean(data?.isDone);
            if (state.runsNextCursor) {
                state.runsPageCursors.set(normalizedPage + 1, state.runsNextCursor);
            } else {
                state.runsPageCursors.delete(normalizedPage + 1);
            }
            if (state.runs.length === 0 && normalizedPage > 1) {
                fallbackPage = normalizedPage - 1;
                return undefined;
            }
            if (normalizedPage === 1) state.latestRunId = state.runs[0]?.id || null;
            /* THE OPENING CONFIRMS ITS SUMMONER (restoreViewState). The newest
               the device view named is the one page 1 still leads with, or the
               newest is selected below; a summoner the player picked meanwhile
               ended the opening (selectRun). */
            if (state.openingLatest && normalizedPage === 1) {
                state.openingLatest = false;
                if (state.selectedRunId && state.selectedRunId !== state.latestRunId) state.selectedRunId = null;
            }
            renderRuns();
            setRunsLoadState('ready');
            if (!state.selectedRunId && state.runs.length > 0) {
                if (usesMobileMasterDetail()) {
                    setMobileDreamView('history');
                    setDetailEmpty(uiText('foundry.moonDream.selectSummoner', 'Select a summoner to view details.'));
                } else {
                    await selectRun(state.runs[0].id);
                }
            } else if (!state.selectedRunId) {
                setDetailEmpty(uiText('foundry.moonDream.selectSummoner', 'Select a summoner to view details.'));
            }
            return 'read';
        } catch (_) {
            console.warn('[MoonDream]', { code: 'runs_load_failed' });
            if (state.user === requestUser && pageSize === runsPageSize()) {
                setRunsLoadState('error');
            }
            return undefined;
        } finally {
            state.loadingRuns = false;
            if (readAgain && state.user === requestUser) {
                return await loadRunsPage(targetPage, { forceReload });
            }
            if (
                fallbackPage !== null
                && state.user === requestUser
                && pageSize === runsPageSize()
            ) {
                return await loadRunsPage(fallbackPage);
            }
            const nextPageSize = runsPageSize();
            if (
                state.user === requestUser
                && state.surfaceActive
                && nextPageSize !== pageSize
            ) {
                state.runsPageSize = nextPageSize;
                resetRunsPagination();
                void loadRunsPage(1, { forceReload: true });
            }
        }
    }

    /* THE LIBRARY ROW'S PORTRAIT DISC — the lobby avatar's face, per summoner.
       The server names the picture (`portraitUrl`: the chosen portrait, or the
       first finished candidate while the choice is open). Without one, a run
       still drawing its portraits shows the moving loading well and anything
       else shows the lobby's moon. */
    const PORTRAIT_PENDING_STATUSES = new Set([
        'pending',
        'queued',
        'generating',
        'art',
        'summoner_queued',
        'summoner_designing',
        'summoner_art_generating',
    ]);
    // The face is baked for the disc's circle: the head, not the shoulders.
    const RUN_PORTRAIT_PROFILE = Object.freeze({ fit: 'cover', positionX: 0.5, positionY: 0.18 });
    // The disc's face box (116% of a 36px disc) when the row is not laid out.
    const RUN_PORTRAIT_FALLBACK_SIZE = 42;
    // Each face's readiness: resolves once its baked raster can paint.
    const runPortraitReadiness = new WeakMap();

    function runPortraitState(run) {
        const url = sanitizeImageUrlForDirectClient(run?.portraitUrl);
        if (url) return { state: 'image', url };
        const failed = getStatusLabel(run?.status, run?.queueStatus).className === 'failed';
        return { state: !failed && PORTRAIT_PENDING_STATUSES.has(run?.status) ? 'generating' : 'none' };
    }

    function disposeRunPortraitImage(img) {
        img.__moonDreamMediaCleanup?.({ keepSource: false });
        img.remove();
    }

    /**
     * The lobby's own cure for a portrait aliasing at avatar size
     * (avatar-picker.js): the decoded picture is handed to the game's raster
     * sampler, which writes a raster baked for this disc back into the SAME
     * <img>. (The card-art owner was the first choice and is wrong here: on
     * some engines it swaps its <img> for a <canvas>, orphaning every reference
     * this list keeps.) The face stays invisible until the baked raster has
     * decoded, so a player never sees it sharpen.
     */
    function sharpenRunPortrait(img, url) {
        const sampler = window.MoonfallRasterSampler;
        if (typeof sampler?.enhanceImage !== 'function') return Promise.resolve();
        const box = img.getBoundingClientRect();
        const size = Math.round(Math.max(box.width, box.height)) || RUN_PORTRAIT_FALLBACK_SIZE;
        let task;
        try {
            task = sampler.enhanceImage(img, url, {
                ...RUN_PORTRAIT_PROFILE,
                displayWidth: size,
                displayHeight: size,
            });
        } catch (_) {
            return Promise.resolve();
        }
        return Promise.resolve(task)
            .then(() => (typeof img.decode === 'function' ? img.decode() : undefined))
            .catch(() => undefined);
    }

    /**
     * An <img> for `url` through Moon Dream's direct-media owner (capped
     * retries, the lifecycle wake, detach cleanup). Its readiness resolves once
     * the sampled face can paint — never false: a face that cannot load keeps
     * retrying behind the disc's well.
     *
     * The bake starts only once that owner has settled the picture. A picture
     * this page already decoded (paging back, the same face on two rows) is a
     * sampler cache hit whose raster is written back at once; written before
     * the owner had seen the load event, the owner took the event for a stale
     * one and kept the face hidden as loading for good.
     */
    function createRunPortraitImage(url) {
        const img = document.createElement('img');
        img.className = 'moon-dream-run-portrait-image is-sampling';
        img.alt = '';
        img.decoding = 'async';
        img.dataset.portraitSource = url;
        let presentFace = null;
        runPortraitReadiness.set(img, new Promise((resolve) => { presentFace = resolve; }));
        attachDirectMediaImage(img, url, null, {
            // A cache hit settles before the row has placed the face; a
            // microtask later it is laid out and can be measured.
            onSettled: () => {
                void Promise.resolve()
                    .then(() => sharpenRunPortrait(img, url))
                    .then(() => {
                        img.classList.remove('is-sampling');
                        presentFace(true);
                    });
            },
        });
        return img;
    }

    function setRunPortrait(row, run) {
        const disc = row.querySelector(':scope > .moon-dream-run-portrait');
        if (!disc) return null;
        const desired = runPortraitState(run);
        if (disc.dataset.portrait !== desired.state) disc.dataset.portrait = desired.state;
        const faces = [...disc.querySelectorAll(':scope > .moon-dream-run-portrait-image')];
        if (desired.state !== 'image') {
            faces.forEach(disposeRunPortraitImage);
            return null;
        }
        const current = faces.find((img) => !img.classList.contains('is-leaving'));
        if (current?.dataset.portraitSource === desired.url) return null;
        const img = createRunPortraitImage(desired.url);
        disc.appendChild(img);
        /* A face that is replaced (the first candidate giving way to the chosen
           portrait) stays up until its successor can paint, bounded by the
           cohort cap, so the disc never blinks through its loading well. */
        faces.filter((face) => face !== img).forEach((face) => {
            face.classList.add('is-leaving');
            const leave = () => { if (face.isConnected) disposeRunPortraitImage(face); };
            const cap = setTimeout(leave, MEDIA_COHORT_REVEAL_CAP_MS);
            void runPortraitReadiness.get(img).then(() => {
                clearTimeout(cap);
                leave();
            });
        });
        return img;
    }

    /**
     * The group form of the grace rule for a page of faces: every face a page
     * adds appears together once all of them can paint, or at the bounded cap
     * when one is slow — never in a ragged cascade down the list. Faces already
     * on screen are never held.
     */
    function revealRunPortraitsTogether(faces) {
        const held = faces.filter(Boolean);
        if (held.length < 2) return;
        held.forEach((img) => img.classList.add('is-held'));
        let released = false;
        const release = () => {
            if (released) return;
            released = true;
            clearTimeout(cap);
            held.forEach((img) => img.classList.remove('is-held'));
        };
        const cap = setTimeout(release, MEDIA_COHORT_REVEAL_CAP_MS);
        void Promise.all(held.map((img) => runPortraitReadiness.get(img))).then(release, release);
    }

    function createRunRow(runId) {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'moon-dream-run';
        row.dataset.runId = runId;

        const disc = document.createElement('span');
        disc.className = 'moon-dream-run-portrait';
        disc.setAttribute('aria-hidden', 'true');
        const moon = document.createElement('span');
        moon.className = 'moon-dream-run-portrait-moon';
        moon.textContent = '☾';
        disc.appendChild(moon);

        const main = document.createElement('div');
        main.className = 'moon-dream-run-copy';
        const title = document.createElement('div');
        title.className = 'moon-dream-run-title';
        main.appendChild(title);

        const statusEl = document.createElement('div');
        statusEl.className = 'moon-dream-run-status';

        row.append(disc, main, statusEl);
        row.addEventListener('click', (event) => selectRun(runId, {
            focusDetail: event.detail === 0,
        }));
        return row;
    }

    function setTextIfChanged(element, text) {
        if (element.textContent !== text) element.textContent = text;
    }

    let loreAffordanceFrame = 0;

    /**
     * One of the detail panel's own words (uiText, not the run's): written
     * now and remembered with `text`, so a language change can say it again
     * in place (relabelRunDetail). `attr` names an attribute instead of the
     * text.
     */
    function labelDetail(element, text, attr = null) {
        if (!element) return element;
        const value = text();
        if (attr) element.setAttribute(attr, value);
        else element.textContent = value;
        state.detailLabels.push({ element, attr, text });
        return element;
    }

    /**
     * THE PANEL'S OWN WORDS, SAID AGAIN IN PLACE (snappy round 15, 28
     * September 2026). A language change repainted a finished summoner's
     * whole panel when its re-read landed — portrait, deck and lore torn down
     * and rebuilt 1.1-1.35 s after the rest of the page had changed (25
     * pop-ins, the rebuilt cards' names re-fitted a frame later) — although
     * only the panel's own words differed. A panel whose words are all
     * remembered (a finished summoner's, with no notice of its own on it) is
     * re-described here in the task of the change; returns false, leaving the
     * re-read to repaint it as before, for any other.
     */
    function relabelRunDetail() {
        const run = state.currentRunDetails;
        if (!state.detailRelabelable || !run?.id || !canPreserveRenderedRunDetail(run.id)) return false;
        if (
            state.deletingSummoner
            || state.pendingDeletionRunId === run.id
            || state.deletionFeedback?.runId === run.id
            || elements.detail.querySelector('.moon-dream-detail-refresh-error, .moon-dream-spells-raster-failure')
        ) return false;
        state.detailLabels = state.detailLabels.filter(({ element }) => element.isConnected);
        state.detailLabels.forEach(({ element, attr, text }) => {
            const value = text();
            if (!attr) setTextIfChanged(element, value);
            else if (element.getAttribute(attr) !== value) element.setAttribute(attr, value);
        });
        /* Its "scroll for more" is read off the laid-out lore, so it waits for
           the frame: measured here it forced a layout in the middle of the
           language change's task, before the rest of the page had written. */
        if (!loreAffordanceFrame) {
            loreAffordanceFrame = requestAnimationFrame(() => {
                loreAffordanceFrame = 0;
                updateLoreScrollAffordances();
            });
        }
        state.detailLanguage = currentLanguage();
        return true;
    }

    /** Bring one row up to date in place; returns a face it started loading. */
    function updateRunRow(row, run) {
        const selected = run.id === state.selectedRunId;
        row.classList.toggle('active', selected);
        row.setAttribute('aria-pressed', selected ? 'true' : 'false');

        const main = row.querySelector(':scope > .moon-dream-run-copy');
        const name = run.summonerName || run.prompt || 'Moon Dream';
        setTextIfChanged(main.querySelector('.moon-dream-run-title'), name);
        const etaLine = buildQueueEtaLine(run);
        let meta = main.querySelector('.moon-dream-run-meta');
        if (etaLine) {
            if (!meta) {
                meta = document.createElement('div');
                meta.className = 'moon-dream-run-meta';
                main.appendChild(meta);
            }
            setTextIfChanged(meta, etaLine.replace(/\.$/, ''));
        } else {
            meta?.remove();
        }

        const status = getStatusLabel(run.status, run.queueStatus);
        const statusEl = row.querySelector(':scope > .moon-dream-run-status');
        const statusClass = `moon-dream-run-status ${status.className}`;
        if (statusEl.className !== statusClass) statusEl.className = statusClass;
        setTextIfChanged(statusEl, status.label);
        // The whole row in words, where a line was set small, cut or hidden.
        const tooltip = [name, status.label, etaLine].filter(Boolean).join(' · ');
        if (row.title !== tooltip) row.title = tooltip;

        return setRunPortrait(row, run);
    }

    function removeRunRow(row) {
        row.querySelectorAll('.moon-dream-run-portrait-image').forEach(disposeRunPortraitImage);
        row.remove();
    }

    /* RECONCILED, NOT REBUILT. Every poll tick (2s while a run works) calls
       this; wiping the list recreated every face and restarted every loading
       sweep, so the portraits would blink on each tick. Rows are keyed by run
       and kept, so a row that did not change is not touched, focus stays
       where it was, and only a page's NEW faces are revealed as a group
       (docs/asset-loading.md: "a poll-driven rebuild must carry settled
       media"). */
    function renderRuns() {
        if (!elements.runs) return;
        const runs = Array.isArray(state.runs) ? state.runs : [];
        const content = state.root?.querySelector?.('#moon-dream-panel-content');
        if (content) content.dataset.libraryEmpty = String(runs.length === 0);
        updateRunsPagination();

        const wanted = new Set(runs.map((run) => run.id));
        const rows = new Map();
        let empty = null;
        [...elements.runs.children].forEach((child) => {
            const runId = child.classList.contains('moon-dream-run') ? child.dataset.runId : null;
            if (runId && wanted.has(runId) && !rows.has(runId)) rows.set(runId, child);
            else if (child.classList.contains('moon-dream-run')) removeRunRow(child);
            // An empty library stays the same node across re-reads: rebuilding
            // it made the line vanish and reappear on every poll and resume.
            else if (runs.length === 0 && !empty && child.classList.contains('moon-dream-empty')) empty = child;
            else child.remove();
        });

        if (runs.length === 0) {
            if (!empty) {
                empty = document.createElement('div');
                empty.className = 'moon-dream-empty';
                elements.runs.appendChild(empty);
            }
            setTextIfChanged(empty, uiText('foundry.moonDream.noSummoners', 'No summoners yet. Generate a Moon Dream to begin.'));
            return;
        }

        const arrivingFaces = [];
        let cursor = elements.runs.firstElementChild;
        runs.forEach((run) => {
            let row = rows.get(run.id);
            const arriving = !row;
            if (arriving) row = createRunRow(run.id);
            const face = updateRunRow(row, run);
            if (arriving) arrivingFaces.push(face);
            if (row === cursor) cursor = cursor.nextElementSibling;
            else elements.runs.insertBefore(row, cursor);
        });
        fitRunLines();
        revealRunPortraitsTogether(arrivingFaces);
    }

    /* A LIBRARY LINE STAYS ONE LINE (moon-dream.css, THE LIBRARY IS A PAGE).
       Every row is one height, so a name or a status too long for its row is
       set smaller by the share it overflows, down to three quarters, and only
       past that ends in an ellipsis. Every line is set back and then measured
       in one pass, inside the task that wrote the text: no frame sees a line
       at a size it is about to leave. Phones too, since their rows stand two
       to a line (screen-fit round 1, 28 October 2026). */
    const RUN_LINE_FIT_FLOOR = 0.75;
    function fitRunLines() {
        const runs = elements.runs;
        if (!runs) return;
        const lines = [...runs.querySelectorAll('.moon-dream-run-title, .moon-dream-run-status')];
        lines.forEach((line) => line.style.removeProperty('--dream-run-fit'));
        const fits = lines.map((line) => {
            const { clientWidth, scrollWidth } = line;
            if (!clientWidth || scrollWidth <= clientWidth) return null;
            return Math.max(RUN_LINE_FIT_FLOOR, Math.floor((clientWidth / scrollWidth) * 100) / 100 - 0.01);
        });
        lines.forEach((line, index) => {
            if (fits[index] !== null) line.style.setProperty('--dream-run-fit', fits[index].toFixed(2));
        });
    }

    /* The lines are fitted to the rail's width, which a hidden tab does not
       have and a resize changes; the rows' own heights never change it, so
       this cannot feed itself. */
    let runsResizeObserver = null;
    function observeRunsRail() {
        runsResizeObserver?.disconnect();
        runsResizeObserver = null;
        if (typeof ResizeObserver !== 'function' || !elements.runs) return;
        let width = null;
        runsResizeObserver = new ResizeObserver(() => {
            const next = elements.runs?.clientWidth ?? 0;
            if (next === width) return;
            width = next;
            fitRunLines();
        });
        runsResizeObserver.observe(elements.runs);
    }

    async function selectRun(runId, { focusDetail = false } = {}) {
        if (!runId) return;
        state.openingLatest = false;
        const preserveExisting = canPreserveRenderedRunDetail(runId);
        state.selectedRunId = runId;
        setMobileDreamView('detail');
        renderRuns();
        if (!preserveExisting) {
            setDetailEmpty(uiText('status.loading', 'Loading'), { loading: true });
        }
        await loadRunDetails(runId, { preserveExisting });
        if (focusDetail && state.selectedRunId === runId && usesMobileMasterDetail()) {
            const backButton = document.getElementById('moon-dream-history-back');
            try {
                backButton?.focus({ preventScroll: true });
            } catch (_) {
                backButton?.focus();
            }
        }
    }

    function isCurrentDetailRequest(requestId, runId, user) {
        return state.detailRequest?.id === requestId
            && state.detailRequest?.runId === runId
            && state.selectedRunId === runId
            && state.user === user;
    }

    /** Whether a detail read of `runId` was started after `requestId` (a detailRequestGeneration). */
    function detailReadSince(requestId, runId) {
        const last = state.lastDetailRequest;
        return Boolean(last && last.id > requestId && last.runId === runId);
    }

    /**
     * Read one summoner's detail and paint it. `paintAfter`: a read on its way
     * beside this one (the tick's balance) whose answer the paint shows, so the
     * detail is painted once, after it, rather than painted and repainted.
     * `fresh`: never join a read already out — one that left before a change
     * the server accepted cannot answer for it.
     */
    function loadRunDetails(runId, { preserveExisting = false, paintAfter = null, fresh = false } = {}) {
        if (!state.user) return Promise.resolve();
        if (!fresh && state.detailRequest?.runId === runId && state.detailRequest?.user === state.user) {
            return state.detailRequest.promise;
        }
        const requestId = state.detailRequestGeneration + 1;
        state.detailRequestGeneration = requestId;
        state.lastDetailRequest = { id: requestId, runId };
        const requestUser = state.user;
        state.loadingDetail = true;
        // Whatever this read paints — the summoner or its failure — is painted after it.
        const paintAfterSettles = paintAfter
            ? Promise.resolve(paintAfter).then(() => undefined, () => undefined)
            : null;
        const request = (async () => {
            try {
                const params = new URLSearchParams({ lang: currentLanguage() });
                const res = await fetchWithAuth(`/api/moon-dream/run/${encodeURIComponent(runId)}?${params.toString()}`, {
                    cache: 'no-store',
                    timeoutMs: READ_REQUEST_TIMEOUT_MS,
                });
                await paintAfterSettles;
                if (!res.ok) {
                    if (res.status === 404) {
                        if (!isCurrentDetailRequest(requestId, runId, requestUser)) return null;
                        const wasPendingDeletion = state.pendingDeletionRunId === runId;
                        if (wasPendingDeletion) state.pendingDeletionRunId = null;
                        pruneRunSelectionState(runId);
                        forgetCurrentRunDetails(runId);
                        state.runs = state.runs.filter((listedRun) => listedRun.id !== runId);
                        if (state.selectedRunId === runId) state.selectedRunId = null;
                        renderRuns();
                        if (wasPendingDeletion) {
                            setStatus(uiText('foundry.moonDream.deleted', 'Summoner deleted.'), 'success');
                            // The asynchronous deletion has finished removing its spells.
                            announceHoldingsChanged();
                        }
                        if (state.runs.length > 0) {
                            await selectRun(state.runs[0].id);
                        } else {
                            setMobileDreamView('history');
                            setDetailEmpty(uiText('foundry.moonDream.selectSummoner', 'Select a summoner to view details.'));
                        }
                        return null;
                    }
                    if (isCurrentDetailRequest(requestId, runId, requestUser)) {
                        if (preserveExisting && canPreserveRenderedRunDetail(runId)) {
                            showDetailRefreshFailure(runId);
                        } else {
                            setDetailEmpty(uiText('foundry.moonDream.loadDetailsFailed', 'Failed to load summoner details.'));
                        }
                    }
                    return null;
                }
                const run = await res.json();
                if (isCurrentDetailRequest(requestId, runId, requestUser)) {
                    state.detailReadAt = Date.now();
                    if (preserveExisting && (canPreserveSpellInspection(run) || isUnchangedSelectionPoll(run))) {
                        // Keep the source and its touch latch connected while
                        // the player reads, and keep the portrait choice's
                        // controls in place while nothing about the run
                        // moved. Polling continues; the next poll after
                        // dismissal paints the latest arrivals. Changed cards
                        // and stage transitions still repaint immediately.
                        state.currentRunDetails = run;
                    } else if (!(preserveExisting && isUnchangedSettledRun(run))) {
                        renderRunDetail(run);
                    }
                }
                return run;
            } catch (_) {
                await paintAfterSettles;
                console.warn('[MoonDream]', { code: 'run_detail_load_failed' });
                if (isCurrentDetailRequest(requestId, runId, requestUser)) {
                    if (preserveExisting && canPreserveRenderedRunDetail(runId)) {
                        showDetailRefreshFailure(runId);
                    } else {
                        setDetailEmpty(uiText('foundry.moonDream.loadDetailsFailed', 'Failed to load summoner details.'));
                    }
                }
                return null;
            } finally {
                if (state.detailRequest?.id === requestId) {
                    state.detailRequest = null;
                    state.loadingDetail = false;
                }
            }
        })();
        state.detailRequest = { id: requestId, runId, user: requestUser, promise: request };
        return request;
    }

    async function readRunForSelectionReconciliation(
        runId,
        requestUser,
        { requireSelectionEvidence = false } = {}
    ) {
        try {
            const params = new URLSearchParams({ lang: currentLanguage() });
            const res = await fetchWithAuth(`/api/moon-dream/run/${encodeURIComponent(runId)}?${params.toString()}`, {
                cache: 'no-store',
                timeoutMs: READ_REQUEST_TIMEOUT_MS,
            });
            if (!sameAuthenticatedAccount(state.user, requestUser)) return { cancelled: true };
            if (res.status === 404) return { settled: true, notFound: true, run: null };
            if (!res.ok) return { settled: false, notFound: false, run: null };
            const run = await res.json();
            if (!run || run.id !== runId) return { settled: false, notFound: false, run: null };
            const selectionStillUnchanged = !run.selectedCandidateId
                && ['awaiting_art_selection', 'ready'].includes(run.status);
            return {
                settled: !requireSelectionEvidence || !selectionStillUnchanged,
                notFound: false,
                run,
            };
        } catch (_) {
            return sameAuthenticatedAccount(state.user, requestUser)
                ? { settled: false, notFound: false, run: null }
                : { cancelled: true };
        }
    }

    async function reconcileArtSelection(
        runId,
        requestUser,
        operationId,
        { requireSelectionEvidence = false } = {}
    ) {
        let attempts = 0;
        let lastAuthoritativeRun = null;
        while (
            sameAuthenticatedAccount(state.user, requestUser)
            && state.selectingArtRunId === runId
            && state.selectionMutationGeneration === operationId
        ) {
            attempts += 1;
            const outcome = await readRunForSelectionReconciliation(runId, requestUser, {
                requireSelectionEvidence,
            });
            if (outcome.run?.id === runId) lastAuthoritativeRun = outcome.run;
            if (outcome.cancelled || outcome.settled) return outcome;
            if (attempts >= SELECTION_RECONCILIATION_MAX_ATTEMPTS) {
                return {
                    settled: false,
                    notFound: false,
                    timedOut: true,
                    run: lastAuthoritativeRun,
                };
            }
            await new Promise((resolve) => setTimeout(resolve, ACTIVE_POLL_INTERVAL_MS));
        }
        return { cancelled: true };
    }

    function resolveRunProgress(run) {
        const completed = Number(run?.progress?.completed);
        const processed = Number(run?.progress?.processed);
        const total = Number(run?.progress?.total);
        if (
            typeof run?.progress?.stage === 'string'
            && Number.isFinite(completed)
            && Number.isFinite(total)
            && completed >= 0
            && total >= 0
        ) {
            return {
                stage: run.progress.stage,
                completed: Math.min(completed, total || completed),
                processed: Number.isFinite(processed)
                    ? Math.min(Math.max(processed, completed), total || processed)
                    : Math.min(completed, total || completed),
                total,
                active: Math.max(0, Number(run.progress.active) || 0),
                legacy: false,
            };
        }
        const legacyTotal = Number(run?.totalSpells);
        const legacyCompleted = Number.isFinite(Number(run?.processedSpells))
            ? Number(run.processedSpells)
            : Number(run?.successfulSpells);
        if (Number.isFinite(legacyTotal) && legacyTotal >= 0 && Number.isFinite(legacyCompleted)) {
            return {
                stage: ['pending', 'queued', 'generating'].includes(run.status) ? 'spell_design' : 'spell_art',
                completed: Math.min(Math.max(legacyCompleted, 0), legacyTotal),
                total: legacyTotal,
                legacy: true,
            };
        }

        const inferredStages = {
            summoner_queued: 'summoner_design',
            summoner_designing: 'summoner_design',
            summoner_art_generating: 'summoner_art',
            awaiting_art_selection: 'selection',
            ready: 'selection',
            spells_queued: 'spell_design',
            spells_designing: 'spell_design',
            spells_art_generating: 'spell_art',
            pending: 'spell_design',
            queued: 'spell_design',
            generating: 'spell_design',
            art: 'spell_art',
        };
        const inferredStage = inferredStages[String(run?.status || '')];
        if (inferredStage) {
            return {
                stage: inferredStage,
                completed: 0,
                processed: 0,
                total: 0,
                active: 0,
                legacy: false,
                indeterminate: true,
            };
        }
        return null;
    }

    function progressStageLabel(stage) {
        const labels = {
            summoner_design: ['foundry.moonDream.progressSummonerDesign', 'Designing summoner'],
            summoner_art: ['foundry.moonDream.progressSummonerArt', 'Creating summoner art'],
            selection: ['foundry.moonDream.progressSelection', 'Awaiting final selection'],
            spell_design: ['foundry.moonDream.progressSpellDesign', 'Designing spells'],
            spell_art: ['foundry.moonDream.progressSpellArt', 'Creating spell art'],
            complete: ['foundry.moonDream.progressComplete', 'Moon Dream complete'],
            deleting: ['foundry.moonDream.statusDeleting', 'Deleting'],
            failed: ['foundry.moonDream.progressFailed', 'Moon Dream failed'],
        };
        const [key, fallback] = labels[stage] || labels.failed;
        return uiText(key, fallback);
    }

    /**
     * How far the indeterminate sweep had travelled before this rebuild. The
     * bar is destroyed and recreated on every poll tick; without carrying the
     * phase the sweep snapped back to its start every two seconds.
     */
    function readIndeterminateSweepPhaseMs() {
        const fill = elements.detail?.querySelector('.moon-dream-progress--indeterminate .moon-dream-progress-fill');
        if (!fill || typeof fill.getAnimations !== 'function') return 0;
        const [sweep] = fill.getAnimations();
        // Read the phase *within* the iteration, not the animation's own clock:
        // a carried phase rides on a negative delay, which currentTime ignores.
        const timing = sweep?.effect?.getComputedTiming?.();
        const progress = timing?.progress;
        const iterationMs = timing?.duration;
        // progress is null while an effect is idle or finished; Number(null)
        // would read that as phase zero, i.e. a silent restart.
        if (typeof progress !== 'number' || !Number.isFinite(progress)) return 0;
        if (typeof iterationMs !== 'number' || !Number.isFinite(iterationMs)) return 0;
        return Math.max(0, Math.min(progress, 1) * iterationMs);
    }

    function appendRunProgress(container, run, sweepPhaseMs = 0) {
        const progressData = resolveRunProgress(run);
        if (!progressData) return;
        const total = Math.max(0, progressData.total);
        const completed = total > 0
            ? Math.min(Math.max(0, progressData.completed), total)
            : 0;
        const processed = total > 0
            ? Math.min(Math.max(completed, Number(progressData.processed) || 0), total)
            : 0;
        const active = Math.min(
            Math.max(0, Number(progressData.active) || 0),
            Math.max(total - processed, 0)
        );
        const remaining = Math.max(total - completed, 0);
        const stageLabel = progressStageLabel(progressData.stage);
        const progress = document.createElement('div');
        progress.className = 'moon-dream-progress';
        const actionRequired = progressData.stage === 'selection';
        const waitingForMeasuredGenerationProgress = [
            'summoner_design',
            'summoner_art',
            'spell_design',
            'spell_art',
        ].includes(progressData.stage)
            && processed <= 0;
        const indeterminate = progressData.indeterminate === true
            || total <= 0
            || waitingForMeasuredGenerationProgress;
        progress.classList.toggle('moon-dream-progress--action-required', actionRequired);
        progress.classList.toggle('moon-dream-progress--indeterminate', indeterminate);
        progress.setAttribute('role', actionRequired || indeterminate ? 'status' : 'progressbar');
        if (!actionRequired && !indeterminate) {
            progress.setAttribute('aria-valuemin', '0');
            progress.setAttribute('aria-valuemax', String(total));
            progress.setAttribute('aria-valuenow', String(processed));
            progress.setAttribute('aria-label', stageLabel);
        }

        const bar = document.createElement('div');
        bar.className = 'moon-dream-progress-bar';
        bar.setAttribute('aria-hidden', 'true');
        const fill = document.createElement('div');
        fill.className = 'moon-dream-progress-fill';
        if (indeterminate) {
            // Resume the sweep where the destroyed bar left it.
            if (sweepPhaseMs > 0) fill.style.animationDelay = `-${Math.round(sweepPhaseMs)}ms`;
        } else {
            fill.style.width = `${total > 0 ? Math.round((processed / total) * 100) : 0}%`;
        }
        bar.appendChild(fill);
        progress.appendChild(bar);

        const meta = document.createElement('div');
        meta.className = 'moon-dream-progress-meta';
        if (!actionRequired && !indeterminate) {
            meta.setAttribute('aria-live', 'polite');
            meta.setAttribute('aria-atomic', 'true');
        }
        meta.textContent = actionRequired || indeterminate
            ? stageLabel
            : progressData.legacy && ['generating', 'art'].includes(run.status)
            ? uiText('foundry.moonDream.spellsLeft', '{{remaining}} spells left ({{current}}/{{total}})', {
                remaining,
                current: completed,
                total,
            })
            : (['summoner_art', 'spell_art'].includes(progressData.stage)
                ? (active > 0
                    ? uiText('foundry.moonDream.progressActive', '{{label}} · {{completed}} created · {{active}} creating · {{processed}}/{{total}} processed', {
                        label: stageLabel,
                        completed,
                        active,
                        processed,
                        total,
                    })
                    : uiText('foundry.moonDream.progressProcessed', '{{label}} · {{completed}} created · {{processed}}/{{total}} processed', {
                        label: stageLabel,
                        completed,
                        processed,
                        total,
                    }))
                : uiText('foundry.moonDream.progressCount', '{{label}} ({{completed}}/{{total}})', {
                    label: stageLabel,
                    completed,
                    total,
                }));
        progress.appendChild(meta);
        container.appendChild(progress);
    }

    function deriveMoonDreamViewModel(run) {
        const status = String(run?.status || 'pending');
        if (status === 'deleting') {
            return { stage: 'deleting', currentStep: null, failedStep: null };
        }
        if (['failed', 'summoner_failed', 'spells_failed'].includes(status)) {
            const failureStage = run?.failure?.stage;
            const failedStep = status === 'summoner_failed' || failureStage === 'summoner' ? 0 : 2;
            return { stage: 'failed', currentStep: failedStep, failedStep };
        }
        if (status === 'complete') return { stage: 'complete', currentStep: null, failedStep: null };
        if (['awaiting_art_selection', 'ready'].includes(status) && !run?.selectedCandidateId) {
            return { stage: 'selection', currentStep: 1, failedStep: null };
        }
        if ([
            'pending', 'queued', 'generating', 'art',
            'spells_queued', 'spells_designing', 'spells_art_generating',
        ].includes(status) || run?.selectedCandidateId) {
            return { stage: 'spells', currentStep: 2, failedStep: null };
        }
        return { stage: 'portraits', currentStep: 0, failedStep: null };
    }

    function buildStageJourney(run) {
        const view = deriveMoonDreamViewModel(run);
        const journey = document.createElement('ol');
        journey.className = `moon-dream-stage-journey moon-dream-stage-journey--${view.stage}`;
        journey.setAttribute('aria-label', uiText('foundry.moonDream.journeyLabel', 'Moon Dream stages'));
        const steps = [
            ['foundry.moonDream.stagePortraits', 'Portraits'],
            ['foundry.moonDream.stageChoose', 'Choose a portrait'],
            ['foundry.moonDream.stageSpells', 'Spells'],
        ];
        steps.forEach(([key, fallback], index) => {
            const step = document.createElement('li');
            step.className = 'moon-dream-stage-step';
            const completed = view.stage === 'complete'
                || (view.currentStep !== null && index < view.currentStep);
            const failed = index === view.failedStep;
            const current = index === view.currentStep;
            step.classList.toggle('is-complete', completed);
            step.classList.toggle('is-current', current && !failed);
            step.classList.toggle('is-failed', failed);
            if (current) step.setAttribute('aria-current', 'step');
            const label = uiText(key, fallback);
            step.textContent = label;
            if (completed) {
                step.setAttribute('aria-label', `${label} — ${uiText('status.completed', 'Completed')}`);
            } else if (failed) {
                step.setAttribute('aria-label', `${label} — ${uiText('status.failed', 'Failed')}`);
            }
            journey.appendChild(step);
        });
        return { journey, view };
    }

    function buildHistoryBackButton() {
        const button = document.createElement('button');
        button.type = 'button';
        button.id = 'moon-dream-history-back';
        button.className = 'moon-dream-history-back';
        labelDetail(button, () => uiText('foundry.moonDream.backToSummoners', '← Summoners'));
        button.addEventListener('click', () => {
            setMobileDreamView('history');
            elements.runs?.querySelector?.('.moon-dream-run.active')?.focus?.();
        });
        return button;
    }

    function updateLoreScrollAffordances() {
        for (const id of ['moon-dream-lore-name', 'moon-dream-lore-summary']) {
            const region = document.getElementById(id);
            if (!region) continue;
            const scrollable = region.clientHeight > 0 && region.scrollHeight > region.clientHeight + 1;
            region.dataset.scrollable = String(scrollable);
            if (scrollable) region.setAttribute('aria-description', uiText('feedback.scrollForMore', 'Scroll for more'));
            else region.removeAttribute('aria-description');
        }
    }

    function configureLoreText(title, summary) {
        title.id = 'moon-dream-lore-name';
        summary.id = 'moon-dream-lore-summary';
        title.setAttribute('aria-label', title.textContent);
        summary.setAttribute('aria-labelledby', title.id);
        for (const region of [title, summary]) {
            region.tabIndex = 0;
            region.setAttribute('role', 'region');
        }
    }

    function buildDetailIdentity(title, summary, run, open = false) {
        configureLoreText(title, summary);
        const identity = document.createElement('div');
        identity.className = 'moon-dream-detail-identity';
        const about = document.createElement('details');
        about.className = 'moon-dream-about';
        about.dataset.runId = run.id;
        about.open = open;
        about.addEventListener('toggle', () => {
            if (about.isConnected) updateLoreScrollAffordances();
        }, { signal: state.eventController?.signal });
        const toggle = document.createElement('summary');
        toggle.id = `moon-dream-about-${String(run.id).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
        toggle.appendChild(title);
        toggle.setAttribute('aria-label', uiText(
            'foundry.moonDream.aboutSummoner',
            'About {{name}}',
            { name: title.textContent },
        ));
        about.append(toggle, summary);
        appendDeleteAction(run, about);
        identity.appendChild(about);
        return identity;
    }

    function buildDetailCommand(journey = null, identity = null) {
        const command = document.createElement('div');
        command.className = 'moon-dream-detail-command';
        command.classList.toggle('moon-dream-detail-command--back-only', !journey);
        command.appendChild(buildHistoryBackButton());
        if (identity) command.appendChild(identity);
        if (journey) command.appendChild(journey);
        return command;
    }

    function buildTrashIcon() {
        const svgNs = 'http://www.w3.org/2000/svg';
        const icon = document.createElementNS(svgNs, 'svg');
        icon.setAttribute('class', 'moon-dream-delete-icon');
        icon.setAttribute('viewBox', '0 0 24 24');
        icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('focusable', 'false');
        for (const d of ['M4 7h16', 'M9 7V4.5h6V7', 'M6.5 7l1 13h9l1-13', 'M10 11v6', 'M14 11v6']) {
            const stroke = document.createElementNS(svgNs, 'path');
            stroke.setAttribute('d', d);
            icon.appendChild(stroke);
        }
        return icon;
    }

    function appendDeleteAction(run, target = elements.detail) {
        if (!target || !run) return;
        const deleteWrap = document.createElement('div');
        deleteWrap.className = 'moon-dream-secondary-actions';
        deleteWrap.dataset.runId = run.id;
        const deleteButton = document.createElement('button');
        deleteButton.type = 'button';
        deleteButton.className = 'moon-dream-delete';
        /* The label is the button's name everywhere; the phone gallery shows
           the bin in its place (moon-dream.css) and keeps the label for
           assistive technology. */
        deleteButton.appendChild(buildTrashIcon());
        const deleteLabel = document.createElement('span');
        deleteLabel.className = 'moon-dream-delete-label';
        labelDetail(deleteLabel, () => uiText('foundry.moonDream.deleteSummoner', 'Delete Summoner'));
        deleteButton.appendChild(deleteLabel);
        labelDetail(deleteButton, () => uiText('foundry.moonDream.deleteSummonerTitle', 'Deletes this summoner and its spells. Deletion does not itself issue a refund; mandatory rights are unaffected.'), 'title');
        deleteButton.disabled = state.deletingSummoner
            || state.selectingArtRunId === run.id
            || run.status === 'deleting';
        deleteButton.addEventListener('click', () => handleDeleteSummoner(run.id, deleteButton));
        deleteWrap.appendChild(deleteButton);
        const feedback = document.createElement('p');
        feedback.className = 'moon-dream-delete-feedback';
        feedback.setAttribute('role', 'alert');
        feedback.hidden = true;
        deleteWrap.appendChild(feedback);
        target.appendChild(deleteWrap);
    }

    function refreshDeletionAction({ reveal = false, scrollTop = null } = {}) {
        const run = state.currentRunDetails;
        if (!run || !elements.detail) return;
        const wrap = elements.detail.querySelector('.moon-dream-secondary-actions');
        if (wrap?.dataset.runId !== run.id) return;
        const notice = state.deletionFeedback?.runId === run.id ? state.deletionFeedback : null;
        const message = notice?.message || '';
        const feedback = wrap.querySelector('.moon-dream-delete-feedback');
        feedback.textContent = message;
        feedback.hidden = !message;
        wrap.querySelector('.moon-dream-delete').disabled = state.deletingSummoner
            || state.selectingArtRunId === run.id
            || state.pendingDeletionRunId === run.id
            || run.status === 'deleting';

        const lore = elements.detail.querySelector('.moon-dream-complete-lore');
        const command = elements.detail.querySelector(':scope > .moon-dream-detail-command');
        if (lore && command) {
            // Where Delete lives is the layout's call (moon-dream.css):
            // `always` — the finished summoner seats it on the header line
            // (beside Back on a phone); `1` — the short completed layout moves
            // it into its command column only while an error needs the room;
            // otherwise the lore.
            const placement = getComputedStyle(elements.detail)
                .getPropertyValue('--dream-delete-in-command').trim();
            const useRail = placement === 'always' || (Boolean(message) && placement === '1');
            const host = useRail ? command : lore;
            if (wrap.parentElement !== host) host.appendChild(wrap);
            command.classList.toggle('moon-dream-command-feedback', useRail && Boolean(message));
        }
        // Rebuilding the children temporarily collapses the studio's actual
        // scroll owner. Restore its position after the feedback is laid out.
        if (scrollTop !== null) elements.detail.scrollTop = scrollTop;
        const about = wrap.closest('.moon-dream-about');
        const layout = `${elements.detail.clientWidth}:${elements.detail.clientHeight}`;
        const reflowed = notice?.layout && notice.layout !== layout;
        if (notice) notice.layout = layout;
        if (message && (reveal || (reflowed && (!about || about.open)))) {
            if (about) about.open = true;
            feedback.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
        }
        updateLoreScrollAffordances();
    }

    function setDeletionFeedback(runId, message = '') {
        state.deletionFeedback = message ? { runId, message } : null;
        refreshDeletionAction({ reveal: Boolean(message) });
    }

    function restoreDetailFocus(focusedDetailId) {
        if (!focusedDetailId) return;
        const replacement = document.getElementById(focusedDetailId);
        if (!replacement || !elements.detail?.contains(replacement) || replacement.disabled) return;
        try {
            replacement.focus({ preventScroll: true });
        } catch (_) {
            replacement.focus();
        }
    }

    function renderCompletedRun(run, summary, focusedDetailId) {
        elements.detail.dataset.stage = 'complete';
        setCompletedPresentation(true);
        elements.detail.appendChild(buildDetailCommand());

        renderSummonerPortrait(run);

        const spells = Array.isArray(run.spells) && run.spells.length > 0
            ? run.spells
            : (Array.isArray(run.spellPreviews) ? run.spellPreviews : []);
        if (spells.length > 0) renderSpellDeck(run, spells, { isLoading: false });

        const lore = document.createElement('div');
        lore.className = 'moon-dream-detail-card moon-dream-complete-lore';
        const title = document.createElement('div');
        title.className = 'moon-dream-detail-title';
        const summonerName = run.summonerName
            || run.summoner?.name
            || run.prompt
            || 'Moon Dream';
        title.textContent = summonerName;
        title.title = summonerName;
        title.setAttribute('aria-label', summonerName);
        const summaryEl = document.createElement('div');
        summaryEl.className = 'moon-dream-detail-summary';
        if (run.summary) summaryEl.textContent = summary;
        else labelDetail(summaryEl, () => uiText('foundry.moonDream.formingSummary', 'A summoner and spell deck are forming.'));
        configureLoreText(title, summaryEl);
        lore.appendChild(title);
        // The summoner's epithet ("The Instant Conclusion"), under the name in
        // the phone gallery (moon-dream.css).
        const epithetText = typeof run.summoner?.title === 'string' ? run.summoner.title.trim() : '';
        if (epithetText) {
            const epithet = document.createElement('div');
            epithet.className = 'moon-dream-detail-epithet';
            epithet.textContent = epithetText;
            lore.appendChild(epithet);
        }
        lore.appendChild(summaryEl);
        appendDeleteAction(run, lore);
        elements.detail.appendChild(lore);
        // Every word of this panel that is not the run's is remembered above.
        state.detailRelabelable = true;
        restoreDetailFocus(focusedDetailId);
        /* The payoff belongs to a summoner ARRIVING, not to looking one up.
           This used to fire on `detailEntrancePlaying`, which is armed only by
           the mobile master-detail navigation — so the chime played on every
           tap of an already-finished summoner on a phone, and never once on
           desktop. Celebrate the transition instead: the run must have been
           seen mid-generation, and each run pays out once per session. */
        const runId = runIdentity(run);
        const seenStage = runId ? state.detailStageByRun.get(runId) : null;
        if (runId) state.detailStageByRun.set(runId, 'complete');
        const justArrived = seenStage && seenStage !== 'complete';
        if (justArrived && runId && !state.celebratedRuns.has(runId)) {
            state.celebratedRuns.add(runId);
            celebrateSummonerArrival(runId);
        } else {
            // A held arrival opened later, after another summoner: this is
            // the paint that first shows it.
            playUnseenCelebration(runId);
        }
    }

    function collectPreviousSpellCards() {
        const previous = new Map();
        if (!elements.detail) return previous;
        elements.detail.querySelectorAll('.moon-dream-spell-card[data-spell-card-id]').forEach((slot) => {
            const card = slot.querySelector(':scope > .card');
            if (card) previous.set(slot.dataset.spellCardId, card);
        });
        return previous;
    }

    /** The spell-deck page on screen, if the player can see one. */
    function shownSpellDeckPage() {
        const deck = elements.detail?.querySelector('.moon-dream-spells');
        if (
            !deck
            || deck.classList.contains('moon-dream-spells-raster-staging')
            || deck.querySelector('.moon-dream-spells-raster-failure')
            // A pane hidden behind a phone's library has shown nothing yet.
            || isDetailBehindLibrary()
        ) return null;
        return { runId: String(state.currentRunDetails?.id || ''), page: Number(deck.dataset.page) || 1 };
    }

    function renderRunDetail(run) {
        // Record the class this paint was built for, so a later resize knows
        // whether the structure it produced is still the right one.
        lastRenderedCompactClass = usesMobileMasterDetail();
        const stageScroll = state.currentRunDetails?.id === run?.id
            ? elements.detail?.querySelector('.moon-dream-art-stage')?.scrollTop : null;
        const detailScroll = state.currentRunDetails?.id === run?.id
            && state.deletionFeedback?.runId === run?.id ? elements.detail?.scrollTop : null;
        const loreScroll = state.currentRunDetails?.id === run?.id
            ? ['moon-dream-lore-name', 'moon-dream-lore-summary'].map((id) => {
                const region = document.getElementById(id);
                return { id, top: region?.scrollTop || 0 };
            }) : [];
        try {
            paintRunDetail(run);
            const stage = elements.detail?.querySelector('.moon-dream-art-stage');
            if (stage && stageScroll !== null && stageScroll !== undefined) stage.scrollTop = stageScroll;
            refreshDeletionAction({ scrollTop: detailScroll });
            for (const { id, top } of loreScroll) {
                const region = document.getElementById(id);
                if (region) region.scrollTop = top;
            }
            updateLoreScrollAffordances();
        } finally {
            // The carry pools hold detached nodes; never let them outlive the
            // one render that adopts from them.
            cleanupUnusedCarriedDetailMedia();
            state.carriedDetailMedia = null;
            state.previousSpellCards = null;
        }
    }

    function paintRunDetail(run) {
        if (!elements.detail) return;
        cancelSpellPreview(null, { onlyOwn: true });
        spellHoverIntent.disarm();
        state.spellDeckGeneration += 1;
        const focusedDetailId = elements.detail.contains(document.activeElement)
            ? document.activeElement.id
            : '';
        state.currentRunDetails = run || null;
        state.detailLanguage = currentLanguage();
        noteRunCompletions(state.currentRunDetails);
        reschedulePollingForCurrentState();
        // Progress polling rebuilds this whole panel. Keep the outgoing spell
        // cards' settled paints so the rebuilt grid can inherit their pixels
        // instead of re-entering the loading gate and blinking every tick.
        state.previousSpellCards = collectPreviousSpellCards();
        state.shownSpellDeck = shownSpellDeckPage();
        state.carriedDetailMedia = harvestSettledDetailMedia();
        cleanupUnsettledDetailMedia();
        const progressSweepPhaseMs = readIndeterminateSweepPhaseMs();
        const previousAbout = elements.detail.querySelector('.moon-dream-about');
        const aboutOpen = previousAbout?.open === true && previousAbout.dataset.runId === run?.id;
        elements.detail.innerHTML = '';
        state.detailLabels = [];
        state.detailRelabelable = false;
        // Keep the outer paint stable while an unchanged poll rebuilds its
        // children. Removing the layout briefly invalidates its surface styles.
        if (run && !isCompletedPresentation(run)) {
            if (elements.detail.dataset.layout !== 'studio') elements.detail.dataset.layout = 'studio';
        } else {
            delete elements.detail.dataset.layout;
        }
        elements.detail.setAttribute('aria-busy', 'false');

        if (!run) {
            state.detailEntrancePlaying = false;
            setDetailEmpty(uiText('foundry.moonDream.selectSummoner', 'Select a summoner to view details.'));
            return;
        }

        // Exactly one paint per navigation gets the reveal; poll ticks and page
        // turns repaint into a panel that is already there.
        state.detailEntrancePlaying = consumeDetailEntrance();

        if (!['awaiting_art_selection', 'ready'].includes(run.status)) {
            state.selectionFeedback.delete(run.id);
        }

        pruneArtSelection(run);
        ensureSpellsPageForRun(run.id);
        const summary = run.summary || uiText('foundry.moonDream.formingSummary', 'A summoner and spell deck are forming.');
        if (isCompletedPresentation(run)) {
            renderCompletedRun(run, summary, focusedDetailId);
            return;
        }
        setCompletedPresentation(false);
        const { journey, view } = buildStageJourney(run);
        elements.detail.dataset.stage = view.stage;
        // Remember that this run was watched mid-generation; renderCompletedRun
        // pays out only for a run that transitions in front of the player.
        const inProgressId = runIdentity(run);
        if (inProgressId) state.detailStageByRun.set(inProgressId, view.stage || 'working');

        const title = document.createElement('div');
        title.className = 'moon-dream-detail-title';
        title.textContent = run.summonerName || run.prompt || 'Moon Dream';

        const summaryEl = document.createElement('div');
        summaryEl.className = 'moon-dream-detail-summary';
        summaryEl.textContent = summary;
        // Native disclosure keeps the full biography and delete action reachable
        // by touch and keyboard without competing with the portrait decision.
        const compactIdentity = buildDetailIdentity(title, summaryEl, run, aboutOpen);
        const command = buildDetailCommand(journey, compactIdentity);
        elements.detail.appendChild(command);

        const status = getStatusLabel(run.status, run.queueStatus);
        const hasSelectedArt = Boolean(run.selectedCandidateId || run.selectedImageUrl || run.summoner?.imageUrl);
        if (run.selectedCandidateId || !['awaiting_art_selection', 'ready'].includes(run.status)) {
            state.artSelections.delete(run.id);
        }

        const card = document.createElement('div');
        card.className = 'moon-dream-detail-card moon-dream-status-card';

        const statusLine = document.createElement('div');
        statusLine.className = 'moon-dream-detail-actions';
        statusLine.textContent = uiText('foundry.moonDream.statusLine', `Status: ${status.label}`, {
            status: status.label,
        });

        card.appendChild(statusLine);
        appendRunProgress(card, run, progressSweepPhaseMs);

        if (run.failure) {
            const failure = document.createElement('div');
            failure.className = 'moon-dream-detail-actions';
            const classifications = {
                safety_rejected: uiText('foundry.moonDream.failureSafety', 'The request could not pass the safety review.'),
                generation_incomplete: uiText('foundry.moonDream.failureIncomplete', 'The required generation set could not be completed.'),
                technical_failure: uiText('foundry.moonDream.failureTechnical', 'A technical failure stopped this stage.'),
            };
            const reason = classifications[run.failure.classification] || classifications.technical_failure;
            failure.textContent = uiText('foundry.moonDream.failureLine', 'Failure: {{reason}}', {
                reason,
            });
            card.appendChild(failure);

            const refund = document.createElement('div');
            refund.className = 'moon-dream-detail-actions moon-dream-refund-state';
            const refundAmount = Number(run.failure.refundAmount);
            refund.textContent = run.failure.refunded === true
                ? uiText(
                    'foundry.moonDream.failureRefunded',
                    '{{amount}} Stardust was refunded for this failed stage.',
                    { amount: formatStardust(Number.isSafeInteger(refundAmount) ? refundAmount : 0) }
                )
                : uiText(
                    'foundry.moonDream.failureRefundPending',
                    'The failed stage refund is being reconciled.'
                );
            card.appendChild(refund);

            // The way on, in so many words (a player whose spells failed had
            // to ask, 27 Oct 2026): the spells' one retry, or a new summoner.
            if (run.failure.stage === 'spells' || run.status === 'spells_failed') {
                appendSpellsFailureNext(card, run);
            } else {
                const next = document.createElement('div');
                next.className = 'moon-dream-detail-actions moon-dream-failure-next';
                next.textContent = uiText('foundry.moonDream.failureNextSummoner', 'Create a new summoner to try again.');
                card.appendChild(next);
            }
        }

        if (isRunStalled(run)) {
            const stalled = document.createElement('div');
            stalled.className = 'moon-dream-detail-actions moon-dream-status-notice moon-dream-stalled-warning';
            stalled.textContent = uiText('foundry.moonDream.stalled', 'Generation appears stalled. Internal recovery remains automatic; terminal failures are shown here.');
            card.appendChild(stalled);
        }

        const queueEtaLine = buildQueueEtaLine(run);
        if (queueEtaLine) {
            const queueLine = document.createElement('div');
            queueLine.className = 'moon-dream-detail-actions moon-dream-status-notice moon-dream-queue-eta';
            queueLine.textContent = queueEtaLine;
            card.appendChild(queueLine);
        }

        if (run.status === 'complete') {
            const guidance = document.createElement('div');
            guidance.className = 'moon-dream-detail-actions';
            guidance.textContent = uiText('foundry.moonDream.guidance', 'Visit Collection and Deck Manager to view and use your new spells.');
            card.appendChild(guidance);
        }

        elements.detail.appendChild(card);

        if (['summoner_queued', 'summoner_designing', 'summoner_art_generating', 'awaiting_art_selection', 'ready'].includes(run.status)) {
            renderSummonerArt(run, command, card);
        }

        if (run.summoner && hasSelectedArt) {
            renderSummonerPortrait(run);
        }

        const isLoading = [
            'pending',
            'queued',
            'generating',
            'art',
            'spells_queued',
            'spells_designing',
            'spells_art_generating',
        ].includes(run.status);
        const candidateSpells = Array.isArray(run.spells) && run.spells.length > 0
            ? run.spells
            : (Array.isArray(run.spellPreviews) ? run.spellPreviews : []);
        const visibleSpells = isLoading
            ? candidateSpells.filter((spell) => Boolean(spell?.imageUrl))
            : candidateSpells;
        if (visibleSpells.length > 0) {
            renderSpellDeck(run, visibleSpells, { isLoading });
        }
        restoreDetailFocus(focusedDetailId);
    }

    function renderSummonerPortrait(run) {
        if (!elements.detail || !run?.summoner) return;
        const summoner = run.summoner;
        const wrapper = document.createElement('div');
        wrapper.className = 'moon-dream-detail-card moon-dream-showcase';

        const title = document.createElement('div');
        title.className = 'moon-dream-detail-title';
        labelDetail(title, () => uiText('foundry.moonDream.yourSummoner', 'Your Summoner'));
        wrapper.appendChild(title);

        const portrait = document.createElement('div');
        portrait.className = 'moon-dream-portrait';

        /* THE PORTRAIT IS NOT A CONTROL. It briefly opened a full-screen
           viewer; that is reverted — enlarging generated art magnifies its
           imperfections rather than rewarding a look, and the control itself
           cost the picture its sharpness (an absolutely positioned box over an
           image inside a `border-radius` + `overflow: hidden` frame promotes
           it to a composited masked layer the engine rasterizes below device
           resolution: measured 78.1 against 248.4 at 932x300).

           So the frame holds the art and nothing else. Anything laid over it
           in future pays that price again, and
           `moon-dream-workspace.spec.js` measures it. */
        const frame = document.createElement('div');
        frame.className = 'moon-dream-portrait-frame';
        frame.appendChild(createDetailMediaImage(
            summoner.imageUrl,
            summoner.name || 'Summoner',
            { id: summoner.characterId, picture: summoner.picture },
        ));
        portrait.appendChild(frame);

        if (summoner.name) {
            const name = document.createElement('div');
            name.className = 'moon-dream-portrait-name';
            name.textContent = summoner.name;
            name.title = summoner.name;
            name.setAttribute('aria-label', summoner.name);
            portrait.appendChild(name);
        }

        wrapper.appendChild(portrait);
        elements.detail.appendChild(wrapper);
    }

    function removeLocalSpellPreview(preview) {
        if (!preview) return;
        const card = preview.querySelector?.('.card');
        if (card?.classList?.contains('stellar-card')) {
            Cards?.setStellarHover?.(card, false);
        }
        preview.remove();
    }

    /* A REPAINT TAKES DOWN ONLY A PREVIEW MOON DREAM PUT UP (`onlyOwn`). The
       id is shared: Moon Fuse's enlarged card is `#card-hover-preview` too,
       and this panel repaints (when the Foundry opens, on every progress poll)
       while the Moon Fuse tab is in front, so the global clear took the Moon
       Fuse popup down under the player's pointer (26 Oct 2026). A spell
       preview handed to a neighbour gave the id up and is still released.
       Every other tear-down (the account changing, the surface closing) still
       takes whatever is up: a popup can show the old account's own cards. */
    function clearSharedSpellPreview({ onlyOwn = false } = {}) {
        const shown = document.getElementById('card-hover-preview');
        if (onlyOwn && shown && !shown.dataset.moonDreamPreviewGeneration) {
            HoverHandoff?.releaseHandedOff?.();
            return;
        }
        if (typeof Cards?.clearHoverPreview === 'function') {
            Cards.clearHoverPreview();
            return;
        }
        HoverHandoff?.releaseHandedOff?.();
        removeLocalSpellPreview(document.getElementById('card-hover-preview'));
    }

    function cancelSpellPreview(generation = null, { onlyOwn = false } = {}) {
        if (generation !== null && generation !== state.spellPreviewGeneration) return false;
        state.spellPreviewGeneration += 1;
        clearSharedSpellPreview({ onlyOwn });
        return true;
    }

    /* A GLIDE KEEPS THE CARD IT LEFT (owner, 4 October 2026: the Collection's
       hand-off). A spell preview handed to a neighbour (hover-handoff.js) gave
       the id up and stays on screen until the neighbour's own preview is
       revealed, or until no hover is left to reveal one. A hover starting or
       ending on the deck therefore puts down only the preview still in the
       pointer's hands; every other tear-down (a repaint, the tab hidden, the
       account changing) goes through cancelSpellPreview and takes both. */
    function putDownHoveredSpellPreview() {
        if (HoverHandoff?.hasHandedOff?.()) {
            removeLocalSpellPreview(HoverHandoff.currentPreview());
            return;
        }
        clearSharedSpellPreview();
    }

    function endSpellHover(generation) {
        if (generation !== state.spellPreviewGeneration) return;
        state.spellPreviewGeneration += 1;
        putDownHoveredSpellPreview();
    }

    /* A poll that brings back the very run already on screen, in the stage
       where the player is choosing a portrait, has nothing to paint. Rebuilding
       the six radios and the Create spells button every tick replaced the node
       under a finger mid-tap (iOS drops the click when the DOM under it
       changes) — the owner's "I pressed Create spells and nothing happened".
       Anything that differs, including the status, repaints as before. */
    /* TRYING THE SPELLS AGAIN (game/utils/moonDreamSpellRetry.js): one paid
       retry per summoner, one a day per player. The server keeps the rule and
       says what this card may offer; a daily limit that has passed since the
       read opens the button here, because a cached read is not re-run by the
       clock alone. */
    // The answers that mean the retry's spells are on their way.
    const SPELL_RETRY_STARTED_STATUSES = new Set([
        'spells_queued',
        'spells_designing',
        'spells_art_generating',
        'complete',
    ]);

    function spellRetryOpen(retry, now = Date.now()) {
        if (!retry) return false;
        if (retry.available === true) return true;
        return retry.reason === 'daily_limit'
            && Number.isFinite(retry.availableAt)
            && now >= retry.availableAt;
    }

    function formatRetryOpensAt(availableAt) {
        const opens = new Date(availableAt);
        const sameDay = opens.toDateString() === new Date().toDateString();
        return opens.toLocaleString(currentLanguage(), {
            ...(sameDay ? {} : { weekday: 'short' }),
            hour: 'numeric',
            minute: '2-digit',
        });
    }

    function appendSpellsFailureNext(card, run) {
        const retry = run.failure?.retry;
        const next = document.createElement('div');
        next.className = 'moon-dream-detail-actions moon-dream-failure-next';
        if (spellRetryOpen(retry)) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'moon-dream-commit moon-dream-retry-spells';
            const label = document.createElement('span');
            label.textContent = uiText('foundry.moonDream.retrySpells', 'Try the spells again');
            const cost = document.createElement('span');
            cost.className = 'moon-dream-action-cost';
            cost.textContent = uiText('foundry.moonDream.stardustCost', '{{cost}} Stardust', {
                cost: formatStardust(retry.price),
            });
            button.append(label, ' ', cost);
            button.disabled = state.retryingSpellsRunId === run.id;
            button.addEventListener('click', () => {
                void retrySpells(run, button);
            });
            const note = document.createElement('p');
            note.className = 'moon-dream-retry-note';
            note.textContent = uiText(
                'foundry.moonDream.retrySpellsNote',
                'One more try for this summoner. If it fails, your Stardust comes back.'
            );
            next.append(button, note);
        } else if (retry?.reason === 'daily_limit' && Number.isFinite(retry.availableAt)) {
            next.textContent = uiText(
                'foundry.moonDream.retrySpellsDailyLimit',
                'You can try a summoner’s spells again once a day. Your next try: {{time}}.',
                { time: formatRetryOpensAt(retry.availableAt) }
            );
        } else {
            next.textContent = uiText(
                'foundry.moonDream.failureNextSpells',
                'This summoner can’t make its spells again. Create a new summoner to try once more.'
            );
        }
        card.appendChild(next);
    }

    function spellRetryRefusalLine(data) {
        const code = String(data?.code || '');
        if (code === 'moon_dream_spell_retry_daily_limit') {
            return uiText('foundry.moonDream.retrySpellsUsedToday', 'You have already tried a summoner’s spells again today.');
        }
        if (
            /^moon_dream_spell_retry_/.test(code)
            || code === 'moon_dream_selected_candidate_unavailable'
            || data?.status === 'spells_failed'
        ) {
            return uiText(
                'foundry.moonDream.failureNextSpells',
                'This summoner can’t make its spells again. Create a new summoner to try once more.'
            );
        }
        return creationRefusalLine(data, uiText, { locale: document.documentElement.lang || undefined })
            || data?.error
            || uiText('foundry.moonDream.retrySpellsFailed', 'The spells could not start again.');
    }

    async function retrySpells(run, button) {
        if (!run?.id || state.retryingSpellsRunId) return;
        const retry = run.failure?.retry;
        // Held from the first press: a second press while the question is
        // open must not ask again or send twice.
        state.retryingSpellsRunId = run.id;
        if (button) button.disabled = true;
        const confirmed = await Confirm.ask(
            uiText(
                'foundry.moonDream.retrySpellsConfirm',
                'Try {{name}}’s spells again for {{cost}} Stardust? If it fails, your Stardust comes back. Each summoner gets one more try, once a day.',
                { name: run.summonerName || run.prompt || 'Moon Dream', cost: formatStardust(retry?.price) }
            ),
            { confirmLabel: uiText('foundry.moonDream.retrySpellsConfirmLabel', 'Try again') }
        );
        if (!confirmed) {
            if (state.retryingSpellsRunId === run.id) state.retryingSpellsRunId = null;
            if (button?.isConnected) button.disabled = false;
            return;
        }
        const retryUser = state.user;
        setStatus(uiText('foundry.moonDream.retryingSpells', 'Starting the spells again...'), 'info');
        try {
            const res = await fetchWithAuth('/api/moon-dream/retry-spells', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                timeoutMs: PAID_MUTATION_TIMEOUT_MS,
                body: JSON.stringify({ runId: run.id }),
            });
            if (!sameAuthenticatedAccount(state.user, retryUser)) return;
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data?.error || !SPELL_RETRY_STARTED_STATUSES.has(data?.status)) {
                setStatus(spellRetryRefusalLine(data), 'error');
                return;
            }
            applyMutationBalance(data);
            setStatus(
                uiText('foundry.moonDream.spellsRetried', 'Spell creation has begun again.'),
                'success',
                { runId: run.id, status: 'spells_queued' }
            );
        } catch (_) {
            console.error('[MoonDream]', { code: 'spell_retry_failed' });
            setStatus(
                uiText('foundry.moonDream.retrySpellsInterrupted', 'The response was interrupted. Checking this summoner...'),
                'error'
            );
        } finally {
            if (state.retryingSpellsRunId === run.id) state.retryingSpellsRunId = null;
            // The server's answer is the truth either way: a retry that
            // committed behind an interrupted response replays without a
            // second charge, and the panel shows the run as it now is.
            if (sameAuthenticatedAccount(state.user, retryUser)) {
                await loadRunsPage(1, { forceReload: true });
                if (state.selectedRunId) await loadRunDetails(state.selectedRunId);
            }
        }
    }

    function isUnchangedSelectionPoll(run) {
        const current = state.currentRunDetails;
        return Boolean(run?.id) && state.surfaceActive
            && ['awaiting_art_selection', 'ready'].includes(run.status)
            && current?.id === run.id && current.status === run.status
            && elements.detail?.querySelector?.('.moon-dream-commit')
            // A deletion in flight or its error message keep the old repaint:
            // that path re-seats the feedback after a resize.
            && !state.deletingSummoner
            && state.pendingDeletionRunId !== run.id
            && state.deletionFeedback?.runId !== run.id
            // A read that failed left its notice on the panel; the read that
            // succeeds has to repaint, which is what clears it.
            && !elements.detail.querySelector('.moon-dream-detail-refresh-error')
            // The chrome is uiText: a language change the panel missed (the
            // tab was hidden, or a read was in flight) is a change.
            && state.detailLanguage === currentLanguage()
            && JSON.stringify(current) === JSON.stringify(run);
    }

    /**
     * A settled run (nothing generating, so nothing on its panel depends on the
     * clock) that a re-read returned byte for byte. Its panel is already the
     * truth; repainting it rebuilt every node a round trip after the tab had
     * appeared — the portrait, the deck, the lore — as identical twins.
     */
    function isUnchangedSettledRun(run) {
        const current = state.currentRunDetails;
        return Boolean(run?.id)
            && current?.id === run.id
            && !GENERATING_RUN_STATUSES.has(run.status)
            && canPreserveRenderedRunDetail(run.id)
            && lastRenderedCompactClass === usesMobileMasterDetail()
            && state.spellPageRunId === run.id
            && !state.deletingSummoner
            && state.pendingDeletionRunId !== run.id
            && state.deletionFeedback?.runId !== run.id
            && !elements.detail.querySelector('.moon-dream-detail-refresh-error')
            // Byte for byte is not enough: the labels ("← Summoners", "Delete
            // Summoner", "Status: …") are uiText, so a language change the
            // panel missed — the tab hidden when it happened, or a detail read
            // in flight — left it in the old language beside switched rows.
            && state.detailLanguage === currentLanguage()
            && JSON.stringify(current) === JSON.stringify(run);
    }

    function canPreserveSpellInspection(run) {
        const preview = document.getElementById('card-hover-preview');
        if (!preview || !state.surfaceActive || !run?.id
            || run.id !== state.currentRunDetails?.id
            || run.status !== state.currentRunDetails?.status
            || preview.dataset.moonDreamRunId !== run.id) return false;
        const spells = Array.isArray(run.spells) && run.spells.length > 0
            ? run.spells : (Array.isArray(run.spellPreviews) ? run.spellPreviews : []);
        const spell = spells.find((item) => String(item?.cardId || '') === preview.dataset.moonDreamCardId);
        return Boolean(spell) && preview.dataset.moonDreamCardSnapshot
            === JSON.stringify(buildMoonDreamCardData(spell, 'Spell'));
    }

    /* `sourceCard` is the spell card the popup enlarges: the popup adopts its
       description (size step + lines), so the text never re-wraps between
       the two (Cards: ENGRAVED DESCRIPTIONS). */
    function sizeSpellPreview(card, variant, sourceRect, preview, sourceCard = null) {
        const insets = SafeArea.insets();
        const options = Cards?.HOVER_PREVIEW_OPTIONS;
        if (!options || !Object.values(insets).some((inset) => inset > 0)) {
            if (options && sourceCard) {
                Cards?.applyPreviewScale?.(card, variant, sourceRect, preview, { ...options, sourceCard });
            } else {
                Cards?.applyPreviewScale?.(card, variant, sourceRect, preview);
            }
            return;
        }
        const safe = SafeArea.box(6);
        const bounds = card.getBoundingClientRect();
        const width = card.offsetWidth || bounds.width;
        const height = card.offsetHeight || bounds.height;
        if (!(width > 0 && height > 0)) return;
        const fit = Math.min((safe.right - safe.left) / width, (safe.bottom - safe.top) / height);
        // Limit physical enlargement before its raster is settled. Position
        // clamping alone cannot fit a card taller than the safe page box.
        Cards.applyPreviewScale(card, variant, sourceRect, preview, {
            ...options,
            baseScale: Math.min(options.baseScale, fit),
            minScale: Math.min(options.minScale, fit),
            sourceCard,
        });
        const enlarged = card.getBoundingClientRect();
        const placed = SafeArea.clamp(enlarged.left, enlarged.top, enlarged.width, enlarged.height, 6);
        preview.style.left = `${(parseFloat(preview.style.left) || 0) + placed.left - enlarged.left}px`;
        preview.style.top = `${(parseFloat(preview.style.top) || 0) + placed.top - enlarged.top}px`;
    }

    function isCurrentSpellPreview(generation, sourceCardEl, cardSlot, preview = null) {
        if (
            generation !== state.spellPreviewGeneration
            || !state.surfaceActive
            || !sourceCardEl?.isConnected
            || !cardSlot?.isConnected
            || !elements.detail?.contains(cardSlot)
        ) return false;
        if (!preview) return true;
        return preview.isConnected
            && document.getElementById('card-hover-preview') === preview;
    }

    /* A SPELL PREVIEW ANSWERS A POINTER THE PLAYER MOVED (hover-intent.js,
       the Collection's rule; snappy round 15, 28 September 2026). Firefox,
       the language menu chosen over the finished deck: the menu closed, the
       browser hit-tested the new layout under the resting pointer and sent
       that spell card a trusted enter, and a 3.5x preview opened over a
       screen the player was reading. Disarmed whenever cards come to lie
       under a resting pointer — a language change, the panel painted, the tab
       shown — so the first real move hovers the card it is on. */
    const spellHoverIntent = createHoverIntent({
        selector: '.moon-dream-spell-card > .card',
        within: () => elements.detail,
    });

    function attachSpellHoverPreview(cardSlot, sourceCardEl, cardData) {
        if (!cardSlot || !sourceCardEl || !cardData || !Cards?.createCardElement) return;

        let activeGeneration = 0;
        // The hover a neighbour's preview handed to this card, while it builds.
        let handoffEnter = null;

        // Touch: tap enlarges, tap again acts. The guard above already admits the
        // synthetic (untrusted) mouseenter this dispatches while rejecting the
        // compatibility one a real tap produces.
        TouchPreview?.attach?.(sourceCardEl);
        const presentSpellPreview = async () => {
            const generation = ++state.spellPreviewGeneration;
            activeGeneration = generation;
            putDownHoveredSpellPreview();

            const prepareRaster = Cards.prepareCardRasterForInteraction;
            if (typeof prepareRaster === 'function') {
                let rasterReady = false;
                try {
                    rasterReady = await prepareRaster(sourceCardEl, 'hover');
                } catch (_) {
                    rasterReady = false;
                }
                /* The raster is WARMED, never REQUIRED — because two repo rules
                   say so outright: AGENTS.md, "activation must never be gated on
                   raster readiness", and asset-loading law 2, never hide an
                   element because its media isn't ready.

                   HONEST LIMIT: that this gate is what broke enlarging on iOS is
                   a HYPOTHESIS, not a measured fact. What is measured is only
                   that the handler reaches this probe. No available account has
                   a completed Moon Dream run (account 1: three runs stuck at
                   "Choose final art"; account 2: none), so the live failure was
                   never observed. Removing the gate is justified by the rules
                   above regardless; do not read it as a proven root cause. */
                if (!isCurrentSpellPreview(generation, sourceCardEl, cardSlot)) {
                    return;
                }
            }
            if (!isCurrentSpellPreview(generation, sourceCardEl, cardSlot)) return;

            const sourceRect = sourceCardEl.getBoundingClientRect();
            const preview = document.createElement('div');
            preview.id = 'card-hover-preview';
            preview.className = 'card-hover-preview';
            preview.dataset.moonDreamPreviewGeneration = String(generation);
            preview.dataset.moonDreamRunId = state.currentRunDetails?.id || '';
            preview.dataset.moonDreamCardId = String(cardData.id || '');
            preview.dataset.moonDreamCardSnapshot = JSON.stringify(cardData);
            preview.style.zIndex = Cards?.HOVER_PREVIEW_Z_INDEX || '2147483647';
            preview.style.left = `${Math.round(sourceRect.left)}px`;
            preview.style.top = `${Math.round(sourceRect.top)}px`;
            preview.style.visibility = 'hidden';

            const variant = cardData.isStellar ? 'stellar' : 'normal';
            const cardEl = Cards.createCardElement(
                cardData,
                `moon-dream-preview-${cardData.id}`
            );
            cardEl.style.transformOrigin = 'center center';
            cardEl.style.setProperty('--card-hover-transform', 'scale(1) rotate(0deg) translateY(0%)');
            if (variant === 'stellar') cardEl.dataset.stellarPreview = 'collection';
            preview.appendChild(cardEl);

            HoverHandoff?.bind?.(preview, {
                source: cardSlot,
                selector: '.moon-dream-spell-card',
                clear: () => cancelSpellPreview(generation),
                handoff: true,
                release: () => removeLocalSpellPreview(preview),
            });

            preview.addEventListener('mouseleave', () => {
                // Handed off, it no longer takes the pointer, so the next move
                // reports a leave; it stays until the neighbour's is revealed.
                if (HoverHandoff?.isHandedOff?.(preview)) return;
                cancelSpellPreview(generation);
            });
            preview.addEventListener('click', () => {
                cancelSpellPreview(generation);
            });
            preview.addEventListener('wheel', (e) => {
                preview.style.pointerEvents = 'none';
                const target = document.elementFromPoint(e.clientX, e.clientY);
                preview.style.pointerEvents = 'auto';
                if (target) {
                    const scrollable = target.closest('.moon-dream-detail')
                        || target.closest('.moon-dream-spells');
                    if (scrollable) scrollable.scrollTop += e.deltaY;
                }
            }, { passive: true });

            document.body.appendChild(preview);
            sizeSpellPreview(cardEl, variant, sourceRect, preview, sourceCardEl);

            let settledRaster = false;
            try {
                settledRaster = await Cards.settleCardRasterForPresentation?.(
                    cardEl,
                    cardData,
                    {
                        oversample: 1,
                        stableGeometry: true,
                        sourceCard: sourceCardEl,
                        coverVisualExpansion: true,
                    },
                ) === true;
            } catch (_) {
                settledRaster = false;
            }
            /* STALENESS removes the preview. An unsettled raster does not.
               This is the gate that actually killed tap-to-enlarge: the popup
               was appended and then deleted because the art had not settled —
               and a Moon Dream card's art is freshly generated, so settling is
               exactly what fails here first. Measured live on WebKit at a phone
               page box: the handler ran, the popup was created, and this branch
               removed it, so nothing ever appeared.

               AGENTS.md: activation must never be gated on raster readiness.
               asset-loading law 2: show the card, let the art hydrate into it. */
            /* Staleness here means "a NEWER popup replaced this one", not "the
               hover generation moved on". A touch-latched preview outlives its
               hover generation by design: the finger lifts, the pointer stops
               hovering, and the card must stay enlarged until the player
               dismisses it. Measured live on WebKit — the settle await finishes
               after pointerup, the generation had advanced, and this branch
               deleted a preview the player had just latched open. */
            const stillCurrent = preview.isConnected
                && document.getElementById('card-hover-preview') === preview;
            if (!stillCurrent) {
                removeLocalSpellPreview(preview);
                return;
            }
            void settledRaster;

            CardTilt?.registerPreview?.(cardEl);
            if (variant === 'stellar') Cards.setStellarHover?.(cardEl, true);
            preview.style.removeProperty('visibility');
            // The preview this card took the hover from leaves in this task.
            HoverHandoff?.releaseHandedOff?.();
        };
        const enterSpellHover = (event) => {
            if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
            if (!spellHoverIntent.admits(event)) return;
            /* A neighbour's preview handed this card the hover and made itself
               transparent to the pointer; the browser then reports its own
               enter onto this card on the next move. That is the same hover,
               not a new one: restarting it would drop the build under way. */
            const handoff = HoverHandoff?.isHandoffEnter?.(event) ? {} : null;
            if (!handoff && handoffEnter) return;
            if (handoff) handoffEnter = handoff;
            const endHover = HoverHandoff?.beginHover?.();
            void presentSpellPreview().finally(() => {
                if (handoffEnter === handoff) handoffEnter = null;
                endHover?.();
            });
        };
        sourceCardEl.addEventListener('mouseenter', enterSpellHover);
        /* The hand-off names the slot (its selector), and the slot hears only
           that: a pointer the browser moves onto the slot alone, beside the
           card, enlarges nothing, as before. */
        cardSlot.addEventListener('mouseenter', (event) => {
            if (HoverHandoff?.isHandoffEnter?.(event)) enterSpellHover(event);
        });
        TouchPreview?.attach?.(sourceCardEl);

        cardSlot.addEventListener('mouseleave', (e) => {
            /* A TAP-LATCH OUTLIVES THE COMPATIBILITY MOUSE SEQUENCE.
               This is what broke enlarging on touch. A tap opens the preview
               through TouchPreview's synthetic `mouseenter`, and then the
               browser finishes the compatibility mouse sequence it synthesises
               for that tap — which ends the notional hover and delivers
               `mouseleave` here, cancelling the preview the tap had just
               opened. Measured on real page-box WebKit AND Chromium: the popup
               was created and destroyed within the same gesture
               (`previewEver: true`, `previewNow: false`), so the card appeared
               never to enlarge at all.

               While the latch holds this card, dismissal belongs to the latch —
               a second tap, or a tap elsewhere — never to a mouse event the
               finger never generated. Hover on a real pointer is unaffected:
               nothing is latched then. */
            if (TouchPreview?.isLatched?.(sourceCardEl)) return;
            // The pointer really left: its next enter is a new hover.
            handoffEnter = null;
            const preview = document.getElementById('card-hover-preview');
            if (preview && e.relatedTarget && (preview === e.relatedTarget || preview.contains(e.relatedTarget))) {
                return;
            }
            endSpellHover(activeGeneration);
        });
    }

    /* Lengths the stylesheet states, resolved where their parts use them:
       each is laid out once, as the width of a hidden probe on the HOST. On
       the detail that is where `--card-unit` is still the ambient one (the
       phone floor included — inside the grid it is the dealt card's) and the
       detail's own box is the container its `cq` units read. A custom
       property's text is not a length to parse: above full HD the server
       serves every px length as a viewport expression
       (game/utils/uiScaleCss.js), and read as text a gutter came out 0. */
    function resolveLengths(host, lengths) {
        const probe = document.createElement('span');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:absolute;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;';
        const parts = Object.entries(lengths).map(([key, length]) => {
            const part = document.createElement('span');
            part.dataset.dreamLength = key;
            part.style.cssText = `position:absolute;left:0;top:0;height:1px;width:${length};`;
            probe.appendChild(part);
            return { key, part };
        });
        host.appendChild(probe);
        const resolved = Object.fromEntries(parts.map(({ key, part }) => [key, part.getBoundingClientRect().width]));
        probe.remove();
        return resolved;
    }

    /* THE DETAIL'S ARRANGEMENT (moon-dream.css). The dossier stacks the story
       under a portrait that never drops below the owner's design; on a window
       wide or short for its height that floor took the whole column (at
       2560x720 the story was a 4px sliver of a scroller). Where the floor would
       leave the story less than two of its lines, the summoner is laid out in
       lanes instead. The stylesheet states both lengths from the detail's own
       box, so the answer never depends on the arrangement it picks; a layout
       that states its own lanes (a phone's page box) is not measured, and a
       pane with no box (hidden behind a phone's library) keeps what it had.

       The answer belongs to the WINDOW. A status line in the composer strip
       (a refused delete, a failed generation) shortens the panel under it, and
       at 1400x640 that put the story under its two lines and the whole
       summoner jumped into lanes; so it is asked again only when the window's
       size changes, while the deck is still re-cut for whatever box it has. */
    let completedArrangement = null;
    function arrangeCompletedDetail(detail, detailStyle) {
        if (detailStyle.getPropertyValue('--dream-arrangement').trim() !== 'measured') {
            if (detail.dataset.arrangement) delete detail.dataset.arrangement;
            completedArrangement = null;
            return;
        }
        if (!(detail.clientWidth > 0) || !(detail.clientHeight > 0)) return;
        const viewport = `${window.innerWidth}x${window.innerHeight}`;
        if (completedArrangement?.viewport !== viewport) {
            const { short } = resolveLengths(detail, {
                short: 'calc(max(0px, var(--dream-story-min) - var(--dream-story-room)))',
            });
            completedArrangement = { viewport, lanes: short > 0.5 };
        }
        if (completedArrangement.lanes) {
            if (detail.dataset.arrangement !== 'lanes') detail.dataset.arrangement = 'lanes';
        } else if (detail.dataset.arrangement) {
            delete detail.dataset.arrangement;
        }
    }

    /* A FINISHED DECK IS CUT FROM ITS OWN BOX. The grid's box is fixed by the
       detail's arrangement and its header and footer lines (moon-dream.css),
       so it is measured before a card is drawn; the cut publishes the one card
       width the grid's tracks and every card read. Only a layout that says so
       (`--dream-deck-fit: 1`) is measured, and only on screen: the narrow
       fallback, and a pane hidden behind a phone's library, keep the page they
       had until they can be measured. */
    function measureCompletedDeck(grid, total) {
        const detail = elements.detail;
        if (!detail || !grid?.isConnected || detail.dataset.stage !== 'complete') return null;
        const detailStyle = getComputedStyle(detail);
        if (detailStyle.getPropertyValue('--dream-deck-fit').trim() !== '1') return null;
        arrangeCompletedDetail(detail, detailStyle);
        // The layout caps the page (two rows of four unless it says otherwise).
        const cap = (name) => Number.parseInt(detailStyle.getPropertyValue(name), 10) || undefined;
        const gridStyle = getComputedStyle(grid);
        const width = grid.clientWidth
            - (Number.parseFloat(gridStyle.paddingLeft) || 0) - (Number.parseFloat(gridStyle.paddingRight) || 0);
        const height = grid.clientHeight
            - (Number.parseFloat(gridStyle.paddingTop) || 0) - (Number.parseFloat(gridStyle.paddingBottom) || 0);
        if (!(width > 0) || !(height > 0)) return null;
        // The game's standard card, and the narrowest gutter the cut may use;
        // the page then spreads its cards up to the widest one the layout
        // allows, and centres the rest.
        const lengths = resolveLengths(detail, {
            card: 'calc(12.29 * var(--card-unit, 1vmin))',
            gap: 'calc(var(--dream-deck-gap, 0px))',
            gapMax: 'calc(var(--dream-deck-gap-max, 0px))',
        });
        const standardCard = lengths.card;
        const gap = lengths.gap || 0;
        const gapMax = Math.max(gap, lengths.gapMax || 0);
        const fit = MoonDreamDeckFit.solve({
            width,
            height,
            columnGap: gap,
            rowGap: gap,
            aspect: CARD_ASPECT,
            minCardWidth: Math.max(standardCard * DECK_MIN_CARD_SHARE, DECK_MIN_CARD_PX),
            total,
            maxRows: cap('--dream-deck-max-rows'),
            maxColumns: cap('--dream-deck-max-columns'),
            sizeStep: Number.parseFloat(detailStyle.getPropertyValue('--dream-deck-size-step')) || 0,
        });
        if (!fit.measured) return null;
        const spare = (free, count) => (count > 1 ? Math.max(gap, Math.min(gapMax, free / (count - 1))) : 0);
        const gapX = spare(width - fit.columns * fit.cardWidth, fit.columns);
        const gapY = spare(height - fit.rows * fit.cardHeight, fit.rows);
        const block = fit.columns * fit.cardWidth + (fit.columns - 1) * gapX;
        return { ...fit, gapX, gapY, inset: Math.max(0, (width - block) / 2) };
    }

    /* The cut lives on the deck's wrapper (a box-less element), so its heading
       lines up with the first and last card column and its grid deals at the
       cut's size. An unchanged value is not written again. */
    function applyCompletedDeckFit(grid, fit) {
        state.completedDeckFit = fit;
        const deck = grid.parentElement || grid;
        const values = {
            '--dream-card-width': `${fit.cardWidth}px`,
            '--dream-deck-columns': String(fit.columns),
            '--dream-deck-rows': String(fit.rows),
            '--dream-deck-gap-x': `${fit.gapX}px`,
            '--dream-deck-gap-y': `${fit.gapY}px`,
            '--dream-deck-inset': `${fit.inset}px`,
        };
        Object.entries(values).forEach(([name, value]) => {
            if (deck.style.getPropertyValue(name) !== value) deck.style.setProperty(name, value);
        });
    }

    /* THE DECK WATCHES ITS OWN BOX, AND THE DETAIL'S. A window resize is not
       the only thing that changes them: a phone's safe areas, the pane being
       shown, the panel around it. Each change re-arranges the detail and
       re-cuts the page in the same frame (a ResizeObserver reports after
       layout, before paint), so a stale arrangement or cut never paints. The
       cut never changes the grid's own box (it only fills its tracks); a new
       arrangement does, but it is decided from the detail's box, which nothing
       inside it changes — observed together, the deck's new box is deeper
       than the detail's and is reported in the same frame, so the observer
       cannot feed itself. */
    let deckResizeObserver = null;
    function observeCompletedDeck(grid) {
        deckResizeObserver?.disconnect();
        deckResizeObserver = null;
        if (typeof ResizeObserver !== 'function' || !grid) return;
        let first = true;
        deckResizeObserver = new ResizeObserver(() => {
            // The first report is the boxes this deck was just cut from.
            if (first) {
                first = false;
                return;
            }
            refitCompletedDeck();
        });
        if (elements.detail) deckResizeObserver.observe(elements.detail);
        deckResizeObserver.observe(grid);
    }

    /**
     * Re-measure a finished deck after its box changed (a resize, a phone's
     * pane shown). A cut that shows the same cards (the same page size, or a
     * deck that fits on one page either way) resizes and re-spaces them in
     * place — the shape is the grid's tracks, not its cards; any other re-deals
     * the page that holds the first card on screen.
     */
    function refitCompletedDeck() {
        const run = state.currentRunDetails;
        const grid = elements.detail?.querySelector?.(':scope > .moon-dream-spells > .moon-dream-spells-grid');
        if (!run || !grid) return;
        const spells = Array.isArray(run.spells) && run.spells.length > 0
            ? run.spells : (Array.isArray(run.spellPreviews) ? run.spellPreviews : []);
        const fit = measureCompletedDeck(grid, spells.length);
        if (!fit) return;
        const shown = state.spellsPageSize;
        if (fit.pageSize === shown || spells.length <= Math.min(fit.pageSize, shown)) {
            applyCompletedDeckFit(grid, fit);
            state.spellsPageSize = fit.pageSize;
            return;
        }
        if (canRepaintDetailForViewport()) renderRunDetail(run);
    }

    let deckRefitRaf = 0;
    function scheduleDeckRefit() {
        if (deckRefitRaf) return;
        deckRefitRaf = requestAnimationFrame(() => {
            deckRefitRaf = 0;
            refitCompletedDeck();
        });
    }

    /**
     * The detail's STRUCTURE depends on the viewport class, not just its CSS.
     *
     * `renderInProgressRun` measures the window to decide whether the summoner
     * identity is hosted inside `.moon-dream-detail-command` (phone) or beside
     * it (desktop), so a detail painted in one class keeps that structure
     * forever — a phone rotating across the 560px landscape boundary kept
     * whichever orientation painted first. Only a class CHANGE repaints:
     * repainting on every resize tick would discard carried media and focus for
     * nothing.
     */
    let lastRenderedCompactClass = null;
    function scheduleDetailViewportRepaint() {
        updateLoreScrollAffordances();
        const compact = usesMobileMasterDetail();
        if (compact === lastRenderedCompactClass) return;
        lastRenderedCompactClass = compact;
        if (!canRepaintDetailForViewport()) return;
        renderRunDetail(state.currentRunDetails);
    }

    /**
     * A viewport repaint rebuilds the panel ON SCREEN for the new class, so it
     * has something to rebuild only while that panel is `currentRunDetails`.
     * After the player picks another summoner, `currentRunDetails` is the one
     * they LEFT until the new read lands, and the panel shows the new one's
     * loading well (or, if that read failed, its failure): repainting there
     * put the summoner they left back over it. The window's resize listener
     * and `moonfall:viewport-change` both ask this.
     */
    function canRepaintDetailForViewport() {
        const current = state.currentRunDetails;
        if (!current || !canPreserveRenderedRunDetail(current.id)) return false;
        const beingReplaced = (state.loadingDetail
                && state.selectedRunId
                && current.id !== state.selectedRunId)
            || (state.detailRequest && state.detailRequest.runId !== current.id);
        return !beingReplaced;
    }

    if (typeof window !== 'undefined') {
        window.addEventListener('resize', scheduleDeckRefit, { passive: true });
        window.addEventListener('resize', scheduleDetailViewportRepaint, { passive: true });
        window.addEventListener('orientationchange', scheduleDetailViewportRepaint, { passive: true });
    }

    function renderSpellDeck(run, spells, { isLoading } = {}) {
        if (!elements.detail || !Array.isArray(spells)) return;
        const orderedSpells = [...spells].sort((left, right) => (
            (Number(left?.manaCost) - Number(right?.manaCost))
            || String(left?.name || '').localeCompare(String(right?.name || ''))
            || String(left?.cardId || '').localeCompare(String(right?.cardId || ''))
        ));
        const wrapper = document.createElement('div');
        wrapper.className = 'moon-dream-detail-card moon-dream-spells';

        const title = document.createElement('div');
        title.className = 'moon-dream-detail-title';
        labelDetail(title, () => {
            const label = isLoading
                ? uiText('foundry.moonDream.spellsConjured', 'Spells Conjured')
                : uiText('foundry.moonDream.spellDeck', 'Spell Deck');
            return uiText('foundry.moonDream.spellSectionCount', `${label} (${orderedSpells.length})`, {
                label,
                count: orderedSpells.length,
            });
        });
        wrapper.appendChild(title);

        const subtitle = document.createElement('div');
        subtitle.className = 'moon-dream-detail-actions';
        const expectedSpellCount = Math.max(
            orderedSpells.length,
            Number.isSafeInteger(run?.progress?.total) ? run.progress.total : 0,
        );
        labelDetail(subtitle, () => {
            if (isLoading) {
                return uiText(
                    'foundry.moonDream.spellsArriving',
                    'Completed spells appear here as they arrive. They enter your Collection together when creation finishes.',
                );
            }
            if (expectedSpellCount > orderedSpells.length) {
                return uiText(
                    'foundry.moonDream.partialSpells',
                    '{{completed}} of {{total}} spell cards were generated. Those cards are now in your Collection.',
                    { completed: orderedSpells.length, total: expectedSpellCount },
                );
            }
            return uiText(
                'foundry.moonDream.spellsInCollection',
                'These spells are now in your Collection.',
            );
        });
        wrapper.appendChild(subtitle);

        const grid = document.createElement('div');
        grid.className = 'moon-dream-spells-grid';
        /* A finished deck is placed before it is dealt: its box is fixed by the
           detail's header and footer lines, so the cut (how many cards, how
           wide) is measured on the empty grid and every card is built at its
           final size. */
        wrapper.appendChild(grid);
        elements.detail.appendChild(wrapper);
        const fit = measureCompletedDeck(grid, orderedSpells.length);
        if (fit) applyCompletedDeckFit(grid, fit);
        if (elements.detail.dataset.stage === 'complete') observeCompletedDeck(grid);

        const previousPageSize = state.spellsPageSize;
        const pageSize = spellsPageSize();
        // A re-cut keeps the player where they were: the page that holds the
        // first card they were looking at.
        if (fit && previousPageSize > 0 && previousPageSize !== pageSize && state.spellsPage > 1) {
            state.spellsPage = Math.floor(((state.spellsPage - 1) * previousPageSize) / pageSize) + 1;
        }
        state.spellsPageSize = pageSize;
        const pagination = paginateList(orderedSpells, pageSize, state.spellsPage);
        state.spellsPage = pagination.page;
        wrapper.classList.toggle('moon-dream-spells--paginated', pagination.totalPages > 1);
        wrapper.dataset.page = String(pagination.page);
        // This page was already on screen (a poll tick, a language change):
        // the section stays painted and only a card still settling waits, in
        // its own cell. A first reveal or a page turn stages the page whole.
        const shown = state.shownSpellDeck;
        state.shownSpellDeck = null;
        const deckShown = Boolean(shown)
            && shown.runId === String(run?.id || '')
            && shown.page === pagination.page;

        const sourceRasters = [];
        const previousSpellCards = state.previousSpellCards || new Map();
        state.previousSpellCards = null;

        pagination.pageItems.forEach((spell, dealIndex) => {
            const cardSlot = document.createElement('div');
            cardSlot.className = 'moon-dream-spell-card';
            cardSlot.dataset.spellCardId = String(spell.cardId || '');
            // The deal's stagger offset. Set unconditionally so the reveal has
            // nothing left to compute at the moment it starts.
            cardSlot.style.setProperty('--md-deal-index', String(dealIndex));
            const cardData = buildMoonDreamCardData(spell, 'Spell');
            if (Cards?.createCardElement && cardData) {
                const cardEl = Cards.createCardElement(cardData, `moon-dream-spell-${spell.cardId}`);
                cardEl.classList.add('moon-dream-card');
                cardSlot.appendChild(cardEl);
                if (cardEl.querySelector('.card-art-paint')) {
                    sourceRasters.push({
                        cardEl,
                        cardData,
                        sourceCard: previousSpellCards.get(cardSlot.dataset.spellCardId) || null,
                    });
                }
                if (CardTilt?.register) {
                    CardTilt.register(cardEl);
                }
                attachSpellHoverPreview(cardSlot, cardEl, cardData);
            } else if (cardData) {
                cardSlot.classList.add('moon-dream-card-fallback');
                const art = document.createElement('div');
                art.className = 'moon-dream-card-art';
                const img = document.createElement('img');
                img.alt = cardData.name;
                art.appendChild(img);
                const loadCardArt = Cards?.loadCardArtImage
                    || Cards?.enhanceCardArt;
                if (cardData.picture && loadCardArt) {
                    void loadCardArt(img, cardData.picture, {
                        fit: 'cover',
                        oversample: 1,
                    });
                } else {
                    attachDirectMediaImage(
                        img,
                        cardData.picture,
                        { id: cardData.id },
                    );
                }

                const name = document.createElement('div');
                name.className = 'moon-dream-card-name';
                name.textContent = cardData.name;

                const cost = document.createElement('div');
                cost.className = 'moon-dream-card-cost';
                labelDetail(cost, () => uiText('common.costValue', `Cost ${cardData.manaCost}`, {
                    cost: cardData.manaCost,
                }));

                cardSlot.appendChild(art);
                cardSlot.appendChild(name);
                cardSlot.appendChild(cost);
            }
            grid.appendChild(cardSlot);
        });

        const paginationEl = document.createElement('div');
        paginationEl.className = 'moon-dream-pagination';
        paginationEl.hidden = pagination.totalPages <= 1;
        paginationEl.innerHTML = `
            <button id="moon-dream-spells-prev" type="button"></button>
            <div class="moon-dream-page-info" id="moon-dream-spells-page-info"></div>
            <button id="moon-dream-spells-next" type="button"></button>
        `;
        const pageOfLabel = () => uiText('common.pageOf', `Page ${pagination.page} of ${pagination.totalPages}`, {
            page: pagination.page,
            total: pagination.totalPages,
        });
        labelDetail(paginationEl.querySelector('#moon-dream-spells-prev'), () => uiText('common.prev', 'Prev'));
        labelDetail(paginationEl.querySelector('#moon-dream-spells-page-info'), pageOfLabel);
        labelDetail(paginationEl.querySelector('#moon-dream-spells-next'), () => uiText('common.next', 'Next'));
        /* The finished summoner's deck (moon-dream.css) reads its page at the
           end of its own heading line and shows a dot per page under the
           cards; the counter keeps the phrase for assistive technology. The
           heading carries the label so the two share one line box and can
           never run into each other in a long language. */
        if (pagination.totalPages > 1) labelDetail(title, pageOfLabel, 'data-page');
        const dots = document.createElement('div');
        dots.className = 'moon-dream-page-dots';
        dots.setAttribute('aria-hidden', 'true');
        for (let page = 1; page <= pagination.totalPages; page += 1) {
            const dot = document.createElement('span');
            dot.className = page === pagination.page ? 'moon-dream-page-dot is-current' : 'moon-dream-page-dot';
            dots.appendChild(dot);
        }
        paginationEl.appendChild(dots);
        const prevBtn = paginationEl.querySelector('#moon-dream-spells-prev');
        const nextBtn = paginationEl.querySelector('#moon-dream-spells-next');
        if (prevBtn) prevBtn.disabled = pagination.page <= 1;
        if (nextBtn) nextBtn.disabled = pagination.page >= pagination.totalPages;
        const turnSpellPage = (direction) => {
            state.spellsPage = direction === 'next'
                ? state.spellsPage + 1
                : Math.max(1, state.spellsPage - 1);
            renderRunDetail(run);
        };
        if (prevBtn) prevBtn.addEventListener('click', () => turnSpellPage('prev'));
        if (nextBtn) nextBtn.addEventListener('click', () => turnSpellPage('next'));
        /* A finger swipes the deck's pages (owner, 28 October 2026: "scroll
           left right on spells ... through swiping, but keep pagination as
           is"): the same turn the buttons make, on touch only. The cards
           trail the finger; the rebuilt deck replaces them on release. */
        if (pagination.totalPages > 1) {
            SwipePager.attach(wrapper, {
                follow: grid,
                canSwipe: (direction) => (direction === 'next'
                    ? pagination.page < pagination.totalPages
                    : pagination.page > 1),
                onSwipe: turnSpellPage,
            });
        }

        wrapper.appendChild(paginationEl);

        // THE DEAL RIDES THE COHORT RELEASE; IT DOES NOT ADD ONE.
        //
        // The deck is withheld behind the pre-existing raster cohort gate
        // (`visibility: hidden`), and that gate is the sanctioned wait. What
        // the entrance must not do is re-hide the cards once that gate opens —
        // an earlier version dealt them in with an opacity fade plus a 260ms
        // portrait lead, which held the last card invisible for ~400ms AFTER
        // the deck was ready to show. So:
        //
        //   * `md-deal-in` animates transform only. At the release every card
        //     is opaque; the group appears together, as law 2 requires.
        //   * the wave's clock starts with the ENTRANCE, not with the release.
        //     Whatever it consumed while the cohort was staging is handed to
        //     CSS as a negative `animation-delay`, so the stagger straddles the
        //     release instead of queueing behind it. A deck slower than the
        //     whole wave simply appears settled, with no motion owed.
        const dealsOnReveal = state.detailEntrancePlaying === true;
        const dealIn = () => {
            if (!dealsOnReveal || !wrapper.isConnected) return;
            const elapsed = Date.now() - (state.detailEntranceStartedAt || 0);
            // Positive => the wave has not started yet (a very fast release);
            // negative => this much of it already ran behind the staging hold.
            const offset = Math.max(-SPELL_DEAL_TOTAL_MS, SPELL_DEAL_LEAD_MS - elapsed);
            wrapper.style.setProperty('--md-deal-offset', `${offset}ms`);
            wrapper.classList.add('moon-dream-spells-dealing');
            setTimeout(
                () => wrapper.classList.remove('moon-dream-spells-dealing'),
                SPELL_DEAL_TOTAL_MS + Math.max(0, offset),
            );
        };

        const settleRaster = Cards?.settleCardRasterForPresentation;
        const settleOne = ({ cardEl, cardData, sourceCard }) => {
            try {
                return Promise.resolve(settleRaster(
                    cardEl,
                    cardData,
                    sourceCard
                        ? { oversample: 1, stableGeometry: true, sourceCard }
                        : { oversample: 1, stableGeometry: true },
                ));
            } catch (_) {
                return Promise.resolve(false);
            }
        };
        if (typeof settleRaster === 'function' && sourceRasters.length > 0 && deckShown) {
            holdSettlingSpellSlots(run, wrapper, sourceRasters, settleOne);
        } else if (typeof settleRaster === 'function' && sourceRasters.length > 0) {
            const generation = ++state.spellDeckGeneration;
            wrapper.classList.add('moon-dream-spells-raster-staging');
            wrapper.setAttribute('aria-busy', 'true');
            // Bounded atomic reveal (docs/asset-loading.md grace rule): the
            // deck appears together when every raster settles, and a slow
            // settle releases it at the cap with art-well placeholders instead
            // of holding the panel hostage.
            let stagingReleased = false;
            const releaseStaging = () => {
                if (stagingReleased || !wrapper.isConnected) return;
                stagingReleased = true;
                wrapper.classList.remove('moon-dream-spells-raster-staging');
                wrapper.setAttribute('aria-busy', 'false');
                dealIn();
            };
            const stagingCap = setTimeout(releaseStaging, SPELL_DECK_REVEAL_CAP_MS);
            void Promise.allSettled(sourceRasters.map(settleOne)).then((settlements) => {
                if (
                    generation !== state.spellDeckGeneration
                    || !wrapper.isConnected
                    || !elements.detail?.contains(wrapper)
                    || String(state.currentRunDetails?.id || '') !== String(run?.id || '')
                ) {
                    clearTimeout(stagingCap);
                    return;
                }
                clearTimeout(stagingCap);
                const rasterReady = settlements.every((result) => (
                    result.status === 'fulfilled' && result.value === true
                ));
                // A failure screen may only replace a deck the user has not
                // seen yet; after the cap-release the presented cards keep
                // their truthful per-card placeholders.
                if (!rasterReady && !stagingReleased) {
                    stagingReleased = true;
                    wrapper.classList.remove('moon-dream-spells-raster-staging');
                    wrapper.setAttribute('aria-busy', 'false');
                    grid.remove();
                    paginationEl.remove();
                    const failure = document.createElement('div');
                    failure.className = 'moon-dream-detail-actions moon-dream-spells-raster-failure';
                    failure.textContent = uiText(
                        'foundry.moonDream.failureTechnical',
                        'A technical failure stopped this stage.',
                    );
                    wrapper.appendChild(failure);
                    return;
                }
                releaseStaging();
            });
        } else {
            dealIn();
        }
    }

    /**
     * A spell deck already on screen, rebuilt by a poll tick: each card that
     * ARRIVED and is still settling waits in its own cell (`visibility:
     * hidden`, so the grid never moves) until its own raster settles or until
     * the cap, counted from the spell's FIRST arrival so a tick cannot restart
     * it. A card the player has already seen is never held again: it takes
     * its predecessor's paint in the same task, or keeps showing its art
     * well. A card that cannot settle shows its truthful art well; the deck
     * the player already saw is never swapped for the failure line.
     */
    function holdSettlingSpellSlots(run, wrapper, sourceRasters, settleOne) {
        state.spellDeckGeneration += 1;
        const now = Date.now();
        const runId = String(run?.id || '');
        let waiting = 0;
        const settleDone = () => {
            waiting -= 1;
            if (waiting <= 0 && wrapper.isConnected) wrapper.setAttribute('aria-busy', 'false');
        };
        sourceRasters.forEach((raster) => {
            const slot = raster.cardEl.closest('.moon-dream-spell-card');
            const arrivalKey = `${runId}:${slot?.dataset.spellCardId || ''}`;
            if (!raster.sourceCard && !state.spellArrivalAt.has(arrivalKey)) {
                state.spellArrivalAt.set(arrivalKey, now);
            }
            // No arrival entry means the player has already seen this card:
            // the first reveal released it (settled, or at its cap with its
            // art well), or it settled after its own hold. A slow card handed
            // a fresh cap here vanished for 2.5 s on every poll. Only an
            // arrival still inside its first hold waits.
            const arrivedAt = state.spellArrivalAt.get(arrivalKey);
            const remainingMs = arrivedAt === undefined
                ? 0
                : Math.max(0, SPELL_DECK_REVEAL_CAP_MS - (now - arrivedAt));
            const settling = settleOne(raster);
            if (!slot || remainingMs <= 0) return;
            waiting += 1;
            slot.classList.add('moon-dream-spell-card--settling');
            let released = false;
            const release = (settled) => {
                if (released) return;
                released = true;
                clearTimeout(cap);
                if (settled === true) state.spellArrivalAt.delete(arrivalKey);
                slot.classList.remove('moon-dream-spell-card--settling');
                settleDone();
            };
            const cap = setTimeout(() => release(false), remainingMs);
            settling.then((value) => release(value === true), () => release(false));
        });
        wrapper.setAttribute('aria-busy', waiting > 0 ? 'true' : 'false');
    }

    function renderSummonerArt(run, command, statusCard) {
        if (!elements.detail) return;
        const candidates = Array.isArray(run.candidates)
            ? run.candidates
                .map((candidate) => ({
                    ...candidate,
                    imageUrl: sanitizeImageUrlForDirectClient(candidate?.imageUrl),
                }))
                .sort((left, right) => left.ordinal - right.ordinal)
            : [];
        const selectionReady = ['awaiting_art_selection', 'ready'].includes(run.status);
        const portraitsPending = ['summoner_queued', 'summoner_designing'].includes(run.status);
        const expectedCount = Number(state.artVariationCount)
            || Number(run.progress?.total)
            || candidates.length;
        const requestedCount = selectionReady
            ? candidates.length
            : (state.artVariationCount || Number(run.progress?.total) || candidates.length);
        const slotCount = Math.min(Math.max(Number(requestedCount) || 0, candidates.length), 12);
        if (slotCount === 0) return;
        const candidatesByOrdinal = new Map(candidates.map((candidate) => [candidate.ordinal, candidate]));
        const candidateIds = new Set(candidates.map((candidate) => candidate.candidateId));
        const hasCompleteCandidateSet = candidates.length > 0
            && candidates.length === slotCount
            && candidatesByOrdinal.size === candidates.length
            && candidateIds.size === candidates.length
            && candidates.every((candidate) => (
                candidate?.status === 'complete'
                && typeof candidate.candidateId === 'string'
                && Boolean(candidate.imageUrl)
            ));
        const canChoose = selectionReady
            && !run.selectedCandidateId
            && hasCompleteCandidateSet;
        const selectionLanguage = authoritativeRunLanguage(run);
        const runSelectionPrice = run.selectionPrice === null || run.selectionPrice === undefined
            ? null
            : Number(run.selectionPrice);
        const selectionPrice = run.status === 'ready'
            ? (Number.isSafeInteger(runSelectionPrice) ? runSelectionPrice : 0)
            : (Number.isSafeInteger(runSelectionPrice) ? runSelectionPrice : null);
        const hasSelectionBalance = run.status === 'ready'
            ? selectionPrice === 0
            : Number.isSafeInteger(state.stardustBalance)
                && Number.isSafeInteger(selectionPrice)
                && state.stardustBalance >= selectionPrice;
        const selectionPriceReady = Number.isSafeInteger(selectionPrice)
            && (run.status === 'ready' ? selectionPrice === 0 : selectionPrice > 0);
        const selectionAdmissionReady = Boolean(selectionLanguage)
            && selectionPriceReady
            && hasSelectionBalance;
        const selectionBusy = isSelectionBusy(run.id);
        const selectionFeedback = state.selectionFeedback.get(run.id)
            || (state.selectingArt ? {
                message: uiText('foundry.moonDream.selectingArt', 'Confirming your summoner...'),
                tone: 'info',
                busy: true,
            } : null);
        let selectedCandidateId = run.selectedCandidateId || state.artSelections.get(run.id) || null;
        if (selectedCandidateId && !candidateIds.has(selectedCandidateId)) {
            state.artSelections.delete(run.id);
            selectedCandidateId = null;
        }

        const artWrapper = document.createElement('div');
        artWrapper.className = 'moon-dream-detail-card moon-dream-art-workspace';

        const fieldset = document.createElement('fieldset');
        fieldset.className = 'moon-dream-art-fieldset';
        const legend = document.createElement('legend');
        legend.className = 'moon-dream-detail-title';
        legend.textContent = uiText('foundry.moonDream.summonerArt', 'Summoner Art');
        fieldset.appendChild(legend);

        const helper = document.createElement('div');
        helper.className = 'moon-dream-detail-actions moon-dream-art-hint';
        if (!selectionReady) {
            helper.textContent = uiText(
                'foundry.moonDream.artLoading',
                'All six portraits are generating in parallel. Each appears as soon as it finishes.'
            );
        } else if (selectionReady && candidates.length > 0 && candidates.length < expectedCount) {
            helper.textContent = uiText(
                'foundry.moonDream.partialPortraits',
                '{{completed}} of {{total}} portraits were generated. Choose one of the available portraits.',
                { completed: candidates.length, total: expectedCount },
            );
        } else {
            helper.textContent = uiText(
                'foundry.moonDream.selectArtPermanent',
                'Choose one portrait locally, then make it permanent to begin Stage 2.'
            );
        }
        fieldset.appendChild(helper);

        // Each column owns its own header and actions. A full-width header and
        // footer would consume most of a landscape phone's available height.
        const stage = document.createElement('div');
        stage.className = 'moon-dream-art-stage';
        const choices = document.createElement('div');
        choices.className = 'moon-dream-art-choices';
        const journey = command?.querySelector('.moon-dream-stage-journey');
        if (journey) choices.appendChild(journey);
        if (command) stage.appendChild(command);

        const preview = document.createElement('div');
        preview.className = 'moon-dream-art-preview';
        preview.setAttribute('aria-live', 'polite');

        function renderPreview(candidate, ordinal) {
            preview.replaceChildren();
            if (candidate?.imageUrl) {
                preview.appendChild(createDetailMediaImage(
                    candidate.imageUrl,
                    uiText('foundry.moonDream.optionAlt', 'Summoner portrait option {{option}}', {
                        option: ordinal + 1,
                    }),
                ));
                return;
            }
            const empty = document.createElement('div');
            empty.className = 'moon-dream-empty';
            empty.textContent = candidate?.status === 'failed'
                ? uiText('foundry.moonDream.imageUnavailable', 'Image unavailable')
                : portraitsPending
                    ? uiText('foundry.moonDream.portraitPending', 'Your portraits will appear here')
                    : uiText('foundry.moonDream.portraitGenerating', 'Portrait generating...');
            preview.appendChild(empty);
        }

        const previewCandidate = candidates.find((candidate) => (
            candidate.candidateId === selectedCandidateId
        ))
            || candidates.find((candidate) => Boolean(candidate?.imageUrl))
            || candidatesByOrdinal.get(0);
        renderPreview(previewCandidate, previewCandidate?.ordinal ?? 0);

        const grid = document.createElement('div');
        grid.className = 'moon-dream-art-grid moon-dream-art-selector';
        const radioName = `moon-dream-art-${run.id}`;
        const safeRunId = String(run.id || 'run').replace(/[^a-zA-Z0-9_-]/g, '-');

        for (let displayIndex = 0; displayIndex < slotCount; displayIndex += 1) {
            const candidate = selectionReady
                ? candidates[displayIndex]
                : (candidatesByOrdinal.get(displayIndex) || {
                    candidateId: `dream_${displayIndex + 1}`,
                    ordinal: displayIndex,
                    status: 'generating',
                    imageUrl: null,
                });
            const card = document.createElement('label');
            card.className = 'moon-dream-art-card';
            const selected = candidate.candidateId === selectedCandidateId;
            const selectable = canChoose
                && candidate.status === 'complete'
                && Boolean(candidate.imageUrl)
                && !selectionBusy
                && !state.selectingArt;
            if (selected) card.classList.add('selected');
            if (!selectable) {
                card.classList.add('disabled');
            }

            const radio = document.createElement('input');
            radio.className = 'moon-dream-art-radio';
            radio.type = 'radio';
            radio.name = radioName;
            radio.id = `moon-dream-art-${safeRunId}-${displayIndex + 1}`;
            radio.value = candidate.candidateId;
            radio.setAttribute('aria-label', uiText('foundry.moonDream.optionNumber', 'Option {{option}}', {
                option: displayIndex + 1,
            }));
            radio.checked = selected;
            radio.disabled = !selectable;
            card.appendChild(radio);

            const visual = document.createElement('div');
            visual.className = 'moon-dream-art-visual';
            if (candidate.imageUrl) {
                visual.appendChild(createDetailMediaImage(
                    candidate.imageUrl,
                    uiText('foundry.moonDream.optionAlt', 'Summoner portrait option {{option}}', {
                        option: displayIndex + 1,
                    }),
                ));
            } else {
                const empty = document.createElement('div');
                empty.className = 'moon-dream-empty';
                empty.textContent = candidate.status === 'failed'
                    ? uiText('foundry.moonDream.imageUnavailable', 'Image unavailable')
                    : portraitsPending
                        ? uiText('foundry.moonDream.portraitPendingShort', 'Preparing portrait')
                        : uiText('foundry.moonDream.portraitGenerating', 'Portrait generating...');
                visual.appendChild(empty);
            }
            card.appendChild(visual);

            const artLabel = document.createElement('div');
            artLabel.className = 'moon-dream-art-label';
            artLabel.setAttribute('aria-hidden', 'true');
            artLabel.textContent = selected ? '✓' : String(displayIndex + 1);
            card.appendChild(artLabel);

            if (selectable) {
                radio.addEventListener('change', () => {
                    if (!radio.checked) return;
                    state.artSelections.set(run.id, candidate.candidateId);
                    selectedCandidateId = candidate.candidateId;
                    for (const option of grid.querySelectorAll('.moon-dream-art-card')) {
                        const optionRadio = option.querySelector('.moon-dream-art-radio');
                        const optionLabel = option.querySelector('.moon-dream-art-label');
                        const isSelected = optionRadio?.checked === true;
                        option.classList.toggle('selected', isSelected);
                        if (optionLabel) {
                            const optionNumber = Number(optionRadio?.dataset.ordinal || 0) + 1;
                            optionLabel.textContent = isSelected ? '✓' : String(optionNumber);
                        }
                    }
                    renderPreview(candidate, displayIndex);
                    if (commitButton) commitButton.disabled = !selectionAdmissionReady;
                });
            }
            radio.dataset.ordinal = String(displayIndex);

            grid.appendChild(card);
        }

        stage.appendChild(preview);
        choices.appendChild(grid);
        fieldset.append(stage, choices);
        // The six portrait options appear together, never one by one.
        holdMediaCohortUntilLoaded(fieldset.querySelectorAll('.moon-dream-art-visual img, .moon-dream-art-preview img'));

        let commitButton = null;
        if (canChoose) {
            const commitBar = document.createElement('div');
            commitBar.className = 'moon-dream-commit-bar';
            commitBar.dataset.runId = run.id;
            const commitCopy = document.createElement('div');
            commitCopy.className = 'moon-dream-commit-copy';
            commitBar.appendChild(commitCopy);
            const warning = document.createElement('p');
            warning.id = `moon-dream-permanent-warning-${safeRunId}`;
            warning.className = 'moon-dream-permanent-warning';
            // Read before the spells are paid for, and it also says they can't
            // be traded (owner, 27 Oct 2026: the Moon Exchange trades only
            // Character cards). One short note: the shortest phone has room
            // for two lines under the button.
            warning.textContent = uiText(
                'foundry.moonDream.permanentPortraitNote',
                'Permanent choice. No spell rerolls. Yours alone, can’t be traded.'
            );
            commitCopy.appendChild(warning);

            let languageStatus = null;
            if (!selectionLanguage) {
                languageStatus = document.createElement('p');
                languageStatus.id = `moon-dream-language-status-${safeRunId}`;
                languageStatus.className = 'moon-dream-language-status';
                languageStatus.setAttribute('role', 'status');
                languageStatus.textContent = uiText(
                    'foundry.moonDream.selectionLanguageSync',
                    'Syncing this Moon Dream\'s original language before permanent selection...'
                );
                commitCopy.appendChild(languageStatus);
            }

            let balanceStatus = null;
            if (
                run.status !== 'ready'
                && Number.isSafeInteger(selectionPrice)
                && Number.isSafeInteger(state.stardustBalance)
                && state.stardustBalance < selectionPrice
            ) {
                balanceStatus = document.createElement('p');
                balanceStatus.id = `moon-dream-balance-status-${safeRunId}`;
                balanceStatus.className = 'moon-dream-balance-warning';
                balanceStatus.setAttribute('role', 'status');
                balanceStatus.textContent = uiText(
                    'foundry.moonDream.notEnoughStardustForStage',
                    'You need {{cost}} Stardust to start this stage.',
                    { cost: formatStardust(selectionPrice) }
                );
                commitCopy.appendChild(balanceStatus);
            }

            const commitStatus = document.createElement('p');
            commitStatus.id = `moon-dream-commit-status-${safeRunId}`;
            commitStatus.className = 'moon-dream-commit-status moon-dream-selection-status';
            commitStatus.setAttribute('role', 'status');
            commitStatus.setAttribute('aria-live', usesMobileMasterDetail() ? 'polite' : 'off');
            commitStatus.setAttribute('aria-atomic', 'true');
            commitCopy.appendChild(commitStatus);
            applySelectionFeedback(commitBar, commitStatus, selectionFeedback);

            commitButton = document.createElement('button');
            commitButton.type = 'button';
            commitButton.className = 'moon-dream-commit';
            commitButton.id = `moon-dream-commit-${safeRunId}`;
            commitButton.setAttribute(
                'aria-describedby',
                [warning.id, languageStatus?.id, balanceStatus?.id].filter(Boolean).join(' ')
            );
            commitButton.disabled = !selectedCandidateId
                || selectionBusy
                || state.selectingArt
                || !selectionAdmissionReady;
            if (run.status === 'ready') {
                commitButton.textContent = uiText('foundry.moonDream.confirmSummonerLegacy', 'Confirm Summoner · No additional Stardust');
            } else if (selectionPriceReady) {
                const label = document.createElement('span');
                label.textContent = uiText('foundry.moonDream.createSpells', 'Create spells');
                const cost = document.createElement('span');
                cost.className = 'moon-dream-action-cost';
                cost.textContent = uiText('foundry.moonDream.stardustCost', '{{cost}} Stardust', { cost: formatStardust(selectionPrice) });
                commitButton.append(label, ' ', cost);
            } else {
                commitButton.textContent = uiText('foundry.moonDream.pricingLoading', 'Loading stage prices...');
            }
            if (run.status !== 'ready' && selectionPriceReady) {
                commitButton.setAttribute('aria-label', `${uiText(
                    'foundry.moonDream.confirmSummonerAndGenerateSpells',
                    'Confirm Summoner & Create Spells',
                )} (${selectionPrice} ✦)`);
            }
            commitButton.addEventListener('click', () => {
                const selectedRadio = grid.querySelector('.moon-dream-art-radio:checked');
                if (!selectedRadio?.value || !selectionLanguage) return;
                void commitArtSelection(run.id, selectedRadio.value, selectionLanguage, commitButton, grid);
            });
            commitBar.appendChild(commitButton);
            choices.appendChild(commitBar);
        }

        // Progress and recovery messages use the action slot while portraits
        // form, keeping the image stage's geometry stable as results arrive.
        if (statusCard) {
            if (selectionReady) stage.insertBefore(statusCard, preview);
            else {
                const backgroundNote = document.createElement('p');
                backgroundNote.className = 'moon-dream-background-note';
                backgroundNote.textContent = uiText(
                    'foundry.moonDream.backgroundNote',
                    'You can return to the lobby. Creation continues.'
                );
                stage.appendChild(backgroundNote);
                choices.appendChild(statusCard);
            }
        }

        artWrapper.appendChild(fieldset);
        elements.detail.appendChild(artWrapper);
    }

    async function commitArtSelection(runId, candidateId, language, button, grid) {
        if (!runId || !candidateId || !SUPPORTED_RUN_LANGUAGES.has(language) || state.selectingArt) return;
        let refreshAfterUnlock = false;
        let requireSelectionEvidence = false;
        const selectionUser = state.user;
        const operationId = state.selectionMutationGeneration + 1;
        state.selectionMutationGeneration = operationId;
        const originalButtonDisabled = button?.disabled === true;
        const radioDisabledStates = new Map(
            Array.from(grid?.querySelectorAll?.('.moon-dream-art-radio') || [])
                .map((radio) => [radio, radio.disabled])
        );
        state.selectingArt = true;
        state.selectingArtRunId = runId;
        if (button) button.disabled = true;
        for (const radio of grid?.querySelectorAll?.('.moon-dream-art-radio') || []) radio.disabled = true;
        for (const deleteButton of elements.detail?.querySelectorAll?.('.moon-dream-delete') || []) {
            deleteButton.disabled = true;
        }
        const confirmingMessage = uiText('foundry.moonDream.selectingArt', 'Confirming your summoner...');
        setStatus(confirmingMessage, 'info');
        setSelectionFeedback(runId, { message: confirmingMessage, tone: 'info', busy: true });
        try {
            const res = await fetchWithAuth('/api/moon-dream/select-art', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                timeoutMs: PAID_MUTATION_TIMEOUT_MS,
                body: JSON.stringify({ runId, candidateId, language }),
            });
            if (
                !sameAuthenticatedAccount(state.user, selectionUser)
                || state.selectionMutationGeneration !== operationId
            ) return;
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data?.error) {
                if (res.status === 409 || data?.code === 'moon_dream_permanent_selection_conflict') {
                    const conflictMessage = uiText(
                        'foundry.moonDream.selectionConflict',
                        'This Moon Dream was permanently selected on another device. Loading the server choice...'
                    );
                    setStatus(conflictMessage, 'error');
                    setSelectionFeedback(runId, { message: conflictMessage, tone: 'error', busy: true });
                    refreshAfterUnlock = true;
                    return;
                }
                refreshAfterUnlock = true;
                const failureMessage = creationRefusalLine(data, uiText, {
                    locale: document.documentElement.lang || undefined,
                })
                    || data?.error
                    || uiText('foundry.moonDream.selectArtFailed', 'Failed to select art.');
                setStatus(failureMessage, 'error');
                setSelectionFeedback(runId, { message: failureMessage, tone: 'error', busy: true });
                return;
            }
            state.artSelections.delete(runId);
            applyMutationBalance(data);
            /* The confirmed portrait makes the summoner playable: the roster
               the summoner select and the avatar picker keep ahead is stale
               (summoner-roster.js). */
            window.dispatchEvent(new CustomEvent('moonfall:summoners-changed', { detail: { source: 'moon-dream', runId } }));
            const successMessage = uiText(
                'foundry.moonDream.artSelected',
                'Summoner confirmed. Spell creation has begun.'
            );
            setStatus(successMessage, 'success', { runId, status: 'spells_queued' });
            setSelectionFeedback(runId, { message: successMessage, tone: 'success', busy: true });
            if (data?.charged === true) {
                const selected = state.currentRunDetails?.candidates?.find?.(
                    (candidate) => candidate?.candidateId === candidateId
                );
                showSummonerCreatedBanner(runId, selected?.imageUrl);
            }
            await loadRunsPage(1, { forceReload: true });
            if (state.selectedRunId) {
                await loadRunDetails(state.selectedRunId);
            }
        } catch (_) {
            if (
                !sameAuthenticatedAccount(state.user, selectionUser)
                || state.selectionMutationGeneration !== operationId
            ) return;
            console.error('[MoonDream]', { code: 'art_selection_failed' });
            refreshAfterUnlock = true;
            requireSelectionEvidence = true;
            const interruptedMessage = uiText(
                'foundry.moonDream.selectionReconcile',
                'The response was interrupted. Checking the recorded selection...'
            );
            setStatus(interruptedMessage, 'error');
            setSelectionFeedback(runId, { message: interruptedMessage, tone: 'error', busy: true });
        } finally {
            const operationIsCurrent = () => (
                sameAuthenticatedAccount(state.user, selectionUser)
                && state.selectionMutationGeneration === operationId
                && state.selectingArtRunId === runId
            );
            if (!operationIsCurrent()) return;
            try {
                let reconciled = null;
                if (refreshAfterUnlock) {
                    // The paid response can fail after the server commits it.
                    // Reconcile the run that was charged even if the player
                    // navigated to another summoner while the POST was pending;
                    // otherwise the global paid-action lock could be released
                    // without ever learning the first mutation's outcome.
                    const outcome = await reconcileArtSelection(runId, selectionUser, operationId, {
                        requireSelectionEvidence,
                    });
                    if (!operationIsCurrent() || outcome.cancelled) return;
                    reconciled = outcome.run;
                    if (outcome.timedOut) {
                        const unknownMessage = uiText(
                            'foundry.moonDream.selectionOutcomeUnknown',
                            "We couldn't verify the selection. Refresh this summoner before trying again."
                        );
                        setStatus(unknownMessage, 'error');
                        setSelectionFeedback(runId, {
                            message: unknownMessage,
                            tone: 'error',
                            busy: false,
                        });
                    }
                    if (outcome.notFound) {
                        pruneRunSelectionState(runId);
                        forgetCurrentRunDetails(runId);
                        state.runs = state.runs.filter((listedRun) => listedRun.id !== runId);
                        if (state.selectedRunId === runId) {
                            state.selectedRunId = null;
                            setMobileDreamView('history');
                            renderRuns();
                            setDetailEmpty(uiText(
                                'foundry.moonDream.selectSummoner',
                                'Select a summoner to view details.'
                            ));
                        }
                    } else if (reconciled?.id === runId && state.selectedRunId === runId) {
                        renderRunDetail(reconciled);
                    }
                    if (
                        reconciled?.id === runId
                        && reconciled.selectedCandidateId === candidateId
                        && reconciled.summoner?.characterId
                    ) {
                        showSummonerCreatedBanner(runId, reconciled.selectedImageUrl || reconciled.summoner.imageUrl);
                    }
                    if (reconciled?.id === runId && reconciled.selectedCandidateId) {
                        // A lost success response also lost its exact remaining
                        // balance. Refresh that authority before another paid
                        // run becomes available instead of rendering stale
                        // pre-charge Stardust and inviting a rejected action.
                        await loadStardust();
                    }
                }
                const authoritativeRun = reconciled?.id === runId
                    ? reconciled
                    : (state.currentRunDetails?.id === runId ? state.currentRunDetails : null);
                const stillAwaitingSelection = authoritativeRun
                    && ['awaiting_art_selection', 'ready'].includes(authoritativeRun.status);
                if (stillAwaitingSelection) {
                    const feedback = state.selectionFeedback.get(runId);
                    if (feedback) setSelectionFeedback(runId, { ...feedback, busy: false });
                    if (button?.isConnected) button.disabled = originalButtonDisabled;
                    radioDisabledStates.forEach((wasDisabled, radio) => {
                        if (radio.isConnected) radio.disabled = wasDisabled;
                    });
                } else {
                    state.selectionFeedback.delete(runId);
                }
            } finally {
                if (operationIsCurrent()) {
                    state.selectingArt = false;
                    state.selectingArtRunId = null;
                    const currentDetail = state.currentRunDetails;
                    if (currentDetail) renderRunDetail(currentDetail);
                }
            }
        }
    }

    function showSummonerCreatedBanner(runId, imageUrl) {
        if (
            !runId
            || state.createdBannerRuns.has(runId)
            || typeof showAchievementBanner !== 'function'
        ) return;
        state.createdBannerRuns.add(runId);
        showAchievementBanner(
            uiText('foundry.moonDream.summonerCreatedTitle', 'Summoner Created!'),
            uiText('foundry.moonDream.summonerCreatedSubtitle', 'Your summoner is ready while their spells are being created.'),
            imageUrl || undefined
        );
    }

    async function handleDeleteSummoner(runId, buttonEl) {
        if (!runId || state.deletingSummoner || state.selectingArtRunId === runId) return;
        const target = state.currentRunDetails?.id === runId
            ? state.currentRunDetails : state.runs.find((run) => run.id === runId);
        if (!await Confirm.ask(
            uiText('foundry.moonDream.confirmDelete', 'Delete this summoner? This cannot be undone. Deletion does not itself issue a Stardust refund; mandatory rights are unaffected.'),
            {
                destructive: true,
                confirmLabel: uiText('foundry.moonDream.deleteSummoner', 'Delete Summoner'),
                detail: target?.summonerName || target?.summoner?.name || target?.prompt || '',
            },
        )) return;
        // State can move on while the dialog is open.
        if (state.deletingSummoner || state.selectingArtRunId === runId) return;
        const deletionUser = state.user;
        const operationId = state.deletionMutationGeneration + 1;
        state.deletionMutationGeneration = operationId;
        const operationIsCurrent = () => (
            sameAuthenticatedAccount(state.user, deletionUser)
            && state.deletionMutationGeneration === operationId
        );
        state.deletingSummoner = true;
        setDeletionFeedback(runId);
        if (buttonEl) buttonEl.disabled = true;
        setStatus(uiText('foundry.moonDream.deletingSummoner', 'Deleting summoner...'), 'info');
        try {
            const res = await fetchWithAuth(`/api/moon-dream/summoner/${encodeURIComponent(runId)}`, {
                method: 'DELETE',
            });
            if (!operationIsCurrent()) return;
            const data = await res.json().catch(() => ({}));
            if (!operationIsCurrent()) return;
            if (!res.ok || data?.error) {
                const message = data?.error || uiText('foundry.moonDream.deleteFailed', 'Failed to delete summoner.');
                setStatus(message, 'error');
                setDeletionFeedback(runId, message);
                return;
            }
            if (data?.success !== true) {
                const message = uiText('foundry.moonDream.deleteFailed', 'Failed to delete summoner.');
                setStatus(message, 'error');
                setDeletionFeedback(runId, message);
                return;
            }
            pruneRunSelectionState(runId);
            state.deletionsAccepted += 1;
            // Accepted: its spells are leaving (or have left) the Collection.
            announceHoldingsChanged();
            if (data.deleting === true && data.alreadyDeleted !== true) {
                state.pendingDeletionRunId = runId;
                setStatus(uiText('foundry.moonDream.deletingSummoner', 'Deleting summoner...'), 'info');
                await loadRuns();
                if (!operationIsCurrent()) return;
                if (state.selectedRunId === runId) await loadRunDetails(runId, { fresh: true });
                return;
            }
            state.pendingDeletionRunId = null;
            setStatus(uiText('foundry.moonDream.deleted', 'Summoner deleted.'), 'success');
            if (state.selectedRunId === runId) state.selectedRunId = null;
            forgetCurrentRunDetails(runId);
            await loadRuns();
        } catch (_) {
            if (!operationIsCurrent()) return;
            console.error('[MoonDream]', { code: 'summoner_deletion_failed' });
            const message = uiText('foundry.moonDream.deleteFailed', 'Failed to delete summoner.');
            setStatus(message, 'error');
            setDeletionFeedback(runId, message);
        } finally {
            if (operationIsCurrent()) {
                state.deletingSummoner = false;
                if (buttonEl) buttonEl.disabled = false;
                refreshDeletionAction();
            }
        }
    }

    // The server judges a prompt against Moon Dream's content list before the
    // confirmation (owner, 27 Oct 2026): a forbidden one is refused at once,
    // with nothing spent. Only that refusal stops here; any other answer goes
    // on, and the start request enforces the same rule.
    async function isPromptRefusedByServer(prompt, language) {
        try {
            const res = await fetchWithAuth('/api/moon-dream/check-prompt', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                timeoutMs: PROMPT_CHECK_TIMEOUT_MS,
                body: JSON.stringify({ prompt, language }),
            });
            if (res.ok) return false;
            const data = await res.json().catch(() => ({}));
            return data?.code === PROMPT_POLICY_CODE;
        } catch (_) {
            return false;
        }
    }

    function promptRefusedLine() {
        return uiText(
            'foundry.moonDream.promptNotAllowed',
            'Moon Dream can’t create this summoner. Try describing it another way. No Stardust was spent.'
        );
    }

    async function submitStart(pending) {
        if (!pending || state.starting || !elements.generate) return;
        state.starting = true;
        elements.generate.disabled = true;
        setStatus(uiText('foundry.moonDream.starting', 'Starting summoner generation...'), 'info');
        try {
            const res = await fetchWithAuth('/api/moon-dream/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                timeoutMs: PAID_MUTATION_TIMEOUT_MS,
                body: JSON.stringify({
                    requestId: pending.requestId,
                    prompt: pending.prompt,
                    language: pending.language,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data?.error) {
                if (data?.code === 'moon_dream_start_outcome_unknown') {
                    keepPendingStartForReconciliation(pending.requestId, false);
                    setStatus(uiText(
                        'foundry.moonDream.startReconcile',
                        'The response was interrupted. Moonfall will reconcile this request automatically.'
                    ), 'error');
                    return;
                }
                clearPendingStart(pending.requestId);
                if (data?.code === 'creation_rate_limited') {
                    applyGenerationCooldown(data);
                    return;
                }
                if (data?.code === PROMPT_POLICY_CODE) {
                    setStatus(promptRefusedLine(), 'error');
                    return;
                }
                // Moonfall's own refusal — a pause, a busy or silent service —
                // in plain words and the player's language (service-pause.js).
                const serviceLine = creationRefusalLine(data, uiText, {
                    locale: document.documentElement.lang || undefined,
                });
                setStatus(serviceLine || data?.error || uiText('foundry.moonDream.startFailed', 'Failed to start Moon Dream.'), 'error');
                return;
            }
            if (
                !data
                || typeof data.status !== 'string'
                || (data.status !== 'deleted' && typeof data.id !== 'string')
            ) {
                throw new Error('Moon Dream admission response was incomplete');
            }
            clearPendingStart(pending.requestId);
            if (data?.status === 'deleted' && data?.charged !== true) {
                if (elements.prompt?.value.trim() === pending.prompt) elements.prompt.value = '';
                applyMutationBalance(data);
                setStatus(uiText(
                    'foundry.moonDream.deletedRequestReplay',
                    'This Moon Dream was already deleted. No Stardust was spent.'
                ), 'info');
                await loadRunsPage(1, { forceReload: true });
                return;
            }
            if (data?.charged === true && MoonfallAnalytics) {
                MoonfallAnalytics.capture('moon_dream_run');
            }
            if (elements.prompt?.value.trim() === pending.prompt) elements.prompt.value = '';
            applyMutationBalance(data);
            setStatus(uiText(
                'foundry.moonDream.queuedGenerating',
                'Stage 1 queued. Creating six distinct portraits...'
            ), 'success', { runId: data.id, status: 'summoner_queued' });
            offerDiscordPing();
            await loadRunsPage(1, { forceReload: true });
            if (data?.id) {
                await selectRun(data.id);
            }
        } catch (err) {
            console.error('[MoonDream]', { code: 'summoner_start_failed' });
            if (err?.message === 'Authentication required') {
                clearPendingStart(pending.requestId);
                setStatus(uiText('foundry.moonDream.startFailed', 'Failed to start Moon Dream.'), 'error');
            } else {
                keepPendingStartForReconciliation(pending.requestId, false);
                setStatus(uiText(
                    'foundry.moonDream.startReconcile',
                    'The response was interrupted. Moonfall will reconcile this request automatically.'
                ), 'error');
            }
        } finally {
            state.starting = false;
            updateGenerateAvailability();
        }
    }

    async function reconcilePendingStart() {
        if (!state.user || !state.surfaceActive || state.starting) return;
        const pending = readPendingStart();
        if (!pending) return;
        const validation = validatePrompt(pending.prompt);
        if (!validation.ok) {
            clearPendingStart(pending.requestId);
            return;
        }
        if (elements.prompt && !elements.prompt.value) elements.prompt.value = pending.prompt;
        try {
            const res = await fetchWithAuth(
                `/api/moon-dream/start-status/${encodeURIComponent(pending.requestId)}`,
                { timeoutMs: READ_REQUEST_TIMEOUT_MS }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data?.error) {
                keepPendingStartForReconciliation(pending.requestId, true);
                return;
            }
            if (data?.found !== true) {
                const createdAt = Number(pending.createdAt);
                const ageMs = Number.isFinite(createdAt) ? Date.now() - createdAt : PENDING_RECONCILE_WINDOW_MS;
                if (ageMs < PENDING_RECONCILE_WINDOW_MS) {
                    keepPendingStartForReconciliation(pending.requestId, true);
                    return;
                }
                clearPendingStart(pending.requestId);
                applyMutationBalance(data);
                setStatus(uiText(
                    'foundry.moonDream.startNotAdmitted',
                    'The interrupted request was not admitted. No Stardust was spent.'
                ), 'info');
                return;
            }
            if (typeof data.status !== 'string' || (data.status !== 'deleted' && typeof data.id !== 'string')) {
                keepPendingStartForReconciliation(pending.requestId, true);
                return;
            }
            clearPendingStart(pending.requestId);
            applyMutationBalance(data);
            if (elements.prompt?.value.trim() === pending.prompt) elements.prompt.value = '';
            if (data.status === 'deleted') {
                setStatus(uiText(
                    'foundry.moonDream.deletedRequestReplay',
                    'This Moon Dream was already deleted. No Stardust was spent.'
                ), 'info');
                await loadRunsPage(1, { forceReload: true });
                return;
            }
            setStatus(uiText(
                'foundry.moonDream.queuedGenerating',
                'Stage 1 queued. Creating six distinct portraits...'
            ), 'success', { runId: data.id, status: 'summoner_queued' });
            offerDiscordPing();
            await loadRunsPage(1, { forceReload: true });
            await selectRun(data.id);
        } catch (err) {
            if (err?.message === 'Authentication required') {
                clearPendingStart(pending.requestId);
                setStatus(uiText('foundry.moonDream.startFailed', 'Failed to start Moon Dream.'), 'error');
                return;
            }
            keepPendingStartForReconciliation(pending.requestId, true);
        }
    }

    function attemptPendingStartReconciliation() {
        const pending = readPendingStart();
        if (
            state.starting
            || !pending
            || state.reconciledPendingRequestId === pending.requestId
            || Date.now() < state.pendingReconcileRetryAt
        ) {
            return Promise.resolve();
        }
        state.reconciledPendingRequestId = pending.requestId;
        return reconcilePendingStart();
    }

    async function handleGenerate() {
        if (!elements.prompt || !elements.generate || state.starting) return;
        if (!state.pricing || state.artVariationCount !== 6) {
            setStatus(uiText(
                'foundry.moonDream.pricingUnavailable',
                'Stage pricing is unavailable. No Stardust was spent.'
            ), 'error');
            updateGenerateAvailability();
            return;
        }
        if (!clearGenerationCooldownIfExpired() && isGenerationCooldownActive()) {
            setStatus(buildCreationCooldownMessage({
                retryAfterMs: state.generationCooldownUntil - Date.now(),
                cooldownUntil: state.generationCooldownUntil,
            }), 'error');
            elements.generate.disabled = true;
            return;
        }

        const validation = validatePrompt(elements.prompt.value);
        if (!validation.ok) {
            setStatus(validation.message, 'error');
            return;
        }
        if (state.checkingPrompt) return;
        state.checkingPrompt = true;
        let refused = false;
        try {
            refused = await isPromptRefusedByServer(validation.normalized, currentLanguage());
        } finally {
            state.checkingPrompt = false;
        }
        if (refused) {
            setStatus(promptRefusedLine(), 'error');
            return;
        }
        // Both stages, in one breath, before any Stardust moves: the summoner
        // stage is charged now, the spell stage only if the player keeps a
        // portrait and creates its spells.
        const confirmed = await Confirm.ask(
            uiText('foundry.moonDream.startConfirm', 'Create this summoner?'),
            {
                confirmLabel: uiText('foundry.moonDream.generateSummoner', 'Create Summoner'),
                detail: uiText(
                    'foundry.moonDream.startConfirmDetail',
                    '{{summoner}} now for six portraits. {{spells}} more later, only if you keep one and create its spells.',
                    {
                        summoner: `${formatStardust(Number(state.pricing.summoner))} ✦`,
                        spells: `${formatStardust(Number(state.pricing.spells))} ✦`,
                    },
                ),
            },
        );
        if (!confirmed || state.starting || !state.pricing) return;
        const language = currentLanguage();
        let pending;
        try {
            pending = resolvePendingStart(validation.normalized, language);
        } catch (_) {
            console.error('[MoonDream]', { code: 'request_id_generation_failed' });
            setStatus(uiText('foundry.moonDream.startFailed', 'Failed to start Moon Dream.'), 'error');
            return;
        }
        await submitStart(pending);
    }

    function bindEvents() {
        const signal = state.eventController.signal;
        document.addEventListener('pointermove', (event) => spellHoverIntent.observeMove(event), {
            passive: true,
            signal,
        });
        if (elements.form) {
            elements.form.addEventListener('submit', (event) => {
                event.preventDefault();
                void handleGenerate();
            }, { signal });
        } else if (elements.generate) {
            elements.generate.addEventListener('click', handleGenerate, { signal });
        }
        if (elements.runsPrev) {
            elements.runsPrev.addEventListener('click', () => {
                if (state.runsPage <= 1) return;
                loadRunsPage(state.runsPage - 1);
            }, { signal });
        }
        if (elements.runsNext) {
            elements.runsNext.addEventListener('click', () => {
                if (state.runsIsDone) return;
                loadRunsPage(state.runsPage + 1);
            }, { signal });
        }
        window.addEventListener('moonfall:languagechange', () => {
            // The language menu closes over the deck, under a resting pointer.
            spellHoverIntent.disarm();
            if (!state.user || !state.surfaceActive) return;
            updatePricingUi();
            /* THE CLIENT'S OWN WORDS CHANGE WITH THE REST OF THE PAGE, in this
               task: the library rows' status and pager (renderRuns reconciles
               them in place) and a finished summoner's panel
               (relabelRunDetail). They used to wait for the re-reads below —
               the status 0.6-1.0 s after the page's labels, resizing its row
               on the phones, and the panel rebuilt whole after that (snappy
               round 15). */
            renderRuns();
            const relabeled = relabelRunDetail();
            /* The page on screen, re-read with its own cursor (a position,
               not a language), exactly as a poll tick does. Reloading page
               1 and walking forward repainted every page on the way:
               Page 3 -> Page 1 -> Page 2 -> Page 3. And the summoner beside
               it at the same time — it was read only after the page had
               answered — never joining a read that left in the old language.
               A panel said again above is kept when its run comes back
               unchanged (isUnchangedSettledRun); any other is repainted. */
            const selectedRunId = state.selectedRunId;
            void Promise.all([
                loadRuns(),
                selectedRunId
                    ? loadRunDetails(selectedRunId, { preserveExisting: relabeled, fresh: true })
                    : null,
            ]);
        }, { signal });
        window.addEventListener('moonfall:viewport-change', () => {
            const nextRunsPageSize = runsPageSize();
            const nextSpellsPageSize = spellsPageSize();
            const runsPageSizeChanged = state.runsPageSize !== nextRunsPageSize;
            const spellsPageSizeChanged = state.spellsPageSize !== nextSpellsPageSize;
            updateCreateStripVisibility();
            updateLoreScrollAffordances();
            if (!runsPageSizeChanged && !spellsPageSizeChanged) return;
            /* "The viewport just became compact" — said against the compact
               PREDICATE rather than against one page-size value. It used to
               read `next === PHONE_RUNS_PAGE_SIZE`, which silently meant
               "compact" only while the phone tier had exactly one page size;
               the landscape tier now has its own, and a rotation into it would
               have left the player on the library instead of their summoner. */
            const enteringCompactDetail = usesMobileMasterDetail()
                && !PHONE_RUNS_PAGE_SIZES.has(state.runsPageSize)
                && Boolean(state.selectedRunId);
            state.spellsPageSize = nextSpellsPageSize;
            if (spellsPageSizeChanged) state.spellsPage = 1;
            if (runsPageSizeChanged) {
                state.runsPageSize = nextRunsPageSize;
                resetRunsPagination();
            }
            if (enteringCompactDetail) {
                setMobileDreamView('detail');
            }
            if (canRepaintDetailForViewport()) {
                renderRunDetail(state.currentRunDetails);
            }
            if (!runsPageSizeChanged) return;
            if (!state.user || !state.surfaceActive) {
                renderRuns();
                return;
            }
            void loadRunsPage(1, { forceReload: true });
        }, { signal });
        document.addEventListener('visibilitychange', () => {
            handleSurfaceVisibility(readDreamSurfaceActive());
        }, { signal });
        // A library line was fitted in the face it had; a face landing later is wider or narrower.
        document.fonts?.addEventListener?.('loadingdone', fitRunLines, { signal });
        bindResumeViewInteractionGuards();
    }

    function markResumeViewInteraction(key) {
        if (!state.resumePollPromise) return;
        state.viewInteractionVersions[key] += 1;
    }

    function bindResumeViewInteractionGuards() {
        const scrollIntentEvents = ['wheel', 'pointerdown', 'touchstart', 'keydown'];
        for (const eventName of scrollIntentEvents) {
            elements.detail?.addEventListener(
                eventName,
                () => markResumeViewInteraction('detailScroll'),
                { signal: state.eventController.signal }
            );
            elements.runs?.addEventListener(
                eventName,
                () => markResumeViewInteraction('runsScroll'),
                { signal: state.eventController.signal }
            );
        }
        const focusIntentEvents = ['pointerdown', 'touchstart', 'keydown', 'beforeinput', 'input'];
        for (const eventName of focusIntentEvents) {
            document.addEventListener(
                eventName,
                () => markResumeViewInteraction('focusSelection'),
                { signal: state.eventController.signal }
            );
        }
    }

    function hasActiveGeneration() {
        const activeStatuses = new Set([
            'pending', 'queued', 'generating', 'art',
            'summoner_queued', 'summoner_designing', 'summoner_art_generating',
            'spells_queued', 'spells_designing', 'spells_art_generating',
            // A deletion finishes in the background: followed until the
            // summoner is gone, or its row says "Deleting" until the next press.
            'deleting',
        ]);
        return activeStatuses.has(state.currentRunDetails?.status)
            || state.runs.some((run) => activeStatuses.has(run?.status))
            // A pending start awaiting reconciliation is active work: its
            // (paid) outcome resolves through poll ticks, so idle suspension
            // must not park it until the next user action.
            || Boolean(readPendingStart());
    }

    function scheduleNextPoll() {
        if (state.pollTimer || !state.surfaceActive || !state.user) return;
        // Idle suspension (docs/asset-loading.md law 4): with nothing
        // generating there is nothing to poll for. User actions reschedule
        // through reschedulePollingForCurrentState, and a surface re-show
        // always runs a fresh resume tick before startPolling.
        if (!hasActiveGeneration()) return;
        state.pollDelayMs = ACTIVE_POLL_INTERVAL_MS;
        state.pollTimer = setTimeout(async () => {
            state.pollTimer = null;
            state.pollDelayMs = null;
            await runPollTick().catch(() => {});
            scheduleNextPoll();
        }, ACTIVE_POLL_INTERVAL_MS);
    }

    function reschedulePollingForCurrentState() {
        if (state.resumePollPromise) return;
        if (!state.pollTimer) {
            scheduleNextPoll();
            return;
        }
        if (hasActiveGeneration()) return; // already on the active cadence
        stopPolling(); // every run is terminal — suspend until the next action
    }

    function startPolling() {
        if (state.pollTimer || !state.surfaceActive) return;
        scheduleNextPoll();
    }

    function stopPolling() {
        if (state.pollTimer) {
            clearTimeout(state.pollTimer);
            state.pollTimer = null;
            state.pollDelayMs = null;
        }
    }

    /* THE READS OF A TICK GO OUT TOGETHER. The library page, the balance and
       the selected summoner used to be read one after another — three round
       trips before the Foundry reopened on this tab could show anything
       (round 14, Chrome desktop, EU cable: 0.49 s each). The balance never
       depended on the library, and a summoner the view already names (the
       device view the surface restores, the one the player chose) does not
       wait for its row either; its detail is painted after the balance lands,
       as it always was, so the admission it shows is painted once. A summoner
       the library itself picks is read by that pick, never twice. */
    async function pollTick({ prepare = false } = {}, view = { read: false }) {
        if (!state.surfaceActive && !prepare) return;
        // An interrupted paid start is reconciled by the tab itself, never by
        // the unseen first-view read: that one only reads and lays out.
        if (!prepare) await attemptPendingStartReconciliation();
        // From here the tick reads the view it loads: a view restored before
        // this line is carried by this tick (restoreViewState).
        view.read = true;
        const detailRequestsBefore = state.detailRequestGeneration;
        const balance = loadStardust();
        const library = loadRuns();
        /* An opening's summoner is named before page 1 is read: it is read
           beside the library and painted once page 1 says it is still the
           newest (a newer one replaces it there, and this read is dropped). */
        const namedSummoner = state.selectedRunId && !state.loadingDetail
            ? loadRunDetails(state.selectedRunId, {
                preserveExisting: true,
                paintAfter: state.openingLatest ? Promise.all([balance, library]) : balance,
            })
            : null;
        await library;
        await balance;
        await namedSummoner;
        if (
            state.selectedRunId
            && !state.loadingDetail
            && !detailReadSince(detailRequestsBefore, state.selectedRunId)
        ) {
            await loadRunDetails(state.selectedRunId, { preserveExisting: true });
        }
    }

    function runPollTick(options) {
        if (state.pollPromise) return state.pollPromise;
        const view = { read: false };
        const poll = pollTick(options, view);
        state.pollPromise = poll;
        state.pollView = view;
        const settle = () => {
            if (state.pollPromise !== poll) return;
            state.pollPromise = null;
            state.pollView = null;
        };
        void poll.then(settle, settle);
        return poll;
    }

    /* THE FIRST VIEW. What the tab shows the first time the player looks at
       it: the library page, the balance and price, and — on the desktop, which
       opens a summoner beside the library — that summoner's panel with its
       pictures settled. The Foundry waits on it (bounded) before presenting a
       surface that opens here, and has the hidden tab prepare it unseen while
       Moon Fuse shows, so switching tabs is a swap rather than a load. */
    function createFirstView() {
        const firstView = {
            settled: false, tracking: false, startedAt: null, resolve: null, promise: null, capTimer: null,
        };
        firstView.promise = new Promise((resolve) => { firstView.resolve = resolve; });
        return firstView;
    }

    /** @returns {HTMLElement | null} */
    function dreamPanel() {
        // The surface root, or (legacy mount) the panel itself.
        const root = /** @type {any} */ (state.root);
        if (root?.id === 'moon-dream-panel') return root;
        return root?.querySelector?.('#moon-dream-panel') || null;
    }

    /* While the tab is hidden, `data-foundry-preparing` lays its panel out,
       unpainted, in the same box it will have when shown (foundry.css), so its
       pictures load, decode and settle at their final size before the switch. */
    function markPreparing(preparing) {
        const panel = dreamPanel();
        if (!panel) return;
        const panels = panel.parentElement;
        // A fresh wait, or none: what it waits on is said again by the check.
        delete panel.dataset.foundryAwaiting;
        if (preparing) {
            panel.dataset.foundryPreparing = 'true';
            if (panels) panels.dataset.foundryPreparing = 'true';
            return;
        }
        delete panel.dataset.foundryPreparing;
        if (panels) delete panels.dataset.foundryPreparing;
    }

    /* WHAT A PREPARING PANEL STILL WAITS ON. Its reads are in and laid out,
       and all that is left are pictures that draw their own loading well past
       the shared grace — the library's portrait disc, a direct-media shimmer
       — so the tab switch holds it for the grace alone and then lets those
       wells carry the wait in front of the player (foundry-tabs.js). Anything
       held unseen until it settles (a staged deck, a spell settling in its
       cell, a cohort) would pop in instead, so it keeps the whole hand-over. */
    function markAwaitingPictures(onlyPictures) {
        const panel = dreamPanel();
        if (!panel?.dataset.foundryPreparing) return;
        if (onlyPictures) {
            if (panel.dataset.foundryAwaiting !== 'pictures') panel.dataset.foundryAwaiting = 'pictures';
            return;
        }
        if (panel.dataset.foundryAwaiting !== undefined) delete panel.dataset.foundryAwaiting;
    }

    function stopWatchingFirstViewMedia() {
        clearTimeout(state.firstViewSettleTimer);
        state.firstViewSettleTimer = null;
        state.firstViewMediaWatch?.();
        state.firstViewMediaWatch = null;
    }

    function settleFirstView(firstView = state.firstView) {
        if (!firstView || firstView.settled) return;
        firstView.settled = true;
        clearTimeout(firstView.capTimer);
        stopWatchingFirstViewMedia();
        if (firstView === state.firstView) markPreparing(false);
        firstView.resolve();
    }

    const FIRST_VIEW_UNSEEN_HOLDS = [
        '.moon-dream-spells-raster-staging',
        '.moon-dream-spell-card--settling',
        '.moon-dream-media-cohort-hold',
    ].join(', ');
    const FIRST_VIEW_WELLED_FACES = [
        '.moon-dream-run-portrait-image.is-held',
        '.moon-dream-run-portrait-image.is-sampling',
    ].join(', ');

    /**
     * What the first view is still waiting on: `'hold'` while anything is held
     * unseen until it settles (a staged deck, a settling spell, a cohort, an
     * undecoded picture with no well of its own), `'wells'` while only
     * pictures that draw their own loading well are left, else null.
     */
    function firstViewPendingMedia() {
        const panel = dreamPanel() || state.root;
        if (!panel?.querySelector) return null;
        if (panel.querySelector(FIRST_VIEW_UNSEEN_HOLDS)) return 'hold';
        let wells = Boolean(panel.querySelector(FIRST_VIEW_WELLED_FACES));
        for (const img of panel.querySelectorAll('img')) {
            if (!img.getAttribute('src') || img.complete) continue;
            if (
                img.classList.contains('moon-dream-media-loading')
                || img.classList.contains('moon-dream-run-portrait-image')
            ) {
                wells = true;
                continue;
            }
            return 'hold';
        }
        return wells ? 'wells' : null;
    }

    /* A SURFACE OPENING ON THIS TAB SHOWS ITS WELLS PAST THE GRACE: the tab
       switch's rule (foundry-tabs.js endSwitchGrace), for the view the Foundry
       presents when it opens here. Once the reads are in and laid out, a face
       that lands inside the shared grace still makes a whole first frame; past
       it, the surface is presented with the faces that draw their own loading
       well still on their way, and those wells are its one loading state.
       Held for them instead, the whole surface stayed unpainted until the
       library's portrait had downloaded and been baked for its disc (round 14
       review, iPhone 13 landscape, EU phone 4G, a device opening straight on
       Moon Dream with nothing cached: reads in by 0.82 s in WebKit and 1.35 s
       in Chrome, then the portrait's own 0.84-1.54 s download; with the grace
       the surface shows at 0.84 s and 1.26 s). Anything held unseen until it
       settles (a staged deck) still holds it, and a tab preparing unseen
       keeps waiting: there the switch decides. Returns the grace left in ms,
       or null when the rule does not apply. */
    function firstViewWellsGraceLeft(firstView) {
        if (!state.surfaceActive || !Number.isFinite(firstView.startedAt)) return null;
        return Math.max(0, FIRST_VIEW_WELLS_GRACE_MS - (Date.now() - firstView.startedAt));
    }

    /* The first view settles in the task its last picture does: a load, an
       error or a class a face drops (its sampling, its cohort hold) wakes the
       check at once. The 50 ms check stays behind it for anything no event
       announces, so a settled face no longer waited up to a poll for the
       switch it was holding. */
    function waitForFirstViewMedia(firstView) {
        return new Promise((resolve) => {
            const panel = dreamPanel() || state.root;
            const check = () => {
                clearTimeout(state.firstViewSettleTimer);
                state.firstViewSettleTimer = null;
                const pending = firstView.settled || firstView !== state.firstView
                    ? null
                    : firstViewPendingMedia();
                const graceLeft = pending === 'wells' ? firstViewWellsGraceLeft(firstView) : null;
                if (!pending || graceLeft === 0) {
                    if (state.firstViewMediaWatch === unwatch) stopWatchingFirstViewMedia();
                    else unwatch();
                    resolve();
                    return;
                }
                markAwaitingPictures(pending === 'wells');
                state.firstViewSettleTimer = setTimeout(
                    check,
                    graceLeft === null ? FIRST_VIEW_SETTLE_POLL_MS : Math.min(FIRST_VIEW_SETTLE_POLL_MS, graceLeft),
                );
            };
            let observer = null;
            const unwatch = () => {
                observer?.disconnect();
                observer = null;
                panel?.removeEventListener?.('load', check, true);
                panel?.removeEventListener?.('error', check, true);
            };
            state.firstViewMediaWatch?.();
            state.firstViewMediaWatch = unwatch;
            if (typeof MutationObserver === 'function' && panel?.nodeType === 1) {
                observer = new MutationObserver(check);
                observer.observe(panel, {
                    subtree: true,
                    childList: true,
                    attributes: true,
                    attributeFilter: ['class', 'src'],
                });
            }
            panel?.addEventListener?.('load', check, true);
            panel?.addEventListener?.('error', check, true);
            check();
        });
    }

    function trackFirstView(read) {
        const firstView = state.firstView;
        if (!firstView || firstView.settled || firstView.tracking) return;
        firstView.tracking = true;
        // The grace a face with its own well gets (firstViewWellsGraceLeft).
        firstView.startedAt = Date.now();
        firstView.capTimer = setTimeout(() => settleFirstView(firstView), FIRST_VIEW_SETTLE_CAP_MS);
        void Promise.resolve(read)
            .catch(() => undefined)
            // A restored view (the Foundry reopening on this tab) starts a
            // resume read of its own; its answer is part of the first view.
            .then(() => state.resumePollPromise)
            .catch(() => undefined)
            .then(() => waitForDetailRequests())
            .then(() => waitForFirstViewMedia(firstView))
            .finally(() => settleFirstView(firstView));
    }

    function startPreparingFirstView() {
        if (!state.prepareRequested || state.surfaceActive || !state.user) return;
        state.prepareRequested = false;
        markPreparing(true);
        trackFirstView(runPollTick({ prepare: true }));
    }

    /**
     * Read and lay out the first view while another tab is showing: one read,
     * never a poll (polls stay suspended until the tab is shown), and no paid
     * reconciliation. Called by the Foundry surface when it opens on Moon Fuse.
     */
    function prepareFirstView() {
        if (!state.initialized || state.surfaceActive || state.firstView?.settled) return;
        state.prepareRequested = true;
        markPreparing(true);
        startPreparingFirstView();
    }

    function whenFirstViewReady() {
        return state.firstView?.promise || Promise.resolve();
    }

    function isFirstViewReady() {
        return !state.firstView || state.firstView.settled;
    }

    /* THE VIEW A REOPEN SHOWS AT ONCE (REMEMBERED_VIEW_MAX_AGE_MS). What this
       tab shows when the Foundry closes — the library page, the selected
       summoner's panel, the prices — is kept for the next open in this page
       session, for the same account, language and page size. Only a settled
       view is kept: nothing reading, nothing generating, no paid start or
       mutation in flight, and a painted panel for a selected summoner; its
       age is that of its oldest read. The balance is not part of it: every
       open prints the figure the lobby chip holds (handleAuthUser). */
    let rememberedView = null;

    function copyData(value) {
        return value == null ? value : JSON.parse(JSON.stringify(value));
    }

    function rememberView() {
        rememberedView = null;
        const uid = typeof state.user?.uid === 'string' ? state.user.uid : '';
        if (!uid || elements.runs?.dataset.loadState !== 'ready') return;
        if (!Number.isFinite(state.runsReadAt)) return;
        if (
            state.loadingRuns || state.loadingDetail || state.starting || state.selectingArt
            || state.deletingSummoner || state.pendingDeletionRunId
            || !state.pricing || hasActiveGeneration()
        ) return;
        const selectedRunId = state.selectedRunId || null;
        if (selectedRunId && (!canPreserveRenderedRunDetail(selectedRunId) || !Number.isFinite(state.detailReadAt))) {
            return;
        }
        rememberedView = {
            uid,
            language: currentLanguage(),
            runsPageSize: state.runsPageSize,
            readAt: selectedRunId ? Math.min(state.runsReadAt, state.detailReadAt) : state.runsReadAt,
            runsReadAt: state.runsReadAt,
            detailReadAt: selectedRunId ? state.detailReadAt : null,
            runs: copyData(state.runs),
            runsPage: state.runsPage,
            runsNextCursor: state.runsNextCursor,
            runsIsDone: state.runsIsDone,
            runsPageCursors: [...state.runsPageCursors.entries()],
            selectedRunId,
            detail: selectedRunId ? copyData(state.currentRunDetails) : null,
            pricing: { ...state.pricing },
            artVariationCount: state.artVariationCount,
        };
    }

    /**
     * The Foundry reopening on this tab, in the task that mounts it: a view
     * this tab showed in the last minute that is an opening's own view — page
     * 1 on its newest summoner, or a phone's library — is painted at once and
     * is the first view (its pictures settled, the rule of every first view).
     * The opening read is not skipped: it re-reads page 1 and the summoner
     * and reconciles in place, so an unchanged answer changes nothing.
     */
    function showRememberedView() {
        const view = rememberedView;
        rememberedView = null;
        const firstView = state.firstView;
        if (!view || !firstView || firstView.settled || view.uid !== state.user?.uid) return false;
        if (Date.now() - view.readAt > REMEMBERED_VIEW_MAX_AGE_MS) return false;
        if (view.language !== currentLanguage() || view.runsPageSize !== runsPageSize()) return false;
        const opening = usesMobileMasterDetail() ? null : (view.runs?.[0]?.id || null);
        if (view.runsPage !== 1 || view.selectedRunId !== opening) return false;
        state.selectedRunId = opening;
        setMobileDreamView(opening ? 'detail' : 'history');

        state.runs = view.runs;
        state.runsPage = view.runsPage;
        state.runsPageSize = view.runsPageSize;
        state.runsNextCursor = view.runsNextCursor;
        state.runsIsDone = view.runsIsDone;
        state.runsPageCursors = new Map(view.runsPageCursors);
        state.runsReadAt = view.runsReadAt;
        state.pricing = view.pricing;
        state.artVariationCount = view.artVariationCount;
        updatePricingUi();
        updateGenerateAvailability();
        renderRuns();
        setRunsLoadState('ready');
        if (view.detail) {
            state.detailReadAt = view.detailReadAt;
            renderRunDetail(view.detail);
        }
        void waitForFirstViewMedia(firstView).then(() => settleFirstView(firstView));
        return true;
    }

    async function waitForDetailRequests() {
        let request = state.detailRequest;
        while (request) {
            try {
                await request.promise;
            } catch (_) { }
            request = state.detailRequest;
        }
    }

    function captureResumeViewState() {
        const focusedElement = document.activeElement;
        const isTextControl = focusedElement?.matches?.('input, textarea') === true;
        return {
            detailScrollTop: Number(elements.detail?.scrollTop) || 0,
            runsScrollTop: Number(elements.runs?.scrollTop) || 0,
            focusedElement: isTextControl ? focusedElement : null,
            focusedElementId: isTextControl ? focusedElement.id : '',
            selectionStart: isTextControl ? focusedElement.selectionStart : null,
            selectionEnd: isTextControl ? focusedElement.selectionEnd : null,
            selectionDirection: isTextControl ? focusedElement.selectionDirection : null,
            interactionVersions: { ...state.viewInteractionVersions },
        };
    }

    function restoreResumeViewState(viewState) {
        if (!viewState) return;
        const unchanged = (key) => (
            state.viewInteractionVersions[key] === viewState.interactionVersions[key]
        );
        if (unchanged('focusSelection')) {
            const focusedElement = viewState.focusedElement?.isConnected
                ? viewState.focusedElement
                : (viewState.focusedElementId
                    ? (state.root === document
                        ? document.getElementById(viewState.focusedElementId)
                        : state.root?.querySelector?.(`#${viewState.focusedElementId}`))
                    : null);
            if (focusedElement?.matches?.('input, textarea')) {
                try {
                    focusedElement.focus({ preventScroll: true });
                } catch (_) {
                    focusedElement.focus();
                }
                if (Number.isInteger(viewState.selectionStart) && Number.isInteger(viewState.selectionEnd)) {
                    try {
                        focusedElement.setSelectionRange(
                            viewState.selectionStart,
                            viewState.selectionEnd,
                            viewState.selectionDirection || 'none'
                        );
                    } catch (_) { }
                }
            }
        }
        if (elements.detail && unchanged('detailScroll')) elements.detail.scrollTop = viewState.detailScrollTop;
        if (elements.runs && unchanged('runsScroll')) elements.runs.scrollTop = viewState.runsScrollTop;
    }

    /**
     * `carrier`: the poll already on its way when the view was restored, which
     * had not read its view yet ({ poll, view }). Once it has read it, it was
     * this resume's read.
     */
    async function resumePolling(generation, previousResume, carrier = null) {
        if (previousResume) {
            try {
                await previousResume;
            } catch (_) { }
        }
        if (!state.surfaceActive || !state.user || generation !== state.resumePollGeneration) return;

        const viewState = state.suspendedViewState || captureResumeViewState();
        const previousPoll = state.pollPromise;
        if (previousPoll) {
            try {
                await previousPoll;
            } catch (_) {
                console.warn('[MoonDream]', { code: 'prehide_poll_failed' });
            }
        }
        if (!state.surfaceActive || !state.user || generation !== state.resumePollGeneration) return;

        const carried = Boolean(carrier && carrier.poll === previousPoll && carrier.view.read);
        if (!carried) {
            try {
                await runPollTick();
            } catch (_) {
                console.warn('[MoonDream]', { code: 'resume_poll_failed' });
            }
        }
        await waitForDetailRequests();

        if (!state.surfaceActive || !state.user || generation !== state.resumePollGeneration) return;
        restoreResumeViewState(viewState);
        state.suspendedViewState = null;
        startPolling();
    }

    function beginResumePolling(generation, carrier = null) {
        const previousResume = state.resumePollPromise;
        const resume = resumePolling(generation, previousResume, carrier);
        state.resumePollPromise = resume;
        void resume.then(
            () => {
                if (state.resumePollPromise === resume) state.resumePollPromise = null;
            },
            () => {
                if (state.resumePollPromise === resume) state.resumePollPromise = null;
            }
        );
    }

    /* LEAVING THE TAB SPENDS ITS ONE-SHOT ENTRANCES. The Foundry keeps this
       panel built and hides it (`display: none`), and showing a hidden subtree
       again restarts every CSS animation in it. An entrance still armed when
       the player left — the summoner reveal, the spell deal, the phone's
       master/detail slide — would replay from its first frame over content
       already on screen when they came back. Each is spent here instead, the
       way its own timer would have spent it. */
    function spendOneShotEntrances() {
        clearTimeout(state.detailEntranceTimer);
        clearTimeout(state.detailEntranceImpactTimer);
        clearTimeout(state.detailNavTimer);
        state.detailEntranceTimer = null;
        state.detailEntranceImpactTimer = null;
        state.detailNavTimer = null;
        if (elements.detail?.dataset.reveal === 'play') delete elements.detail.dataset.reveal;
        elements.detail?.querySelectorAll?.('.moon-dream-spells-dealing').forEach((deck) => {
            deck.classList.remove('moon-dream-spells-dealing');
        });
        const content = /** @type {HTMLElement | null} */ (
            state.root?.querySelector?.('#moon-dream-panel-content') || null
        );
        if (content?.dataset.mobileNav) delete content.dataset.mobileNav;
    }

    /* A WELL THE PLAYER HAS WATCHED STAYS SHOWN. A loading well earns its
       place only after the shared grace — its entrance is a delayed fade — and
       the same restart that replays an entrance would hide a well the player
       had already been looking at for another 450 ms on their return. A
       picture still loading past the grace when the tab is left is marked
       shown, the mark the poll carry already uses (harvestSettledDetailMedia):
       foundry.css keeps such a well up without its entrance, and the library's
       portrait disc keys its own well off the same mark. */
    function markWatchedLoadingWells() {
        const panel = dreamPanel() || state.root;
        if (!panel?.querySelectorAll) return;
        const now = Date.now();
        panel.querySelectorAll('img').forEach((img) => {
            if (!img.dataset.moonDreamMediaSince || img.dataset.moonDreamShown === 'true') return;
            const waiting = img.classList.contains('moon-dream-media-loading')
                || (img.classList.contains('moon-dream-run-portrait-image')
                    && (img.classList.contains('is-sampling') || img.classList.contains('is-held')));
            if (!waiting) return;
            const since = Number(img.dataset.moonDreamMediaSince) || now;
            if (now - since >= DIRECT_MEDIA_GRACE_MS) img.dataset.moonDreamShown = 'true';
        });
    }

    function handleSurfaceVisibility(active) {
        const nextActive = active === true;
        if (!nextActive && state.surfaceActive) {
            state.suspendedViewState = captureResumeViewState();
            spendOneShotEntrances();
            markWatchedLoadingWells();
        }
        // Shown now: the panel takes its ordinary box in the same task.
        if (nextActive) {
            state.prepareRequested = false;
            markPreparing(false);
            // It appears under a resting pointer.
            if (!state.surfaceActive) spellHoverIntent.disarm();
        }
        state.surfaceActive = nextActive;
        state.resumePollGeneration += 1;
        const generation = state.resumePollGeneration;
        stopPolling();
        if (!state.surfaceActive) {
            cancelSpellPreview();
            return;
        }
        if (!state.user) return;
        beginResumePolling(generation);
        void attemptPendingStartReconciliation();
    }

    function handleAuthUser(user) {
        const previousUid = typeof state.user?.uid === 'string' ? state.user.uid : null;
        const nextUid = typeof user?.uid === 'string' ? user.uid : null;
        if (previousUid !== nextUid) {
            // Another account never opens on this one's library.
            if (rememberedView?.uid !== nextUid) rememberedView = null;
            cancelSpellPreview();
            stopPolling();
            state.selectionMutationGeneration += 1;
            state.selectingArt = false;
            state.selectingArtRunId = null;
            state.deletionMutationGeneration += 1;
            state.deletingSummoner = false;
            state.pendingDeletionRunId = null;
            state.readControllers.forEach((controller) => controller.abort());
            state.readControllers.clear();
            state.detailRequestGeneration += 1;
            state.detailRequest = null;
            state.loadingRuns = false;
            state.loadingDetail = false;
            state.runs = [];
            // A different account's run ids must never be read as this one's
            // completion history.
            runsSeenUnfinished.clear();
            runsSeenGenerating.clear();
            runsDrained.clear();
            state.selectedRunId = null;
            state.currentRunDetails = null;
            state.stardustBalance = null;
            state.balanceMutationVersion = 0;
            state.pendingStart = null;
            state.reconciledPendingRequestId = null;
            state.pendingReconcileAttempts = 0;
            state.pendingReconcileRetryAt = 0;
            state.artSelections.clear();
            state.latestRunId = null;
            state.openingLatest = false;
            state.selectionFeedback.clear();
            state.deletionFeedback = null;
            state.createdBannerRuns.clear();
            resetRunsPagination();
            renderStardustBalance(null);
            if (elements.prompt) elements.prompt.value = '';
            renderRuns();
            setRunsLoadState(nextUid ? 'loading' : 'idle');
            setMobileDreamView('history');
            setDetailEmpty(uiText('foundry.moonDream.selectSummoner', 'Select a summoner to view details.'));
        }
        state.user = user || null;
        if (state.user) {
            // First frame: the balance the lobby chip already holds, so the
            // pill never opens blank; the poll's /api/moon-dream/user read
            // remains the authority (its mutation version is still 0 here).
            const known = lastKnownStardust();
            if (known !== null) {
                state.stardustBalance = known;
                renderStardustBalance(known);
            }
            updateGenerateAvailability();
            if (isGenerationCooldownActive()) {
                setStatus(buildCreationCooldownMessage({
                    retryAfterMs: state.generationCooldownUntil - Date.now(),
                    cooldownUntil: state.generationCooldownUntil,
                }), 'error');
            } else {
                setStatus('');
            }
            if (state.surfaceActive) {
                trackFirstView(runPollTick());
                startPolling();
                void attemptPendingStartReconciliation();
            } else {
                startPreparingFirstView();
            }
            return;
        }

        // Signed out: the panel above is final, and there is nothing to read.
        settleFirstView();
        updateGenerateAvailability();
        stopPolling();
        setStatus(uiText('foundry.moonDream.signInCreate', 'Sign in to create a Moon Dream summoner.'), 'error');
        setDetailEmpty(uiText('foundry.moonDream.signInView', 'Sign in to view Moon Dream summoners.'));
        setMobileDreamView('detail');
    }

    function waitForAuth() {
        const parentUser = Firebase?.getUser?.() || null;
        if (parentUser) {
            handleAuthUser(parentUser);
            return;
        }
        /* Subscribe rather than poll every 100ms forever. This used to read
           `window.MoonfallAuthStateChanged(window.MoonfallAuth, …)`, a pair of
           globals nothing has ever assigned — on this branch or on main — so the
           branch was dead and a signed-out player sat in the retry loop below
           for as long as Moon Dream stayed open. `Firebase.subscribe` is the
           registry that actually exists. */
        const unsubscribe = Firebase?.subscribe?.(handleAuthUser);
        if (unsubscribe) {
            state.authUnsubscribe = unsubscribe;
            return;
        }
        state.authRetryTimer = setTimeout(() => {
            state.authRetryTimer = null;
            if (state.initialized) waitForAuth();
        }, 100);
    }

    function init(root = state.root) {
        if (state.initialized) return;
        state.root = root || document;
        cacheElements();
        if (!elements.detail) return;
        updatePricingUi();
        updateGenerateAvailability();
        state.initialized = true;
        state.firstView = createFirstView();
        state.prepareRequested = false;
        state.eventController = new window.AbortController();
        state.surfaceActive = readDreamSurfaceActive();
        bindEvents();
        observeRunsRail();
        state.lifecycleUnsubscribe = FoundryLifecycle?.subscribe?.(() => {
            handleSurfaceVisibility(readDreamSurfaceActive());
        }) || null;
        state.tabLifecycleUnsubscribe = FoundryLifecycle?.subscribeTab?.(() => {
            handleSurfaceVisibility(readDreamSurfaceActive());
        }) || null;
        waitForAuth();
        if (!elements.detail.childElementCount) {
            setDetailEmpty(uiText('foundry.moonDream.selectSummoner', 'Select a summoner to view details.'));
        }
    }

    function dispose() {
        if (!state.initialized) return;
        rememberView();
        settleFirstView();
        markPreparing(false);
        state.firstView = null;
        state.prepareRequested = false;
        cancelSpellPreview();
        cleanupUnsettledDetailMedia();
        stopPolling();
        state.resumePollGeneration += 1;
        state.eventController?.abort();
        state.eventController = null;
        state.lifecycleUnsubscribe?.();
        state.tabLifecycleUnsubscribe?.();
        state.lifecycleUnsubscribe = null;
        state.tabLifecycleUnsubscribe = null;
        if (typeof state.authUnsubscribe === 'function') state.authUnsubscribe();
        state.authUnsubscribe = null;
        if (state.authRetryTimer) clearTimeout(state.authRetryTimer);
        if (state.generationCooldownTimer) clearTimeout(state.generationCooldownTimer);
        if (state.detailEntranceTimer) clearTimeout(state.detailEntranceTimer);
        if (state.detailEntranceImpactTimer) clearTimeout(state.detailEntranceImpactTimer);
        if (state.detailNavTimer) clearTimeout(state.detailNavTimer);
        state.authRetryTimer = null;
        state.generationCooldownTimer = null;
        state.detailEntranceTimer = null;
        state.detailEntranceImpactTimer = null;
        state.detailNavTimer = null;
        state.detailEntrancePlaying = false;
        disarmDetailEntrance();
        state.readControllers.forEach((controller) => controller.abort());
        state.readControllers.clear();
        state.initialized = false;
        state.root = document;
        state.user = null;
        state.surfaceActive = readDreamSurfaceActive();
        state.pollPromise = null;
        state.pollView = null;
        state.lastDetailRequest = null;
        state.resumePollPromise = null;
        state.suspendedViewState = null;
        state.detailRequestGeneration += 1;
        state.detailRequest = null;
        state.loadingRuns = false;
        state.loadingDetail = false;
        state.starting = false;
        state.selectionMutationGeneration += 1;
        state.selectingArt = false;
        state.selectingArtRunId = null;
        state.deletionMutationGeneration += 1;
        state.deletingSummoner = false;
        state.pendingDeletionRunId = null;
        state.pendingStart = null;
        state.reconciledPendingRequestId = null;
        state.pendingReconcileAttempts = 0;
        state.pendingReconcileRetryAt = 0;
        state.artSelections.clear();
        state.selectionFeedback.clear();
        state.deletionFeedback = null;
        state.createdBannerRuns.clear();
        state.stardustBalance = null;
        state.generationNotice = null;
        state.currentRunDetails = null;
        state.balanceMutationVersion = 0;
        state.runsReadAt = null;
        state.detailReadAt = null;
        state.latestRunId = null;
        state.openingLatest = false;
        state.runsPageSize = DESKTOP_RUNS_PAGE_SIZE;
        state.runsFit = null;
        state.spellsPageSize = DESKTOP_SPELLS_PAGE_SIZE;
        state.completedDeckFit = null;
        deckResizeObserver?.disconnect();
        deckResizeObserver = null;
        runsResizeObserver?.disconnect();
        runsResizeObserver = null;
        Object.keys(elements).forEach((key) => { elements[key] = null; });
    }

    function mount(root) {
        if (state.initialized) dispose();
        init(root);
        return api;
    }

    const api = {
        init,
        mount,
        dispose,
        restoreViewState,
        captureViewState,
        prepareFirstView,
        whenFirstViewReady,
        isFirstViewReady,
    };
    if (typeof window !== 'undefined' && window.TEST_MODE) {
        api.__renderRunDetail = (run) => {
            cacheElements();
            renderRunDetail(run);
        };
        api.__setRuns = (runs) => {
            cacheElements();
            state.runs = Array.isArray(runs) ? runs : [];
            state.selectedRunId = state.runs[0]?.id || null;
            state.runsPage = 1;
            state.runsPageSize = runsPageSize();
            state.runsIsDone = state.runs.length <= state.runsPageSize;
            renderRuns();
            setRunsLoadState('ready');
        };
        api.__pollTick = runPollTick;
        api.__loadRuns = () => loadRunsPage(1, { forceReload: true });
        api.__resetRunsFitForTests = () => {
            state.runsFit = null;
        };
        api.__setSurfaceActiveForTests = (active) => {
            state.surfaceActive = active === true;
        };
        api.__setPendingStartForTests = (pending) => {
            if (pending) writePendingStart(pending);
            else clearPendingStart();
        };
        api.__attachDirectMediaImageForTests = attachDirectMediaImage;
        api.__holdMediaCohortForTests = holdMediaCohortUntilLoaded;
        api.__readCarriedDetailMediaForTests = () => state.carriedDetailMedia;
        api.__startPollingForTests = startPolling;
        api.__stopPollingForTests = stopPolling;
        api.__reschedulePollingForTests = reschedulePollingForCurrentState;
        api.__pollTimerActiveForTests = () => Boolean(state.pollTimer);
        api.__setSelectedRunId = (runId) => {
            state.selectedRunId = runId;
        };
        api.__setMobileViewForTests = setMobileDreamView;
        api.__setDetailEmptyForTests = (message, options) => {
            cacheElements();
            setDetailEmpty(message, options);
        };
        api.__setTestUser = (user) => {
            state.user = user;
        };
        api.__setBalanceForTests = (balance) => {
            state.stardustBalance = balance;
            cacheElements();
            renderStardustBalance(balance);
            updateGenerateAvailability();
        };
        api.__loadStardustForTests = () => {
            cacheElements();
            return loadStardust();
        };
        api.__applyMutationBalanceForTests = (data) => {
            cacheElements();
            applyMutationBalance(data);
        };
        api.__handleAuthUserForTests = handleAuthUser;
        api.__setPricingForTests = (pricing, variationCount) => {
            state.pricing = pricing;
            state.artVariationCount = variationCount;
            if (!Number.isSafeInteger(state.stardustBalance)) state.stardustBalance = 500;
            cacheElements();
            updatePricingUi();
            updateGenerateAvailability();
        };
        api.__resetForTests = () => {
            dispose();
            rememberedView = null;
            state.generationCooldownUntil = 0;
            state.surfaceActive = readDreamSurfaceActive();
            state.resumePollGeneration += 1;
            state.resumePollPromise = null;
            state.suspendedViewState = null;
            state.pollPromise = null;
            state.pollView = null;
            state.lastDetailRequest = null;
            state.detailRequestGeneration += 1;
            state.detailRequest = null;
            state.loadingDetail = false;
            state.selectionMutationGeneration += 1;
            state.selectingArt = false;
            state.selectingArtRunId = null;
            state.deletionMutationGeneration += 1;
            state.deletingSummoner = false;
            state.pendingDeletionRunId = null;
            state.pendingReconcileAttempts = 0;
            state.pendingReconcileRetryAt = 0;
            state.viewInteractionVersions = {
                detailScroll: 0,
                runsScroll: 0,
                focusSelection: 0,
            };
        };
    }
    return api;
})();

function autoMountLegacyMoonDream() {
    const detail = document.getElementById('moon-dream-detail');
    if (!detail || detail.closest('[data-foundry-surface-root]')) return;
    MoonDream?.init?.(document);
}

if (document.getElementById('moon-dream-detail')) autoMountLegacyMoonDream();
document.addEventListener('DOMContentLoaded', autoMountLegacyMoonDream);

export { MoonDream };
